import {
  ChatBox
} from "./chunk-HA6TMEBG.js";
import {
  getErrorMessage,
  ok,
  validateQuestion
} from "./chunk-6AC2CODT.js";
import {
  apiRequest,
  useMutationStatus
} from "./chunk-6YYILA2X.js";
import {
  AIRBRAKE_PROJECT_ID,
  AIRBRAKE_PROJECT_KEY,
  BUSINESS_CITY_KEY,
  ERRORS,
  FOCUSABLE_ELEMENT_SELECTOR,
  LINKED_ADDRESS_QUESTION_NAME,
  LINK_ADDRESS_QUESTION_NAME,
  LOCALE_EN_GB,
  PREPOP_ENDPOINT_URL,
  QUESTIONS_WITH_TOGGLE,
  US_STATE_ANSWER_KEY,
  containsHtmlMarkup,
  formatDateByLocale,
  getPropsByName,
  isObject,
  isOneOf,
  mapToArray,
  questionnaireFormUrlAtom,
  redirectAtom,
  seeMoreCoversStateAtom,
  shallowEqual,
  snakeCase,
  snakeCaseKeys,
  toISODate,
  toPascalCase,
  transformAnswers,
  useEnvironmentValues,
  useLocale,
  useTokens,
  useTokensValues
} from "./chunk-KA7SO5SJ.js";

// src/hooks/useQuestionnaireFormUrl/useQuestionnaireFormUrl.tsx
import { useAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
var useSetQuestionnaireFormUrl = (url) => {
  useHydrateAtoms([[questionnaireFormUrlAtom, url]]);
};
var useQuestionnaireFormUrl = () => {
  const serverUrl = useAtom(questionnaireFormUrlAtom);
  if (typeof window === "undefined") {
    return serverUrl[0] || "/";
  }
  return window?.location?.pathname ?? serverUrl[0];
};

// src/components/Questionnaire/SecurityLocks/index.tsx
import loadable from "@loadable/component";
var SecurityLocks = loadable(() => import("./SecurityLocks-QQZC6HXI.js"), {
  resolveComponent: (mod) => mod.SecurityLocks
});

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/DateQuestion/index.tsx
import loadable2 from "@loadable/component";
var DateQuestion = loadable2(() => import("./DateQuestion-F7JMHK36.js"), {
  resolveComponent: (mod) => mod.DateQuestion
});

// src/components/Questionnaire/Actions/SubmitAction/SubmitAction.tsx
import { rightArrow, loading } from "@simplybusiness/icons";
import { Button, Icon } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { useCallback, useEffect, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var SubmitAction = ({
  children,
  isDisabled,
  buttonLabel = "Continue",
  onSubmit,
  errors
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef(null);
  const handleSubmit = useCallback(() => {
    setIsLoading(true);
    onSubmit?.();
  }, [onSubmit]);
  useEffect(() => {
    if (!isDisabled && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isDisabled]);
  useEffect(() => {
    if (isLoading && errors && Object.keys(errors).length > 0) {
      setIsLoading(false);
    }
  }, [errors, isLoading]);
  const classes = classNames("mobius-submit-action", {
    "mobius-submit-action--padded": !!children
  });
  return /* @__PURE__ */ jsxs("div", { id: "mobius-submit-action", className: classes, children: [
    /* @__PURE__ */ jsx("div", { children }),
    /* @__PURE__ */ jsxs(
      Button,
      {
        ref: buttonRef,
        onClick: handleSubmit,
        isDisabled,
        type: "submit",
        children: [
          buttonLabel,
          " ",
          isLoading ? /* @__PURE__ */ jsx(
            Icon,
            {
              icon: loading,
              spin: true,
              fixedWidth: true,
              title: "Processing your request"
            }
          ) : /* @__PURE__ */ jsx(Icon, { icon: rightArrow, fixedWidth: true, title: "Submit form" })
        ]
      }
    )
  ] });
};

// src/components/Questionnaire/Actions/index.tsx
import { forwardRef as forwardRef5 } from "react";

// src/components/Questionnaire/Actions/Actions.tsx
import { chevronLeft, rightArrow as rightArrow2 } from "@simplybusiness/icons";
import { Flex, Icon as Icon2 } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames2 from "classnames/dedupe";
import { forwardRef } from "react";

// src/components/Questionnaire/Actions/LinkOrButton.tsx
import { Button as Button2, LinkButton } from "@simplybusiness/mobius";
import { jsx as jsx2 } from "react/jsx-runtime";
var LinkOrButton = ({
  href,
  children,
  ...rest
}) => {
  if (!children) return null;
  if (href) {
    return /* @__PURE__ */ jsx2(LinkButton, { ...rest, href, children });
  }
  return /* @__PURE__ */ jsx2(Button2, { ...rest, children });
};

// src/components/Questionnaire/Actions/Actions.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var Actions = forwardRef(
  (props, ref) => {
    const {
      className,
      backButton,
      nextButton,
      backLabel,
      nextLabel,
      category,
      submitAction,
      coverGenius,
      ...rest
    } = props;
    const { onClick: onBackButtonClick } = backButton ?? {};
    const { onClick: onNextButtonClick } = nextButton ?? {};
    const { snowplow } = useSnowplowContext();
    const classes = classNames2("mobius", "mobius-actions", className);
    const handleClick = (label, cb) => {
      snowplow?.trigger("navButtonClicked", {
        label,
        category
      });
      cb();
    };
    const backButtonProps = {
      ...backButton,
      onClick: () => handleClick("Back button", onBackButtonClick),
      className: "mobius-actions__back",
      variant: "link",
      size: "sm"
    };
    const nextButtonProps = {
      ...nextButton,
      onClick: () => handleClick("Next button", onNextButtonClick),
      className: "mobius-actions__next",
      size: "md"
    };
    return /* @__PURE__ */ jsxs2(
      Flex,
      {
        ref,
        className: classes,
        justifyContent: "space-between",
        alignItems: "center",
        ...rest,
        children: [
          backLabel && /* @__PURE__ */ jsxs2(LinkOrButton, { href: backButton?.href, ...backButtonProps, children: [
            /* @__PURE__ */ jsx3(Icon2, { icon: chevronLeft, title: "Return to previous step" }),
            backLabel
          ] }),
          submitAction || nextLabel && /* @__PURE__ */ jsxs2(LinkOrButton, { href: nextButton?.href, ...nextButtonProps, children: [
            nextLabel,
            /* @__PURE__ */ jsx3(Icon2, { icon: rightArrow2, title: "Continue to next section" })
          ] })
        ]
      }
    );
  }
);
Actions.displayName = "Actions";

// src/components/Questionnaire/Actions/MoneySuperMarketActions/Actions.tsx
import { leftArrow, rightArrow as rightArrow3 } from "@simplybusiness/icons";
import { Flex as Flex2, Icon as Icon3 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext2 } from "@simplybusiness/services";
import classNames3 from "classnames/dedupe";
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var Actions2 = forwardRef2(
  (props, ref) => {
    const {
      className,
      backButton,
      nextButton,
      backLabel,
      nextLabel,
      category,
      ...rest
    } = props;
    const { onClick: onBackButtonClick } = backButton ?? {};
    const { onClick: onNextButtonClick } = nextButton ?? {};
    const { snowplow } = useSnowplowContext2();
    const classes = classNames3(
      "mobius",
      "mobius-actions",
      "mobius-actions-msm",
      className
    );
    const handleClick = (label, cb) => {
      snowplow?.trigger("navButtonClicked", {
        label,
        category
      });
      cb();
    };
    const backButtonProps = {
      ...backButton,
      onClick: () => handleClick("Back button", onBackButtonClick),
      className: "mobius-actions__back",
      variant: "link",
      size: "sm"
    };
    const nextButtonProps = {
      ...nextButton,
      onClick: () => handleClick("Next button", onNextButtonClick),
      className: "mobius-actions__next",
      size: "md"
    };
    return /* @__PURE__ */ jsxs3(
      Flex2,
      {
        ref,
        className: classes,
        justifyContent: "space-between",
        alignItems: "center",
        ...rest,
        children: [
          backLabel && /* @__PURE__ */ jsxs3(LinkOrButton, { href: backButton?.href, ...backButtonProps, children: [
            /* @__PURE__ */ jsx4(Icon3, { icon: leftArrow, title: "Go back" }),
            backLabel
          ] }),
          nextLabel && /* @__PURE__ */ jsxs3(LinkOrButton, { href: nextButton?.href, ...nextButtonProps, children: [
            nextLabel,
            /* @__PURE__ */ jsx4(Icon3, { icon: rightArrow3, title: "Continue with quotes" })
          ] })
        ]
      }
    );
  }
);
Actions2.displayName = "Actions";

// src/components/Questionnaire/Actions/CoverGeniusActions/CoverGeniusActions.tsx
import { forwardRef as forwardRef4 } from "react";

// src/components/Questionnaire/Actions/CoverGeniusActions/SeeMoreCovers/SeeMoreCoversButton.tsx
import { Flex as Flex3 } from "@simplybusiness/mobius";
import { forwardRef as forwardRef3 } from "react";
import classNames4 from "classnames/dedupe";

// src/components/Questionnaire/Actions/CoverGeniusActions/SeeMoreCovers/CoversButton.tsx
import { Button as Button3 } from "@simplybusiness/mobius";
import { jsx as jsx5 } from "react/jsx-runtime";
var CoversButton = (props) => {
  const { onClick, className, label } = props;
  return /* @__PURE__ */ jsx5(
    Button3,
    {
      variant: "secondary",
      name: "see_more_covers_clicked",
      size: "md",
      onClick,
      className,
      children: label
    }
  );
};

// src/components/Questionnaire/Actions/CoverGeniusActions/SeeMoreCovers/SeeMoreCoversButton.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var SeeMoreCoversButton = forwardRef3((props, ref) => {
  const { onClick, className, desktop = true, label, showButton } = props;
  const containerClasses = classNames4("mobius", "see_more_covers-container");
  const buttonClasses = classNames4(
    "mobius",
    "see_more_covers-button",
    className
  );
  if (!showButton) {
    return null;
  }
  if (desktop) {
    return /* @__PURE__ */ jsx6(CoversButton, { onClick, className, label });
  }
  return /* @__PURE__ */ jsx6(
    Flex3,
    {
      ref,
      className: containerClasses,
      justifyContent: "center",
      alignItems: "center",
      children: /* @__PURE__ */ jsx6(CoversButton, { onClick, className: buttonClasses, label })
    }
  );
});
SeeMoreCoversButton.displayName = "SeeMoreCoversButton";

// src/components/Questionnaire/Actions/CoverGeniusActions/CoverGeniusActions.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var CoverGeniusActions = forwardRef4((props, ref) => {
  const { coverGenius } = props;
  const { desktop } = coverGenius ?? {};
  if (desktop) {
    return /* @__PURE__ */ jsxs4("div", { className: "cover-genius-actions-container", children: [
      /* @__PURE__ */ jsx7(SeeMoreCoversButton, { ...coverGenius }),
      /* @__PURE__ */ jsx7(Actions, { ...props, ref })
    ] });
  }
  return /* @__PURE__ */ jsxs4(Fragment, { children: [
    /* @__PURE__ */ jsx7(SeeMoreCoversButton, { ...coverGenius }),
    /* @__PURE__ */ jsx7("div", { children: /* @__PURE__ */ jsx7(Actions, { ...props, ref }) })
  ] });
});
CoverGeniusActions.displayName = "CoverGeniusActions";

// src/components/Questionnaire/Actions/index.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var Actions3 = forwardRef5(
  (props, ref) => {
    const { site } = useEnvironmentValues();
    const { variant } = props;
    if (site === "moneysupermarket") {
      return /* @__PURE__ */ jsx8(Actions2, { ...props, ref });
    }
    if (variant === "cover_genius") {
      return /* @__PURE__ */ jsx8(CoverGeniusActions, { ...props, ref });
    }
    return /* @__PURE__ */ jsx8(Actions, { ...props, ref });
  }
);
Actions3.displayName = "Actions";

// src/components/Questionnaire/Questionnaire.tsx
import classNames17 from "classnames/dedupe";
import { forwardRef as forwardRef13, useEffect as useEffect19, useMemo as useMemo13, useRef as useRef14 } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";

// src/hooks/questionnaire/useAnswer.tsx
import { useAtomValue } from "jotai";

// src/hooks/useQuestionnaire/atoms/questionnaireAnswerAtom.tsx
import { atomFamily, selectAtom } from "jotai/utils";

// src/hooks/useQuestionnaire/atoms/questionnaireReducerAtom.tsx
import { atomWithReducer } from "jotai/utils";

// src/hooks/useQuestionnaire/reducer/constants.ts
var ACTIONS = {
  SET: "SET",
  SET_VALIDATIONS: "SET_VALIDATIONS",
  SET_CONFIG: "SET_CONFIG",
  SET_ERRORS: "SET_ERRORS",
  SET_MANUAL_ERROR: "SET_MANUAL_ERROR",
  CLEAR_MANUAL_ERROR: "CLEAR_MANUAL_ERROR",
  MERGE: "MERGE",
  UPDATE_ANSWER: "UPDATE_ANSWER",
  NEXT_SECTION: "NEXT_SECTION",
  PREVIOUS_SECTION: "PREVIOUS_SECTION",
  VALIDATE_FORM: "VALIDATE_FORM",
  REMOVE_ERROR: "REMOVE_ERROR",
  SHOW_QUESTION: "SHOW_QUESTION",
  SET_ADDRESS_MODE: "SET_ADDRESS_MODE",
  SET_EDIT_MODE: "SET_EDIT_MODE",
  EDIT_QUESTION: "EDIT_QUESTION",
  APPEND_QUESTIONS: "APPEND_QUESTIONS",
  CONFIRM_ASSUMPTION_GROUP: "CONFIRM_ASSUMPTION_GROUP",
  SET_CURRENT_SECTION: "SET_CURRENT_SECTION",
  SHOW_ADDRESS_GROUP_MANUAL: "SHOW_ADDRESS_GROUP_MANUAL",
  SET_VISIBLE: "SET_VISIBLE",
  SET_HIDDEN: "SET_HIDDEN"
};

// src/hooks/useQuestionnaire/reducer/actions/setAction/setAction.ts
import equal from "deep-equal";

// src/hooks/useQuestionnaire/getVisibleSection.ts
var hasUnansweredRequiredQuestions = (questions, answers) => questions.some((q) => isRequired(q) && !answers[q.name]);
var isRequired = (q) => q.visible && q.required;
var isVisible = (question) => question.visible ?? false;
var getVisibleSection = (state, direction) => {
  const { sections: sectionsFromState, answers, currentSectionIndex } = state;
  const sections = sectionsFromState;
  if (direction === "next") {
    const sectionVisibility2 = sections.map((section, index) => {
      if (index === currentSectionIndex) {
        return hasUnansweredRequiredQuestions(section.questions, answers);
      }
      return section.questions.some(isVisible);
    });
    const nextIndex = sectionVisibility2.indexOf(true, currentSectionIndex);
    if (nextIndex === -1) {
      return sections.length - 1;
    }
    return nextIndex < currentSectionIndex ? currentSectionIndex : nextIndex;
  }
  const sectionVisibility = sections.map(
    ({ questions }) => questions.some(isVisible)
  );
  const prevIndex = sectionVisibility.lastIndexOf(
    true,
    currentSectionIndex - 1
  );
  if (prevIndex === -1) {
    return currentSectionIndex;
  }
  return prevIndex > currentSectionIndex ? currentSectionIndex : prevIndex;
};
var getNextVisibleSection = (state) => getVisibleSection(state, "next");
var getPreviousVisibleSection = (state) => getVisibleSection(state, "previous");

// src/hooks/useQuestionnaire/getFirstIncompleteSection.ts
var getFirstIncompleteSection = (state) => {
  const { sections: sectionsFromState, answers } = state;
  const sections = sectionsFromState;
  const firstIncompleteIndex = sections.findIndex(
    (section) => hasUnansweredRequiredQuestions(section.questions, answers)
  );
  return firstIncompleteIndex === -1 ? void 0 : firstIncompleteIndex;
};

// src/hooks/useQuestionnaire/getFirstSectionWithErrors.ts
var getFirstSectionWithErrors = (sections, errors) => {
  if (Object.keys(errors).length === 0) {
    return void 0;
  }
  const errorQuestionNames = Object.keys(errors);
  const firstErrorSectionIndex = sections.findIndex(
    (section) => section.questions.some(
      (question) => errorQuestionNames.includes(question.name)
    )
  );
  return firstErrorSectionIndex === -1 ? void 0 : firstErrorSectionIndex;
};

// src/hooks/useQuestionnaire/getServerQuestionErrors.ts
var getServerQuestionErrors = (sections) => {
  const errors = {};
  sections.forEach((section) => {
    section.questions.forEach((question) => {
      if (question.showError && question.visible) {
        errors[question.name] = question.errorMessage || ERRORS.INVALID_GENERIC;
      }
    });
  });
  return errors;
};

// src/components/Questionnaire/Section/Question/types.ts
var getItemType = (item) => {
  if ("type" in item && item.type !== void 0) {
    return item.type;
  }
  return "question";
};
var isQuestion = (item) => getItemType(item) === "question";
var isYesNoToggleGroup = (group) => getItemType(group) === "consolidated_question_group";
var isMultiselectGroup = (group) => getItemType(group) === "boolean_group";
var isMultiselectToggleGroup = (group) => getItemType(group) === "consolidated_boolean_group";
var isAddressLookupGroup = (group) => getItemType(group) === "address_autocomplete_group";
var isPercentGroup = (group) => getItemType(group) === "percent_group";
var isPersonalisedCoverQuestionGroup = (group) => getItemType(group) === "personalised_cover_question_group";
var isQuestionInGroup = (questionName, groups) => {
  return Object.values(groups).some(
    (group) => group.names.includes(questionName)
  );
};

// src/hooks/useQuestionnaire/getVisibilityMap.ts
var getVisibilityMap = ({
  sections,
  addressModes,
  groups,
  visibility
}) => {
  const newVisibilityMap = sections.flatMap(
    ({ questions }) => questions.flatMap((q) => {
      if (!q.name) return [];
      if (isQuestionInGroup(q.name, groups)) {
        return [{ [q.name]: visibility?.[q.name] ?? false }];
      }
      if (q.associatedQuestionName && addressModes[q.associatedQuestionName] === "lookup") {
        return [{ [q.name]: false }];
      }
      return [{ [q.name]: q.visible ?? q.suggested ?? false }];
    })
  ).reduce((acc, q) => ({ ...acc, ...q }), {});
  return visibility && shallowEqual(visibility, newVisibilityMap) ? visibility : newVisibilityMap;
};

// src/hooks/useQuestionnaire/setAddressMode.ts
var setAddressQuestionVisibility = (lookupName, sections, addressModes, answers) => {
  const updatedAnswers = { ...answers };
  const updatedSections = sections.map((section) => {
    const updatedQuestions = section.questions.map((question) => {
      const { name, associatedQuestionName } = question;
      if (name && addressModes[name] && name === lookupName) {
        return addressModes[name] !== "lookup" ? { ...question, visible: false } : { ...question, visible: true };
      }
      if (associatedQuestionName && addressModes[associatedQuestionName] && associatedQuestionName === lookupName) {
        if (addressModes[associatedQuestionName] === "manual") {
          updatedAnswers[name] = "";
          return { ...question, answer: "", visible: true };
        }
        return { ...question, visible: false };
      }
      return question;
    });
    return { ...section, questions: updatedQuestions };
  });
  return {
    sections: updatedSections,
    answers: updatedAnswers
  };
};
var setManualQuestionVisibility = (sections, addressModes) => {
  return sections.map((section) => {
    const updatedQuestions = section.questions.map((question) => {
      const { associatedQuestionName } = question;
      if (associatedQuestionName && addressModes[associatedQuestionName]) {
        return addressModes[associatedQuestionName] === "manual" ? { ...question, answer: "", visible: true } : { ...question, visible: false };
      }
      return question;
    });
    return { ...section, questions: updatedQuestions };
  });
};
var setAddressModes = (sections) => {
  const addressModes = sections.reduce((acc, section) => {
    section.questions.forEach(({ name, display, visible }) => {
      if (display.type === "address_lookup") {
        acc[name] = visible ? "lookup" : "none";
      }
    });
    return acc;
  }, {});
  const updatedSections = setManualQuestionVisibility(sections, addressModes);
  return {
    addressModes,
    sections: updatedSections
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/utils/helpers.ts
var getPrimaryAnswer = (primaryQuestion, answers) => {
  const answer = primaryQuestion.answer || answers[primaryQuestion.name];
  return typeof answer === "string" ? answer : null;
};
var createEmptyFlattenResult = (answers, visibility) => ({
  questions: [],
  answers,
  visibility,
  groups: {}
});

// src/hooks/useQuestionnaire/reducer/actions/groups/utils/shadowQuestion.ts
var createShadowQuestion = ({
  title,
  subText,
  visibleQuestions = [],
  answers,
  required,
  lastItemDisables = false,
  helpInfo
}) => {
  const question = {
    id: snakeCase(`shadow::${title}`),
    name: `Shadow::${title}`,
    primaryText: title,
    secondaryText: subText,
    visible: visibleQuestions.some((q) => q.visible),
    includeAnswer: false,
    required,
    options: visibleQuestions.map((q) => ({
      label: q.primaryText,
      value: q.name,
      shouldUpdate: q.shouldUpdate
    })),
    display: { type: "multiselect", config: { lastItemDisables } }
  };
  if (helpInfo) {
    question.helpInfo = helpInfo;
  }
  return {
    question,
    answer: visibleQuestions.filter((q) => answers?.[q.name] === "yes").map((q) => q.name)
  };
};
var createAddressLookupQuestion = ({
  title,
  countryCode,
  subText,
  manualEntryDisabled,
  apiKey
}) => {
  return {
    id: snakeCase(`shadow::${title}`),
    name: `Shadow::${title}`,
    primaryText: title,
    secondaryText: subText,
    visible: true,
    includeAnswer: false,
    // The display type acts as a pseudo primary question controlling address behaviour
    display: {
      type: "address_lookup_group",
      config: {
        countryCode,
        manualEntryDisabled,
        apiKey
      }
    }
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/utils/SecondaryText.tsx
import { List, ListItem } from "@simplybusiness/mobius";
import { jsx as jsx9 } from "react/jsx-runtime";
var SecondaryText = ({
  primaryQuestion,
  questions,
  lastItemDisables = false
}) => {
  const visibleQuestions = (lastItemDisables ? questions.slice(0, -1) : questions).filter((question) => question.visible && !question.excludeFromPrimaryText);
  if (visibleQuestions.length) {
    return /* @__PURE__ */ jsx9(List, { className: "mobius-question__secondary-text", children: visibleQuestions.map((question, index) => /* @__PURE__ */ jsx9(ListItem, { children: question.primaryText }, question.id ?? index)) });
  }
  if (typeof primaryQuestion.secondaryText === "string") {
    return primaryQuestion.secondaryText;
  }
  return "";
};

// src/hooks/useQuestionnaire/reducer/actions/groups/yesNoToggle/yesNoToggleGroup.ts
var flattenYesNoToggleGroup = (group, answers, visibility) => {
  const { primaryQuestion, groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];
  if (!primaryQuestion || !groupQuestions || filteredQuestions.every((q) => q.visible === false)) {
    return createEmptyFlattenResult(answers, visibility);
  }
  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };
  const questionsWithVisibility = filteredQuestions.map((q) => {
    let answer = null;
    if (primaryAnswer === "no") {
      answer = "no";
    }
    if (primaryAnswer === "yes") {
      answer = q.answer || null;
    }
    if (answer !== null) {
      answersByQuestionName[q.name] = answer;
    }
    visibilityByQuestionName[q.name] = primaryAnswer === "yes" ? !!q.visible : false;
    return { ...q, includeAnswer: !!q.visible };
  });
  const primaryQuestionWithSecondaryText = {
    ...primaryQuestion,
    secondaryText: SecondaryText({
      primaryQuestion,
      questions: filteredQuestions
    })
  };
  return {
    questions: [primaryQuestionWithSecondaryText, ...questionsWithVisibility],
    groups: {
      [primaryQuestion.name]: {
        kind: "group",
        names: questionsWithVisibility.map((q) => q.name)
      }
    },
    visibility: visibilityByQuestionName,
    answers: {
      ...answersByQuestionName,
      [primaryQuestion.name]: primaryAnswer || ""
    }
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/multiselect/multiselectGroup.ts
var flattenMultiselectGroup = (group, answers, visibility = {}) => {
  const { title, subText = "", groupQuestions = [], lastItemDisables } = group;
  const booleanQuestions = groupQuestions.filter(isQuestion);
  const visibleBooleanQuestions = booleanQuestions.filter((q) => q.visible);
  const helpText = visibleBooleanQuestions.filter((q) => q.helpInfo).map((q) => `<h3>${q.primaryText}</h3><p>${q.helpInfo?.text}</p>`).join("");
  const helpInfo = helpText ? {
    text: helpText,
    label: "Need help?",
    headline: "More information"
  } : void 0;
  if (!booleanQuestions.length) {
    return createEmptyFlattenResult(answers, visibility);
  }
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };
  const booleanQuestionsWithAnswers = booleanQuestions.map((q) => {
    answersByQuestionName[q.name] = q.answer ?? answers[q.name] ?? "no";
    visibilityByQuestionName[q.name] = false;
    return { ...q, includeAnswer: true };
  });
  const shadowQuestion = createShadowQuestion({
    title,
    subText,
    visibleQuestions: visibleBooleanQuestions,
    answers: answersByQuestionName,
    required: lastItemDisables ?? false,
    lastItemDisables,
    helpInfo
  });
  return {
    questions: [...booleanQuestionsWithAnswers, shadowQuestion.question],
    groups: {
      [shadowQuestion.question.name]: {
        kind: "shadow",
        names: visibleBooleanQuestions.map((q) => q.name)
      }
    },
    visibility: {
      ...visibilityByQuestionName,
      [shadowQuestion.question.name]: true
    },
    answers: {
      ...answersByQuestionName,
      [shadowQuestion.question.name]: shadowQuestion.answer
    }
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/multiselectToggle/multiselectToggleGroup.ts
var flattenMultiselectToggleGroup = (group, answers, visibility) => {
  const { primaryQuestion, booleanGroups } = group;
  const allBooleanQuestions = booleanGroups.flatMap((bg) => bg.groupQuestions).filter(isQuestion);
  if (!primaryQuestion || !booleanGroups || allBooleanQuestions.every((q) => q.visible === false)) {
    return createEmptyFlattenResult(answers, visibility);
  }
  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);
  const visibleBooleanQuestions = allBooleanQuestions.filter((q) => q.visible);
  const lastItemDisables = booleanGroups.some((bg) => bg.lastItemDisables);
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };
  const booleanQuestionsWithAnswers = allBooleanQuestions.map((q) => {
    if (primaryAnswer === "no") {
      answersByQuestionName[q.name] = "no";
    } else {
      answersByQuestionName[q.name] = q.answer ?? answers[q.name] ?? "no";
    }
    visibilityByQuestionName[q.name] = false;
    return { ...q, includeAnswer: true };
  });
  const shadowQuestion = createShadowQuestion({
    title: booleanGroups[0].title,
    subText: "",
    visibleQuestions: visibleBooleanQuestions,
    answers: answersByQuestionName,
    required: true,
    lastItemDisables
  });
  const primaryQuestionWithSecondaryText = {
    ...primaryQuestion,
    secondaryText: SecondaryText({
      primaryQuestion,
      questions: allBooleanQuestions,
      lastItemDisables
    })
  };
  return {
    questions: [
      primaryQuestionWithSecondaryText,
      ...booleanQuestionsWithAnswers,
      shadowQuestion.question
    ],
    groups: {
      [shadowQuestion.question.name]: {
        kind: "shadow",
        names: visibleBooleanQuestions.map((q) => q.name)
      },
      [primaryQuestionWithSecondaryText.name]: {
        kind: "group",
        names: [shadowQuestion.question.name]
      }
    },
    visibility: {
      ...visibilityByQuestionName,
      [shadowQuestion.question.name]: primaryAnswer === "yes"
    },
    answers: {
      ...answersByQuestionName,
      [primaryQuestionWithSecondaryText.name]: primaryAnswer || "",
      [shadowQuestion.question.name]: shadowQuestion.answer
    }
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/addressLookup/addressLookupGroup.ts
var flattenAddressLookupGroup = (group, answers, visibility) => {
  const { groupQuestions } = group;
  if (!groupQuestions?.length) {
    return createEmptyFlattenResult(answers, visibility);
  }
  const addressLookupControllerQuestion = createAddressLookupQuestion(group);
  const visibilityByQuestionName = {
    ...visibility,
    [addressLookupControllerQuestion.name]: true
  };
  const answersByQuestionName = { ...answers };
  const addressFieldQuestions = groupQuestions.map((q) => {
    const hiddenOnAutocomplete = !!q.hiddenOnAutocomplete;
    const hasAnswer = !!(q.answer || answersByQuestionName[q.name]);
    const shouldBeVisible = !!q.visible && !hiddenOnAutocomplete && hasAnswer;
    visibilityByQuestionName[q.name] = shouldBeVisible;
    if (q.answer) {
      answersByQuestionName[q.name] = q.answer;
    }
    return { ...q, includeAnswer: true };
  });
  return {
    questions: [addressLookupControllerQuestion, ...addressFieldQuestions],
    groups: {
      [addressLookupControllerQuestion.name]: {
        kind: "address",
        names: addressFieldQuestions.map((q) => q.name)
      }
    },
    visibility: visibilityByQuestionName,
    answers: answersByQuestionName
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/percentGroup/percentGroup.ts
var flattenPercentGroup = (group, answers, visibility = {}) => {
  const { title, groupQuestions = [] } = group;
  if (!groupQuestions.length) {
    return createEmptyFlattenResult(answers, visibility);
  }
  const regularQuestions = groupQuestions.filter((q) => !q.readOnly);
  const totalQuestion = groupQuestions.find((q) => q.readOnly === true);
  if (!regularQuestions.length) {
    return createEmptyFlattenResult(answers, visibility);
  }
  const regularQuestionLabels = regularQuestions.map((q) => q.primaryText);
  const totalQuestionLabel = totalQuestion?.primaryText || "Total";
  const labels = [...regularQuestionLabels, totalQuestionLabel];
  const validation = {
    type: "percentages",
    value: { min: 0, max: 100 },
    message: totalQuestion?.validation?.message
  };
  const flattenedVisibility = groupQuestions.some((q) => q.visible === true);
  const flattenedQuestion = {
    name: `Shadow::${title}`,
    id: snakeCase(`shadow::${title}`),
    primaryText: title,
    display: {
      type: "percentage_split_group",
      config: {
        labels,
        step: 5
      }
    },
    validation,
    errorMessage: totalQuestion?.errorMessage,
    required: false,
    visible: flattenedVisibility,
    hasSensitiveData: regularQuestions[0]?.hasSensitiveData ?? false,
    presentationGroup: totalQuestion?.presentationGroup,
    presentationText: totalQuestion?.presentationText,
    shouldUpdate: groupQuestions.some((q) => q.shouldUpdate)
  };
  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };
  const hiddenGroupQuestions = groupQuestions.map((q) => {
    if (q.answer) {
      answersByQuestionName[q.name] = q.answer;
    }
    visibilityByQuestionName[q.name] = false;
    return { ...q, includeAnswer: !q.readOnly, visible: false };
  });
  return {
    questions: [flattenedQuestion, ...hiddenGroupQuestions],
    groups: {
      [flattenedQuestion.name]: {
        kind: "percent",
        names: [
          ...regularQuestions.map((q) => q.name),
          ...totalQuestion ? [totalQuestion.name] : []
        ]
      }
    },
    visibility: {
      ...visibilityByQuestionName,
      [flattenedQuestion.name]: flattenedVisibility
    },
    answers: answersByQuestionName
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/personalisedCoverGroup/personalisedCoverGroup.ts
var flattenPersonalisedCoverGroup = (group, answers, visibility) => {
  const { primaryQuestion, groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];
  if (!primaryQuestion || filteredQuestions.every((q) => q.visible === false)) {
    return createEmptyFlattenResult(answers, visibility);
  }
  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);
  const visibilityByQuestionName = { ...visibility };
  const questionsWithVisibility = filteredQuestions.map((q) => {
    visibilityByQuestionName[q.name] = primaryAnswer === "yes" ? !!q.visible : false;
    return { ...q, includeAnswer: !!q.visible };
  });
  return {
    questions: [primaryQuestion, ...questionsWithVisibility],
    groups: {
      [primaryQuestion.name]: {
        kind: "personalised_cover",
        names: questionsWithVisibility.map((q) => q.name)
      }
    },
    visibility: visibilityByQuestionName,
    answers: {
      ...answers,
      [primaryQuestion.name]: primaryAnswer || ""
    }
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/flattenSections.ts
var flattenSections = ({
  sections,
  answers: initialAnswers,
  visibility: initialVisibility = {}
}) => {
  const answers = { ...initialAnswers };
  const visibility = { ...initialVisibility };
  const groups = {};
  const flattenedSections = sections.map((section) => ({
    ...section,
    questions: section.questions.flatMap((question) => {
      if (isQuestion(question)) {
        return [question];
      }
      let result;
      if (isYesNoToggleGroup(question)) {
        result = flattenYesNoToggleGroup(question, answers, visibility);
      } else if (isMultiselectGroup(question)) {
        result = flattenMultiselectGroup(question, answers, visibility);
      } else if (isMultiselectToggleGroup(question)) {
        result = flattenMultiselectToggleGroup(question, answers, visibility);
      } else if (isAddressLookupGroup(question)) {
        result = flattenAddressLookupGroup(question, answers, visibility);
      } else if (isPercentGroup(question)) {
        result = flattenPercentGroup(question, answers, visibility);
      } else if (isPersonalisedCoverQuestionGroup(question)) {
        result = flattenPersonalisedCoverGroup(question, answers, visibility);
      } else {
        result = createEmptyFlattenResult(answers, visibility);
      }
      Object.assign(groups, result.groups);
      Object.assign(answers, result.answers);
      Object.assign(visibility, result.visibility);
      return result.questions;
    })
  }));
  return { sections: flattenedSections, answers, groups, visibility };
};

// src/hooks/useQuestionnaire/expandPercentageObjectAnswer.ts
var expandPercentageObjectAnswer = (questionName, objectAnswer) => {
  if (objectAnswer && typeof objectAnswer === "object" && !Array.isArray(objectAnswer)) {
    const expanded = {};
    Object.entries(objectAnswer).forEach(
      ([label, value]) => {
        expanded[`${questionName}[${label.toLowerCase()}]`] = String(value);
      }
    );
    const values = Object.values(objectAnswer);
    expanded[questionName] = values.join(",");
    return expanded;
  }
  return {};
};

// src/hooks/useQuestionnaire/formatAnswer.ts
var formatAnswer = (question, answer, locale = LOCALE_EN_GB) => {
  if (question.display?.type === "date") {
    return toISODate(answer, locale);
  }
  if (question.display?.type === "percentage_split" && question.name) {
    const expandedAnswers = expandPercentageObjectAnswer(question.name, answer);
    return Object.keys(expandedAnswers).length > 0 ? expandedAnswers : answer;
  }
  if (question.display?.type === "currency" && typeof answer === "string") {
    return answer.replace(/,/g, "");
  }
  return answer;
};

// src/hooks/useQuestionnaire/reducer/actions/setAction/initialiseAnswers.ts
var initialiseAnswers = (answers, payload, locale = LOCALE_EN_GB) => {
  const newAnswers = payload.reduce(
    (acc, section) => section.questions.reduce((acc2, question) => {
      if (question.answer !== void 0) {
        const answer = formatAnswer(question, question.answer, locale);
        if (isObject(answer)) {
          return { ...acc2, ...answer };
        }
        return { ...acc2, [question.name]: answer };
      }
      return acc2;
    }, acc),
    {}
  );
  return { ...answers, ...newAnswers };
};

// src/hooks/useQuestionnaire/reducer/actions/setAction/setAction.ts
var handleSetAction = (state, payload) => {
  const { answers, config } = state;
  const locale = config?.locale;
  const {
    sections: flattenedSections,
    answers: updatedAnswers,
    groups,
    visibility: groupChildrenVisibility
  } = flattenSections({ sections: payload, answers });
  const { addressModes, sections } = setAddressModes(flattenedSections);
  const visibility = getVisibilityMap({
    sections,
    addressModes,
    groups,
    visibility: groupChildrenVisibility
  });
  const sectionsToUse = equal(state.sections, sections) ? state.sections : sections;
  const newState = {
    ...state,
    sections: sectionsToUse,
    answers: initialiseAnswers(updatedAnswers, sections, locale),
    visibility,
    addressModes,
    groups
  };
  const serverErrors = getServerQuestionErrors(sections);
  const firstIncompleteSection = getFirstIncompleteSection(newState);
  const firstSectionWithErrors = getFirstSectionWithErrors(
    sections,
    serverErrors
  );
  const currentSectionIndex = Math.min(
    firstSectionWithErrors ?? sections.length - 1,
    firstIncompleteSection ?? sections.length - 1
  );
  return {
    ...newState,
    currentSectionIndex,
    errors: serverErrors
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setConfigAction.ts
var handleSetConfigAction = (state, payload) => {
  return {
    ...state,
    config: payload
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setErrorsAction.ts
var handleSetErrorsAction = (state, payload) => {
  return {
    ...state,
    errors: payload
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setManualErrorAction.ts
var handleSetManualErrorAction = (state, payload) => {
  return {
    ...state,
    manualErrors: {
      ...state.manualErrors,
      [payload.name]: payload.message
    }
  };
};

// src/hooks/useQuestionnaire/reducer/actions/clearManualErrorAction.ts
var handleClearManualErrorAction = (state, payload) => {
  const { [payload]: _, ...remainingManualErrors } = state.manualErrors || {};
  return {
    ...state,
    manualErrors: remainingManualErrors
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setEditModeAction.ts
var handleSetEditModeAction = (state, payload) => {
  return {
    ...state,
    isEditing: payload
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setValidationsAction.ts
var handleSetValidationsAction = (state, payload) => {
  return {
    ...state,
    validations: payload
  };
};

// src/hooks/useQuestionnaire/reducer/actions/mergeAction/mergeAction.ts
import equal2 from "deep-equal";

// src/hooks/useQuestionnaire/types.ts
var isShadowGroup = (g) => Boolean(g && g.kind === "shadow");
function isShadowQuestion(questionOrName) {
  if (typeof questionOrName === "string") {
    return questionOrName.startsWith("Shadow::");
  }
  return questionOrName.name?.startsWith("Shadow::") ?? false;
}
var isAddressGroup = (g) => Boolean(g && g.kind === "address");
var isPercentKindGroup = (g) => Boolean(g && g.kind === "percent");

// src/hooks/useQuestionnaire/reducer/actions/mergeAction/mergeState.ts
function mergeQuestion({
  question,
  payloadQuestion,
  answers,
  addressModes,
  locale
}) {
  const answer = payloadQuestion?.answer;
  let visible = payloadQuestion?.visible;
  if (addressModes[question.name]) {
    const addressMode = addressModes[question.name];
    visible = addressMode === "lookup" ? true : false;
  }
  if (question.associatedQuestionName) {
    const addressMode = addressModes[question.associatedQuestionName];
    visible = addressMode === "manual" ? true : false;
  }
  if (answer) {
    if (question.display?.type === "date") {
      answers[question.name] = formatAnswer(question, answer, locale);
      return {
        ...question,
        ...payloadQuestion,
        answer: answers[question.name]
      };
    }
    if (question.display?.type === "percentage_split" && question.name) {
      const formattedAnswer = formatAnswer(question, answer, locale);
      Object.assign(answers, formattedAnswer);
    } else {
      answers[question.name] = answer;
    }
  } else if (!isShadowQuestion(question) && !question.includeAnswer) {
    delete answers[question.name];
  }
  return {
    ...question,
    ...payloadQuestion,
    visible: visible ?? question.visible
  };
}
var mergeState = (state, payload, locale = LOCALE_EN_GB) => {
  const {
    sections: stateSections,
    answers: stateAnswers,
    addressModes: stateAddressModes = {},
    ...rest
  } = state;
  const answers = { ...stateAnswers };
  const addressModes = { ...stateAddressModes };
  const sections = stateSections.map(
    (section, sectionIndex) => {
      const payloadQuestionMap = new Map(
        payload[sectionIndex]?.questions?.map((q) => [q.name, q]) || []
      );
      return {
        ...section,
        ...payload[sectionIndex],
        questions: section.questions.map((question) => {
          if (section.displayAs === "edit") return question;
          const payloadQuestion = payloadQuestionMap.get(question.name);
          return mergeQuestion({
            question,
            payloadQuestion,
            answers,
            addressModes,
            locale
          });
        })
      };
    }
  );
  return { sections, answers, addressModes, ...rest };
};

// src/hooks/useQuestionnaire/reducer/actions/mergeAction/mergeAction.ts
var handleMergeAction = (state, payload) => {
  const locale = state.config?.locale;
  const {
    sections,
    groups,
    visibility: flattenedVisibility
  } = flattenSections({
    sections: payload,
    answers: state.answers,
    visibility: state.visibility
  });
  const mergedGroups = { ...state.groups, ...groups };
  const newState = mergeState(state, sections, locale);
  const visibility = getVisibilityMap({
    sections: newState.sections,
    addressModes: newState.addressModes ?? {},
    groups: mergedGroups,
    // Include merged groups so we can apply group visibility rules
    visibility: { ...state.visibility, ...flattenedVisibility }
  });
  const sectionsToUse = equal2(state.sections, newState.sections) ? state.sections : newState.sections;
  return {
    ...newState,
    sections: sectionsToUse,
    groups: mergedGroups,
    visibility
  };
};

// src/hooks/useQuestionnaire/reducer/actions/previousSectionAction.ts
var handlePreviousSectionAction = (state) => {
  const { sections } = state;
  const updatedSections = sections.filter(
    (section) => section.displayAs !== "edit"
  );
  return {
    ...state,
    sections: updatedSections,
    currentSectionIndex: getPreviousVisibleSection(state),
    isEditing: false
  };
};

// src/hooks/useQuestionnaire/reducer/actions/nextSectionAction.ts
var handleNextSectionAction = (state) => {
  const { sections, lastViewedSectionIndex } = state;
  const nextSectionIndex = getNextVisibleSection(state);
  const updatedSections = sections.map(
    (section, index) => index < nextSectionIndex ? { ...section, isVisited: true } : section
  );
  const updatedLastViewedSectionIndex = Math.max(
    nextSectionIndex,
    lastViewedSectionIndex
  );
  return {
    ...state,
    currentSectionIndex: nextSectionIndex,
    lastViewedSectionIndex: updatedLastViewedSectionIndex,
    sections: updatedSections,
    isValid: false
  };
};

// src/components/Questionnaire/validation/form/compare.ts
var compare = (op) => (a, b) => {
  if (a == null || b == null) {
    switch (op) {
      case "eq":
        return a == b;
      case "neq":
        return a != b;
      default:
        return false;
    }
  }
  const normalizeValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(",");
    }
    return value;
  };
  const normalizedA = normalizeValue(a);
  const normalizedB = normalizeValue(b);
  if (op === "lt" || op === "lte" || op === "gt" || op === "gte") {
    const numA = typeof normalizedA === "number" ? normalizedA : Number(normalizedA);
    const numB = typeof normalizedB === "number" ? normalizedB : Number(normalizedB);
    if (!isNaN(numA) && !isNaN(numB) && isFinite(numA) && isFinite(numB)) {
      switch (op) {
        case "lt":
          return numA < numB;
        case "lte":
          return numA <= numB;
        case "gt":
          return numA > numB;
        case "gte":
          return numA >= numB;
      }
    }
    const strA2 = String(normalizedA);
    const strB2 = String(normalizedB);
    switch (op) {
      case "lt":
        return strA2 < strB2;
      case "lte":
        return strA2 <= strB2;
      case "gt":
        return strA2 > strB2;
      case "gte":
        return strA2 >= strB2;
    }
  }
  if (op === "eq" || op === "neq") {
    let isEqual;
    if (typeof normalizedA === typeof normalizedB) {
      isEqual = normalizedA === normalizedB;
    } else {
      const numA = Number(normalizedA);
      const numB = Number(normalizedB);
      if (!isNaN(numA) && !isNaN(numB) && isFinite(numA) && isFinite(numB)) {
        isEqual = numA === numB;
      } else {
        isEqual = String(normalizedA) === String(normalizedB);
      }
    }
    return op === "eq" ? isEqual : !isEqual;
  }
  const strA = String(normalizedA);
  const strB = String(normalizedB);
  switch (op) {
    case "in":
      return strA.includes(strB);
    case "nin":
      return !strA.includes(strB);
    default:
      throw new Error("Unsupported comparison operator");
  }
};
var compareFunctions = /* @__PURE__ */ new Map();
var getCompareFunction = (operator) => {
  if (!compareFunctions.has(operator)) {
    compareFunctions.set(operator, compare(operator));
  }
  return compareFunctions.get(operator);
};

// src/components/Questionnaire/validation/form/expressionEvaluator.ts
var evaluateExpression = (expression, context) => {
  if (!expression) {
    return null;
  }
  try {
    const evaluationContext = createEvaluationContext(context);
    const processedExpression = replaceQuestionReferences(expression, context);
    return evaluateProcessedExpression(processedExpression, evaluationContext);
  } catch (error) {
    console.warn("Expression evaluation failed:", expression, error);
    return null;
  }
};
var replaceQuestionReferences = (expression, context) => {
  return expression.replace(
    /\$\{([^}]+)\}/g,
    (_match, questionName) => {
      const answer = context.answers[questionName.trim()];
      if (answer === void 0 || answer === null) {
        return "null";
      }
      if (answer === "") {
        return '""';
      }
      if (typeof answer === "string") {
        const numValue = Number(answer);
        if (!isNaN(numValue) && isFinite(numValue)) {
          return String(numValue);
        }
        return `"${answer.replace(/"/g, '\\"')}"`;
      }
      if (Array.isArray(answer)) {
        return `[${answer.map((item) => {
          const numValue = Number(item);
          if (!isNaN(numValue) && isFinite(numValue)) {
            return String(numValue);
          }
          return `"${String(item).replace(/"/g, '\\"')}"`;
        }).join(", ")}]`;
      }
      return String(answer);
    }
  );
};
var createEvaluationContext = (_context) => {
  return {
    // Mathematical functions
    sum: (...values) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.reduce((acc, val) => acc + val, 0);
    },
    max: (...values) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.length > 0 ? Math.max(...numbers) : 0;
    },
    min: (...values) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.length > 0 ? Math.min(...numbers) : 0;
    },
    avg: (...values) => {
      const numbers = flattenAndConvertToNumbers(values);
      return numbers.length > 0 ? numbers.reduce((acc, val) => acc + val, 0) / numbers.length : 0;
    },
    count: (value) => {
      if (Array.isArray(value)) {
        return value.length;
      }
      return value !== null && value !== void 0 ? 1 : 0;
    },
    // String functions
    concat: (...values) => {
      return values.map((v) => {
        if (v === null || v === void 0) return "";
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
          return String(v);
        }
        return "[object Object]";
      }).join("");
    },
    length: (value) => {
      if (typeof value === "string" || Array.isArray(value)) {
        return value.length;
      }
      return 0;
    },
    // Conditional functions
    coalesce: (...values) => {
      for (const value of values) {
        if (value !== null && value !== void 0 && value !== "") {
          return value;
        }
      }
      return null;
    },
    // Utility functions
    Math,
    String,
    Number,
    Boolean
  };
};
var flattenAndConvertToNumbers = (values) => {
  const flattened = [];
  for (const value of values) {
    if (Array.isArray(value)) {
      flattened.push(...value);
    } else {
      flattened.push(value);
    }
  }
  return flattened.map((val) => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  });
};
var evaluateProcessedExpression = (expression, evaluationContext) => {
  const contextKeys = Object.keys(evaluationContext);
  const contextValues = Object.values(evaluationContext);
  const evaluatorFunction = new Function(
    ...contextKeys,
    `"use strict"; return (${expression});`
  );
  const result = evaluatorFunction(...contextValues);
  if (typeof result === "string" || typeof result === "number" || typeof result === "boolean") {
    return result;
  }
  return result === null || result === void 0 ? null : String(result);
};

// src/components/Questionnaire/validation/form/types.ts
var isRule = (rule) => {
  return "operator" in rule && !("rules" in rule);
};
var isRuleSet = (rule) => {
  return "message" in rule;
};

// src/components/Questionnaire/validation/form/validate.ts
var compileRule = (rule) => {
  const evaluateRule = getCompareFunction(rule.operator);
  return (answers, visibilityMap = {}) => {
    if (!visibilityMap[rule.id]) {
      return {
        id: rule.id,
        valid: true,
        // Consider invisible questions as valid
        message: void 0
      };
    }
    const compareValue = rule.expression ? evaluateExpression(rule.expression, { answers, visibilityMap }) : rule.value;
    const leftHandValue = rule.leftValue !== void 0 ? rule.leftValue : rule.leftExpression ? evaluateExpression(rule.leftExpression, { answers, visibilityMap }) : answers[rule.id];
    const valid = leftHandValue == null || compareValue == null ? false : evaluateRule(leftHandValue, compareValue);
    return {
      id: rule.id,
      valid,
      message: valid && isRuleSet(rule) ? void 0 : rule.message
    };
  };
};
var compileCombinator = (combinator) => {
  const compiledRules = combinator.rules.map(
    (ruleOrCombinator) => {
      if (isRule(ruleOrCombinator)) {
        return compileRule(ruleOrCombinator);
      }
      return compileCombinator(ruleOrCombinator);
    }
  );
  return (answers, visibilityMap = {}) => {
    const results = compiledRules.map((rule) => rule(answers, visibilityMap));
    const visibleResults = results.filter(
      (result) => visibilityMap[result.id] !== false
    );
    if (visibleResults.length === 0) {
      return {
        id: combinator.id,
        valid: true,
        message: void 0
      };
    }
    const valid = combinator.combinator === "and" ? visibleResults.every((result) => result.valid) : visibleResults.some((result) => result.valid);
    return {
      id: combinator.id,
      valid,
      message: valid && isRuleSet(combinator) ? void 0 : combinator.message
    };
  };
};
var compileRuleSet = (ruleSet) => {
  return isRule(ruleSet) ? compileRule(ruleSet) : compileCombinator(ruleSet);
};
var isRequiredButEmpty = (question, answer) => {
  if (!question.visible && !question.suggested) return false;
  if (!(question.required || question.isRequired)) return false;
  return !answer || Array.isArray(answer) && !answer.length;
};
var createValidator = (validations) => {
  const compiledRuleset = validations.map(compileRuleSet);
  return ({
    question,
    answers,
    visibilityMap,
    locale,
    groups
  }) => {
    const answer = answers[question.name];
    if (!question.visible && !question.suggested || visibilityMap?.[question.name] === false) {
      return ok(question.name);
    }
    if (isRequiredButEmpty(question, answer)) {
      return {
        id: question.name,
        valid: false,
        message: getErrorMessage({
          questionErrorMessage: question.errorMessage,
          fallbackMessage: ERRORS.REQUIRED
        })
      };
    }
    const localValidation = validateQuestion({
      question,
      answers,
      locale,
      groups
    });
    if (!localValidation.valid) {
      return localValidation;
    }
    const formValidation = compiledRuleset.map((ruleFunction) => ruleFunction(answers, visibilityMap)).filter(
      (result) => result.id === question.name && result.valid === false
    )[0];
    return formValidation ?? ok(question.name);
  };
};

// src/hooks/useQuestionnaire/validateAction.ts
var getQuestionList = (sections, currentSectionIndex) => {
  if (currentSectionIndex === void 0) {
    return sections.reduce(
      (acc, section) => acc.concat(section.questions),
      []
    );
  }
  const completedSections = sections.slice(0, currentSectionIndex + 1);
  return completedSections.reduce(
    (acc, section) => acc.concat(section.questions),
    []
  );
};
var getFirstInvalidSection = (results, sections) => {
  if (results.length === 0) {
    return void 0;
  }
  const invalidQuestionIds = results.map(({ id }) => id);
  return sections.findIndex(
    (section) => section.questions.some(({ name }) => invalidQuestionIds.includes(name))
  );
};
var validateAction = ({
  answers,
  sections: sectionsFromState,
  validations,
  visibility,
  currentSectionIndex,
  lastViewedSectionIndex,
  config,
  manualErrors,
  groups
}) => {
  const sections = sectionsFromState;
  const sectionIndexToValidateUpTo = Math.max(
    lastViewedSectionIndex,
    currentSectionIndex
  );
  const questionList = getQuestionList(sections, sectionIndexToValidateUpTo);
  if (questionList.length === 0) {
    return { errors: {}, isValid: false };
  }
  const validateQuestion2 = createValidator(validations || []);
  const validateQuestions = (questions, answers2, visibility2, locale) => questions.map(
    (question) => validateQuestion2({
      question,
      answers: answers2,
      visibilityMap: visibility2,
      locale,
      groups
    })
  ).filter(({ valid }) => !valid);
  const results = validateQuestions(
    questionList,
    answers,
    visibility,
    config?.locale
  );
  const validationErrors = results.reduce(
    (acc, { id, message }) => ({
      ...acc,
      [id]: message
    }),
    {}
  );
  const allErrors = {
    ...validationErrors,
    ...manualErrors
  };
  const isValid = Object.keys(allErrors).length === 0;
  const manualErrorResults = Object.keys(manualErrors).map((id) => ({
    id,
    message: manualErrors[id],
    valid: false
  }));
  const allResults = [...results, ...manualErrorResults];
  const firstInvalidSectionIndex = getFirstInvalidSection(allResults, sections);
  return {
    isValid,
    errors: allErrors,
    firstInvalidSectionIndex
  };
};

// src/hooks/useQuestionnaire/reducer/actions/validateFormAction.ts
var handleValidateFormAction = (state) => {
  const { errors, firstInvalidSectionIndex } = validateAction(state);
  const { currentSectionIndex } = state;
  const errorCount = Object.keys(errors).length;
  const isValid = errorCount === 0;
  const updatedSectionIndex = Math.min(
    firstInvalidSectionIndex ?? currentSectionIndex,
    getVisibleSection(state, "next")
  );
  return {
    ...state,
    errors,
    isValid,
    currentSectionIndex: updatedSectionIndex
  };
};

// src/hooks/useQuestionnaire/reducer/actions/removeErrorAction.ts
var handleRemoveErrorAction = (state, payload) => {
  const { errors } = state;
  const updatedErrors = { ...errors };
  delete updatedErrors[payload];
  const isValid = Object.keys(updatedErrors).length === 0;
  return {
    ...state,
    errors: updatedErrors,
    isValid
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setAddressModeAction.ts
var handleSetAddressModeAction = (state, payload) => {
  const { name, mode } = payload;
  const { addressModes } = state;
  const updatedAddressModes = {
    ...addressModes,
    [name]: mode
  };
  const { sections, answers } = setAddressQuestionVisibility(
    name,
    state.sections,
    updatedAddressModes,
    state.answers
  );
  return {
    ...state,
    sections,
    visibility: getVisibilityMap({
      sections,
      addressModes: updatedAddressModes,
      groups: {},
      visibility: {}
    }),
    addressModes: updatedAddressModes,
    answers
  };
};

// src/hooks/useQuestionnaire/reducer/actions/showQuestionAction.ts
var handleShowQuestionAction = (state, payload) => {
  const { sections: sectionsFromState, currentSectionIndex } = state;
  const sections = sectionsFromState;
  const sectionIndex = sections.findIndex(
    ({ questions }) => questions.some((q) => q.name === payload)
  );
  return {
    ...state,
    currentSectionIndex: sectionIndex !== -1 ? sectionIndex : currentSectionIndex,
    selectedQuestion: payload,
    changedQuestions: []
  };
};

// src/hooks/useQuestionnaire/reducer/actions/editQuestionAction.ts
var handleEditQuestionAction = (state, payload) => {
  const { questionName, assumptionGroupIndex } = payload;
  const { sections: sectionsFromState } = state;
  const sections = sectionsFromState;
  const sectionIndex = sections.findIndex(
    ({ questions }) => questions.some((q) => q.name === questionName)
  );
  if (sectionIndex === -1) {
    return state;
  }
  const section = sections[sectionIndex];
  const question = section.questions.find((q) => q.name === questionName);
  if (!question) {
    return state;
  }
  const editSection = {
    ...section,
    displayAs: "edit",
    questions: [question],
    isVisited: true
  };
  const updatedSections = [...sections, editSection];
  return {
    ...state,
    sections: updatedSections,
    // Set current section to the new edit section
    currentSectionIndex: updatedSections.length - 1,
    // Reset changed list
    selectedQuestion: questionName,
    changedQuestions: [],
    changedAssumptionGroupIndex: assumptionGroupIndex ?? state.changedAssumptionGroupIndex
  };
};

// src/hooks/useQuestionnaire/reducer/actions/groups/flattenQuestions.ts
var flattenQuestions = ({
  payload,
  answers: initialAnswers,
  visibility: initialVisibility = {},
  groups: initialGroups = {}
}) => {
  const answers = { ...initialAnswers };
  const visibility = { ...initialVisibility };
  const groups = { ...initialGroups };
  const flattenedQuestions = payload.flatMap((item) => {
    if (isQuestion(item)) {
      visibility[item.name] = !!item.visible;
      return [item];
    }
    let result;
    if (isYesNoToggleGroup(item)) {
      result = flattenYesNoToggleGroup(item, answers, visibility);
    } else if (isMultiselectGroup(item)) {
      result = flattenMultiselectGroup(item, answers, visibility);
    } else if (isMultiselectToggleGroup(item)) {
      result = flattenMultiselectToggleGroup(item, answers, visibility);
    } else if (isAddressLookupGroup(item)) {
      result = flattenAddressLookupGroup(item, answers, visibility);
    } else if (isPercentGroup(item)) {
      result = flattenPercentGroup(item, answers, visibility);
    } else {
      result = createEmptyFlattenResult(answers, visibility);
    }
    Object.assign(groups, result.groups);
    Object.assign(answers, result.answers);
    Object.assign(visibility, result.visibility);
    return result.questions;
  });
  return { questions: flattenedQuestions, answers, groups, visibility };
};

// src/hooks/useQuestionnaire/reducer/actions/handleAppendQuestionsAction/filterAndAppendQuestions.ts
var filterAndAppendQuestions = ({
  existingQuestions,
  filteredQuestionNames,
  newQuestions,
  allNewQuestionNames
}) => {
  return [
    ...existingQuestions.filter(
      (q) => filteredQuestionNames.includes(q.name) && !allNewQuestionNames.includes(q.name)
    ),
    ...newQuestions
  ];
};

// src/hooks/useQuestionnaire/reducer/actions/handleAppendQuestionsAction/updateChangedQuestionsList.ts
var updateChangedQuestionsList = ({
  currentChangedQuestions,
  newQuestionNames
}) => {
  if (newQuestionNames.length === 0) {
    return {
      updatedChangedQuestions: currentChangedQuestions,
      filteredQuestionNames: currentChangedQuestions
    };
  }
  const firstNewQuestionName = newQuestionNames[0];
  const firstIndex = currentChangedQuestions.indexOf(firstNewQuestionName);
  const filteredQuestions = Array.from(
    new Set(
      firstIndex > -1 ? currentChangedQuestions.slice(0, firstIndex) : currentChangedQuestions
    )
  );
  return {
    updatedChangedQuestions: [...filteredQuestions, ...newQuestionNames],
    filteredQuestionNames: filteredQuestions
  };
};

// src/hooks/useQuestionnaire/reducer/actions/handleAppendQuestionsAction/index.ts
var handleAppendQuestionsAction = (state, payload) => {
  const {
    sections: sectionsFromState,
    currentSectionIndex,
    changedQuestions,
    visibility,
    answers,
    groups
  } = state;
  const sections = sectionsFromState;
  const changes = changedQuestions || [];
  const flattenedResult = flattenQuestions({
    payload,
    answers,
    visibility,
    groups
  });
  const allFlattenedQuestionNames = flattenedResult.questions.map((q) => q.name);
  const { updatedChangedQuestions, filteredQuestionNames } = updateChangedQuestionsList({
    currentChangedQuestions: changes,
    newQuestionNames: allFlattenedQuestionNames
  });
  const updatedSections = sections.map(
    (section, index) => index === currentSectionIndex ? {
      ...section,
      questions: filterAndAppendQuestions({
        existingQuestions: section.questions,
        filteredQuestionNames,
        newQuestions: flattenedResult.questions,
        allNewQuestionNames: allFlattenedQuestionNames
      })
    } : section
  );
  return {
    ...state,
    sections: updatedSections,
    changedQuestions: updatedChangedQuestions,
    visibility: flattenedResult.visibility,
    answers: flattenedResult.answers,
    groups: flattenedResult.groups
  };
};

// src/hooks/useQuestionnaire/reducer/actions/confirmAssumptionGroupAction.ts
var handleConfirmAssumptionGroupAction = (state, payload) => {
  const { confirmedAssumptionGroups } = state;
  const updatedConfirmedAssumptionGroups = new Set(confirmedAssumptionGroups);
  updatedConfirmedAssumptionGroups.add(payload);
  return {
    ...state,
    confirmedAssumptionGroups: updatedConfirmedAssumptionGroups
  };
};

// src/hooks/useQuestionnaire/mergeAnswers.ts
var trimValue = (value) => {
  if (!value) {
    return "";
  }
  if (Array.isArray(value)) {
    return value;
  }
  return value.toString().trim();
};
var addressSyncMap = {
  "UK address lookup": "UK correspondence address lookup",
  address_1: "customer[address_1]",
  address_2: "customer[address_2]",
  town: "customer[town]",
  uk_postcode: "customer[uk_postcode]"
};
function syncAddresses(answers) {
  for (const [key, value] of Object.entries(addressSyncMap)) {
    answers[value] = trimValue(answers[key]);
  }
  return answers;
}
function keepAddressesInSync(answers) {
  if (answers["Same correspondence address pre quote"] === "yes") {
    return syncAddresses(answers);
  }
  return answers;
}
function clearCorrespondenceAddress(answers) {
  for (const key of Object.values(addressSyncMap)) {
    delete answers[key];
  }
  return answers;
}
var mergeAnswers = (answers, payload) => {
  const { name, value } = payload;
  if (isObject(value)) {
    return keepAddressesInSync({
      ...answers,
      ...value
    });
  }
  const updatedAnswers = payload ? { ...answers, [name]: trimValue(value) } : answers;
  if (name === "Same correspondence address pre quote" && value === "no") {
    return clearCorrespondenceAddress(updatedAnswers);
  }
  return keepAddressesInSync(updatedAnswers);
};

// src/hooks/useQuestionnaire/reducer/actions/updateAnswerAction/applyGroupAnswerAndVisibility.ts
var deriveShadowChildrenAnswers = (shadowGroup, selectedValues, answers) => {
  const selected = new Set(selectedValues);
  const updated = { ...answers };
  for (const child of shadowGroup.names) {
    updated[child] = selected.has(child) ? "yes" : "no";
  }
  return updated;
};
var deriveNoGroupUpdates = ({
  groupNames,
  groups,
  answers,
  visibility
}) => {
  let nextAnswers = { ...answers };
  const nextVisibility = { ...visibility };
  for (const child of groupNames) {
    const childGroup = groups[child];
    if (isShadowGroup(childGroup)) {
      nextAnswers[child] = [];
      nextAnswers = deriveShadowChildrenAnswers(childGroup, [], nextAnswers);
    } else {
      nextAnswers[child] = "no";
    }
    nextVisibility[child] = false;
  }
  return { answers: nextAnswers, visibility: nextVisibility };
};
var deriveYesGroupUpdates = ({
  groupNames,
  groups,
  answers,
  visibility,
  serverVisibleByName
}) => {
  let nextAnswers = { ...answers };
  const nextVisibility = { ...visibility };
  for (const child of groupNames) {
    const childGroup = groups[child];
    if (isShadowGroup(childGroup)) {
      nextAnswers[child] = [];
      nextAnswers = deriveShadowChildrenAnswers(childGroup, [], nextAnswers);
    } else {
      delete nextAnswers[child];
    }
    nextVisibility[child] = serverVisibleByName[child] ?? false;
  }
  return { answers: nextAnswers, visibility: nextVisibility };
};
var applyGroupAnswerAndVisibility = (state, payload, serverVisibleByName) => {
  const { groups, answers, visibility } = state;
  const group = groups[payload.name];
  if (!group || group.names?.length === 0) {
    return { answers, visibility };
  }
  let nextAnswers = { ...answers };
  let nextVisibility = { ...visibility };
  if (group.kind === "group" || group.kind === "personalised_cover") {
    if (payload.value === "no") {
      const res = deriveNoGroupUpdates({
        groupNames: group.names,
        groups,
        answers: nextAnswers,
        visibility: nextVisibility
      });
      nextAnswers = res.answers;
      nextVisibility = res.visibility;
    } else if (payload.value === "yes") {
      const res = deriveYesGroupUpdates({
        groupNames: group.names,
        groups,
        answers: nextAnswers,
        visibility: nextVisibility,
        serverVisibleByName
      });
      nextAnswers = res.answers;
      nextVisibility = res.visibility;
    }
  } else if (isShadowGroup(group)) {
    const selectedValues = Array.isArray(payload.value) ? payload.value : [];
    nextAnswers = deriveShadowChildrenAnswers(
      group,
      selectedValues,
      nextAnswers
    );
  } else if (isAddressGroup(group)) {
    group.names.forEach((name) => {
      const hasAnswer = nextAnswers[name] !== void 0;
      const visible = serverVisibleByName[name];
      const q = state.sections[state.currentSectionIndex].questions.filter(isQuestion).find((q2) => q2.name === name);
      nextVisibility[name] = hasAnswer && visible && !q?.hiddenOnAutocomplete;
    });
  }
  return { answers: nextAnswers, visibility: nextVisibility };
};

// src/hooks/useQuestionnaire/reducer/actions/updateAnswerAction/updateAnswerAction.ts
var handleUpdateAnswerAction = (state, payload) => {
  const { answers, changedQuestions, isEditing } = state;
  if (payload.value === answers[payload.name]) return state;
  const mergedAnswers = mergeAnswers(answers, payload);
  const updatedChangedQuestions = [...changedQuestions ?? [], payload.name];
  const updatedState = {
    ...state,
    answers: mergedAnswers,
    changedQuestions: isEditing ? updatedChangedQuestions : changedQuestions
  };
  const currentSection = state.sections[state.currentSectionIndex];
  const serverVisibleState = Object.fromEntries(
    (currentSection?.questions ?? []).map(
      ({ name, visible }) => [name, Boolean(visible)]
    )
  );
  const { answers: groupAnswers, visibility: groupVisibility } = applyGroupAnswerAndVisibility(updatedState, payload, serverVisibleState);
  return {
    ...updatedState,
    answers: groupAnswers,
    visibility: groupVisibility
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setCurrentSectionAction.ts
var handleSetCurrentSectionAction = (state, payload) => {
  const maxSectionIndex = state.sections.length - 1;
  const newCurrentSectionIndex = Math.max(
    0,
    Math.min(payload, maxSectionIndex)
  );
  return {
    ...state,
    currentSectionIndex: newCurrentSectionIndex,
    lastViewedSectionIndex: Math.max(
      newCurrentSectionIndex,
      state.lastViewedSectionIndex
    ),
    isValid: false
  };
};

// src/hooks/useQuestionnaire/reducer/actions/showAddressGroupManualAction.ts
var handleShowAddressGroupManualAction = (state, payload) => {
  const { name } = payload;
  const { groups, visibility } = state;
  const group = groups?.[name];
  if (!group || group.kind !== "address") return state;
  const updatedVisibility = { ...visibility };
  group.names.forEach((childName) => {
    updatedVisibility[childName] = true;
  });
  return {
    ...state,
    visibility: updatedVisibility
  };
};

// src/hooks/useQuestionnaire/reducer/actions/setVisibility/setVisibility.ts
var handleSetVisibleAction = (state, payload) => {
  const visibilityMap = { ...state.visibility };
  payload.forEach((questionName) => {
    visibilityMap[questionName] = true;
  });
  return {
    ...state,
    visibility: visibilityMap
  };
};
var handleSetHiddenAction = (state, payload) => {
  const visibilityMap = { ...state.visibility };
  payload.forEach((questionName) => {
    visibilityMap[questionName] = false;
  });
  return {
    ...state,
    visibility: visibilityMap
  };
};

// src/hooks/useQuestionnaire/reducer/questionnaireReducer.tsx
var questionnaireReducer = (state, action) => {
  const { type } = action;
  switch (type) {
    case ACTIONS.SET: {
      return handleSetAction(state, action.payload);
    }
    case ACTIONS.SET_CONFIG: {
      return handleSetConfigAction(state, action.payload);
    }
    case ACTIONS.SET_ERRORS: {
      return handleSetErrorsAction(state, action.payload);
    }
    case ACTIONS.SET_MANUAL_ERROR: {
      return handleSetManualErrorAction(state, action.payload);
    }
    case ACTIONS.CLEAR_MANUAL_ERROR: {
      return handleClearManualErrorAction(state, action.payload);
    }
    case ACTIONS.SET_EDIT_MODE: {
      return handleSetEditModeAction(state, action.payload);
    }
    case ACTIONS.SET_VALIDATIONS: {
      return handleSetValidationsAction(state, action.payload);
    }
    case ACTIONS.MERGE: {
      return handleMergeAction(state, action.payload);
    }
    case ACTIONS.UPDATE_ANSWER: {
      return handleUpdateAnswerAction(state, action.payload);
    }
    case ACTIONS.PREVIOUS_SECTION: {
      return handlePreviousSectionAction(state);
    }
    case ACTIONS.NEXT_SECTION: {
      return handleNextSectionAction(state);
    }
    case ACTIONS.VALIDATE_FORM: {
      return handleValidateFormAction(state);
    }
    case ACTIONS.REMOVE_ERROR: {
      return handleRemoveErrorAction(state, action.payload);
    }
    case ACTIONS.SET_ADDRESS_MODE: {
      return handleSetAddressModeAction(state, action.payload);
    }
    case ACTIONS.SHOW_QUESTION: {
      return handleShowQuestionAction(state, action.payload);
    }
    case ACTIONS.EDIT_QUESTION: {
      return handleEditQuestionAction(state, action.payload);
    }
    case ACTIONS.APPEND_QUESTIONS: {
      return handleAppendQuestionsAction(state, action.payload);
    }
    case ACTIONS.CONFIRM_ASSUMPTION_GROUP: {
      return handleConfirmAssumptionGroupAction(state, action.payload);
    }
    case ACTIONS.SET_CURRENT_SECTION: {
      return handleSetCurrentSectionAction(state, action.payload);
    }
    case ACTIONS.SHOW_ADDRESS_GROUP_MANUAL: {
      return handleShowAddressGroupManualAction(state, action.payload);
    }
    case ACTIONS.SET_VISIBLE: {
      return handleSetVisibleAction(state, action.payload);
    }
    case ACTIONS.SET_HIDDEN: {
      return handleSetHiddenAction(state, action.payload);
    }
    default: {
      return state;
    }
  }
};

// src/hooks/useQuestionnaire/reducer/initialState.ts
var questionnaireInitialState = {
  config: {},
  sections: [],
  groups: {},
  validations: [],
  answers: {},
  visibility: {},
  errors: {},
  manualErrors: {},
  currentSectionIndex: 0,
  lastViewedSectionIndex: 0,
  isValid: false,
  isEditing: false,
  selectedQuestion: null,
  changedQuestions: [],
  changedAssumptionGroupIndex: 0,
  confirmedAssumptionGroups: /* @__PURE__ */ new Set(),
  addressModes: {}
};

// src/hooks/useQuestionnaire/atoms/questionnaireReducerAtom.tsx
var questionnaireReducerAtom = atomWithReducer(
  questionnaireInitialState,
  questionnaireReducer
);

// src/hooks/useQuestionnaire/atoms/questionnaireAnswerAtom.tsx
var questionnaireAnswerAtom = atomFamily(
  (questionName) => selectAtom(
    questionnaireReducerAtom,
    (state) => state.answers[questionName]
  )
);

// src/hooks/questionnaire/useAnswer.tsx
function useAnswer(questionName) {
  return useAtomValue(questionnaireAnswerAtom(questionName));
}

// src/hooks/questionnaire/useAnswers.tsx
import { useAtomValue as useAtomValue2 } from "jotai";

// src/hooks/useQuestionnaire/atoms/questionnaireAnswersAtom.tsx
import { selectAtom as selectAtom2 } from "jotai/utils";
var questionnaireAnswersAtom = selectAtom2(
  questionnaireReducerAtom,
  (state) => state.answers
);

// src/hooks/questionnaire/useAnswers.tsx
function useAnswers() {
  return useAtomValue2(questionnaireAnswersAtom);
}

// src/hooks/questionnaire/useCurrentSection.tsx
import { useAtomValue as useAtomValue3 } from "jotai";
import { useMemo } from "react";

// src/hooks/useQuestionnaire/atoms/questionnaireCurrentSectionAtom.tsx
import { selectAtom as selectAtom3 } from "jotai/utils";
var questionnaireCurrentSectionAtom = selectAtom3(
  questionnaireReducerAtom,
  (state) => {
    const currentSection = state.sections[state.currentSectionIndex];
    const isFirstSection = state.currentSectionIndex === 0;
    const isLastSection = state.currentSectionIndex === state.sections.length - 1;
    return {
      currentSectionIndex: state.currentSectionIndex,
      lastViewedSectionIndex: state.lastViewedSectionIndex,
      currentSection,
      isFirstSection,
      isLastSection,
      totalSections: state.sections.length
    };
  },
  shallowEqual
);

// src/hooks/useQuestionnaire/atoms/questionnaireCombinedErrorsAtom.tsx
import { selectAtom as selectAtom4 } from "jotai/utils";
var questionnaireCombinedErrorsAtom = selectAtom4(
  questionnaireReducerAtom,
  (state) => ({
    ...state.errors,
    ...state.manualErrors
  }),
  shallowEqual
);

// src/hooks/questionnaire/useCurrentSection.tsx
function useCurrentSection(initialSections) {
  const sectionsData = useAtomValue3(questionnaireCurrentSectionAtom);
  const errors = useAtomValue3(questionnaireCombinedErrorsAtom);
  const currentSection = sectionsData?.currentSection || initialSections?.[sectionsData?.currentSectionIndex ?? 0];
  const sectionErrors = useMemo(() => {
    if (!currentSection || !errors || Object.keys(errors).length === 0) {
      return {};
    }
    const currentSectionQuestionNames = currentSection.questions.filter((q) => "name" in q).map((q) => q.name);
    return Object.keys(errors).filter((errorKey) => currentSectionQuestionNames.includes(errorKey)).reduce((acc, key) => ({ ...acc, [key]: errors[key] }), {});
  }, [errors, currentSection]);
  if (!sectionsData) {
    return {
      currentSection: initialSections?.[0],
      currentSectionIndex: 0,
      lastViewedSectionIndex: 0,
      isFirstSection: true,
      isLastSection: false,
      totalSections: initialSections?.length ?? 0,
      errors: sectionErrors
    };
  }
  if (sectionsData.currentSection) {
    return {
      ...sectionsData,
      errors: sectionErrors
    };
  }
  return {
    ...sectionsData,
    currentSection,
    errors: sectionErrors
  };
}

// src/hooks/questionnaire/useError.tsx
import { useAtomValue as useAtomValue4 } from "jotai";

// src/hooks/useQuestionnaire/atoms/questionnaireErrorAtom.tsx
import { atomFamily as atomFamily2, selectAtom as selectAtom5 } from "jotai/utils";
var questionnaireErrorAtom = atomFamily2(
  (questionName) => selectAtom5(
    questionnaireCombinedErrorsAtom,
    (errors) => errors[questionName]
  )
);

// src/hooks/questionnaire/useError.tsx
function useError(questionName) {
  return useAtomValue4(questionnaireErrorAtom(questionName));
}

// src/hooks/questionnaire/useQuestions.tsx
import { useMemo as useMemo3 } from "react";

// src/hooks/useQuestionnaire/useQuestionnaireActions.tsx
import { useSetAtom } from "jotai";
import { useMemo as useMemo2 } from "react";
var createSetSections = (dispatch) => (sections) => {
  dispatch({ type: ACTIONS.SET, payload: sections });
};
var createSetValidations = (dispatch) => (validations) => {
  dispatch({ type: ACTIONS.SET_VALIDATIONS, payload: validations });
};
var createSetConfig = (dispatch) => (config) => {
  dispatch({ type: ACTIONS.SET_CONFIG, payload: config });
};
var createSetErrors = (dispatch) => (errors) => {
  dispatch({ type: ACTIONS.SET_ERRORS, payload: errors });
};
var createSetEditMode = (dispatch) => (isEditing) => {
  dispatch({ type: ACTIONS.SET_EDIT_MODE, payload: isEditing });
};
var createSetAddressMode = (dispatch) => (name, mode) => {
  dispatch({ type: ACTIONS.SET_ADDRESS_MODE, payload: { name, mode } });
};
var createMerge = (dispatch) => (sections) => {
  dispatch({ type: ACTIONS.MERGE, payload: sections });
};
var createUpdateAnswer = (dispatch) => (event) => {
  const { target } = event;
  const { name, value } = target;
  dispatch({ type: ACTIONS.UPDATE_ANSWER, payload: { name, value } });
};
var createNavigatePrev = (dispatch) => () => {
  dispatch({ type: ACTIONS.PREVIOUS_SECTION });
};
var createNavigateNext = (dispatch) => () => {
  dispatch({ type: ACTIONS.NEXT_SECTION });
};
var createValidateAction = (dispatch) => () => {
  dispatch({ type: ACTIONS.VALIDATE_FORM });
};
var createRemoveError = (dispatch) => (questionName) => {
  dispatch({ type: ACTIONS.REMOVE_ERROR, payload: questionName });
};
var createSetManualError = (dispatch) => (name, message) => {
  dispatch({ type: ACTIONS.SET_MANUAL_ERROR, payload: { name, message } });
};
var createClearManualError = (dispatch) => (name) => {
  dispatch({ type: ACTIONS.CLEAR_MANUAL_ERROR, payload: name });
};
var createShowQuestion = (dispatch) => (questionName) => {
  dispatch({ type: ACTIONS.SHOW_QUESTION, payload: questionName });
};
var createEditQuestion = (dispatch) => ({
  questionName,
  assumptionGroupIndex
}) => {
  dispatch({
    type: ACTIONS.EDIT_QUESTION,
    payload: { questionName, assumptionGroupIndex }
  });
};
var createAppendQuestions = (dispatch) => (questions) => {
  dispatch({ type: ACTIONS.APPEND_QUESTIONS, payload: questions });
};
var createConfirmAssumptionGroup = (dispatch) => (payload) => {
  dispatch({ type: ACTIONS.CONFIRM_ASSUMPTION_GROUP, payload });
};
var createSetCurrentSectionIndex = (dispatch) => (sectionIndex) => {
  dispatch({ type: ACTIONS.SET_CURRENT_SECTION, payload: sectionIndex });
};
var createShowAddressGroupManual = (dispatch) => (name) => {
  dispatch({ type: ACTIONS.SHOW_ADDRESS_GROUP_MANUAL, payload: { name } });
};
var createSetVisible = (dispatch) => (questionNames) => {
  dispatch({ type: ACTIONS.SET_VISIBLE, payload: questionNames });
};
var createSetHidden = (dispatch) => (questionNames) => {
  dispatch({ type: ACTIONS.SET_HIDDEN, payload: questionNames });
};
var useQuestionnaireActions = () => {
  const dispatch = useSetAtom(questionnaireReducerAtom);
  return useMemo2(
    () => ({
      setSections: createSetSections(dispatch),
      setValidations: createSetValidations(dispatch),
      setConfig: createSetConfig(dispatch),
      setErrors: createSetErrors(dispatch),
      setEditMode: createSetEditMode(dispatch),
      setAddressMode: createSetAddressMode(dispatch),
      merge: createMerge(dispatch),
      updateAnswer: createUpdateAnswer(dispatch),
      navigatePrev: createNavigatePrev(dispatch),
      navigateNext: createNavigateNext(dispatch),
      validateAction: createValidateAction(dispatch),
      removeError: createRemoveError(dispatch),
      setManualError: createSetManualError(dispatch),
      clearManualError: createClearManualError(dispatch),
      showQuestion: createShowQuestion(dispatch),
      editQuestion: createEditQuestion(dispatch),
      appendQuestions: createAppendQuestions(dispatch),
      confirmAssumptionGroup: createConfirmAssumptionGroup(dispatch),
      setCurrentSectionIndex: createSetCurrentSectionIndex(dispatch),
      showAddressGroupManual: createShowAddressGroupManual(dispatch),
      setVisible: createSetVisible(dispatch),
      setHidden: createSetHidden(dispatch)
    }),
    [dispatch]
  );
};

// src/hooks/questionnaire/useQuestions.tsx
function useQuestions() {
  const actions = useQuestionnaireActions();
  return useMemo3(
    () => ({
      show: actions.setVisible,
      hide: actions.setHidden
    }),
    [actions.setVisible, actions.setHidden]
  );
}

// src/hooks/questionnaire/useQuestionnaireInitialization.tsx
import { useEffect as useEffect2 } from "react";
var useQuestionnaireInitialization = (sections, validations, config = {}) => {
  const actions = useQuestionnaireActions();
  useEffect2(() => {
    if (config && Object.keys(config).length) {
      actions.setConfig(config);
    }
  }, [config, actions]);
  useEffect2(() => {
    if (sections) {
      actions.setSections(sections);
    }
  }, [sections, actions]);
  useEffect2(() => {
    if (validations && Object.keys(validations).length) {
      actions.setValidations(validations);
    }
  }, [validations, actions]);
};

// src/hooks/questionnaire/useQuestionnaireNavigation.ts
import { useCallback as useCallback2 } from "react";

// src/hooks/useCoverGeniusSeeMoreCoversState/useCoverGeniusSeeMoreCoversState.tsx
import { useAtom as useAtom2 } from "jotai";
import { useHydrateAtoms as useHydrateAtoms2 } from "jotai/utils";
var useSetSeeMoreCoversState = (seeMoreCovers) => {
  useHydrateAtoms2([[seeMoreCoversStateAtom, seeMoreCovers]]);
};
var useCoverGeniusSeeMoreCoversState = () => useAtom2(seeMoreCoversStateAtom);

// src/hooks/questionnaire/useSections.tsx
import { useAtomValue as useAtomValue5 } from "jotai";

// src/hooks/useQuestionnaire/atoms/questionnaireSectionsAtom.tsx
import { selectAtom as selectAtom6 } from "jotai/utils";
var questionnaireSectionsAtom = selectAtom6(
  questionnaireReducerAtom,
  (state) => state.sections
);

// src/hooks/questionnaire/useSections.tsx
function useSections() {
  return useAtomValue5(questionnaireSectionsAtom);
}

// src/hooks/questionnaire/useQuestionnaireNavigation.ts
var useQuestionnaireNavigation = () => {
  const sections = useSections();
  const { navigatePrev, navigateNext } = useQuestionnaireActions();
  const { currentSection } = useCurrentSection(sections);
  const questions = useQuestions();
  const [showSeeMoreCoversTrigger, seeMoreCovers] = useCoverGeniusSeeMoreCoversState();
  const goBack = useCallback2(() => {
    navigatePrev();
  }, [navigatePrev]);
  const goNext = useCallback2(() => {
    navigateNext();
  }, [navigateNext]);
  const handleSeeMoreCovers = useCallback2(() => {
    seeMoreCovers(false);
    const questionNames = currentSection?.questions.filter(
      (q) => "suggested" in q && q.suggested === true
    ).map(({ name }) => name) ?? [];
    questions.show(questionNames);
  }, [seeMoreCovers, questions, currentSection]);
  return {
    goBack,
    goNext,
    handleSeeMoreCovers,
    showSeeMoreCoversTrigger
  };
};

// src/hooks/questionnaire/useQuestionnaireSubmit.ts
import { useCallback as useCallback4, useMemo as useMemo5, useRef as useRef2 } from "react";

// src/hooks/useQuestionnaire/useQuestionnaire.tsx
import { useAtomValue as useAtomValue6 } from "jotai";
import { useEffect as useEffect3, useMemo as useMemo4 } from "react";

// src/hooks/useQuestionnaire/QuestionFilters.ts
var QuestionFilters = class {
  constructor({ sections, currentSectionIndex }) {
    this.sections = sections;
    this.currentSectionIndex = currentSectionIndex;
  }
  /**
   * Get a list of question names in a given section index
   */
  inSectionIndex(sectionIndex) {
    const sections = this.sections;
    const section = sections[sectionIndex];
    if (!section || section?.questions?.length === 0) {
      return [];
    }
    return section.questions.filter(isQuestion).map((question) => question.name);
  }
  /**
   * Get a list of question names in the current section
   */
  inCurrentSection() {
    return this.inSectionIndex(this.currentSectionIndex);
  }
  /**
   * Find a question by its ID across all sections
   */
  getById(id) {
    for (const section of this.sections) {
      const question = section.questions.filter(isQuestion).find((q) => q.id === id);
      if (question) {
        return question;
      }
    }
    return void 0;
  }
  /**
   * Find a question by its name across all sections
   */
  getByName(name) {
    for (const section of this.sections) {
      const question = section.questions.filter(isQuestion).find((q) => q.name === name);
      if (question) {
        return question;
      }
    }
    return void 0;
  }
};

// src/hooks/useQuestionnaire/useQuestionnaire.tsx
var useQuestionnaire = (sections, validations, config = {}) => {
  const state = useAtomValue6(questionnaireReducerAtom);
  const combinedErrors = useAtomValue6(questionnaireCombinedErrorsAtom);
  const actions = useQuestionnaireActions();
  useEffect3(() => {
    if (config && Object.keys(config).length) {
      actions.setConfig(config);
    }
  }, [config, actions]);
  useEffect3(() => {
    if (sections) {
      actions.setSections(sections);
    }
  }, [sections, actions]);
  useEffect3(() => {
    if (validations && Object.keys(validations).length) {
      actions.setValidations(validations);
    }
  }, [validations, actions]);
  const validateQuestion2 = useMemo4(
    () => createValidator(state.validations ?? []),
    [state.validations]
  );
  const filter = useMemo4(() => new QuestionFilters(state), [state]);
  return {
    ...state,
    ...actions,
    errors: combinedErrors,
    validateQuestion: validateQuestion2,
    validateForm: validateAction,
    questions: {
      show: actions.setVisible,
      hide: actions.setHidden,
      filter
    }
  };
};

// src/hooks/useQuestionnaire/computeMergedAnswersWithGroups.ts
var computeMergedAnswersWithGroups = ({
  answers,
  groups,
  payload
}) => {
  const mergedAnswers = mergeAnswers(answers, payload);
  const { answers: groupAnswers } = applyGroupAnswerAndVisibility(
    {
      groups,
      answers: mergedAnswers,
      visibility: {},
      manualErrors: {},
      sections: [{ id: "", questions: [] }],
      currentSectionIndex: 0,
      lastViewedSectionIndex: 0
    },
    payload,
    {}
  );
  return groupAnswers;
};

// src/hooks/useQuestionRefs/useQuestionRefs.tsx
import { useCallback as useCallback3 } from "react";
var questionRefsStore = {};
var buttonRefsStore = {};
var createRef = () => {
  return { current: null };
};
var useQuestionRefs = () => {
  const registerQuestionRef = useCallback3((questionName) => {
    const ref = createRef();
    questionRefsStore[questionName] = ref;
    return ref;
  }, []);
  const unregisterQuestionRef = useCallback3((questionName) => {
    delete questionRefsStore[questionName];
  }, []);
  const getQuestionRef = useCallback3((name) => {
    return questionRefsStore[name];
  }, []);
  const focusQuestion = useCallback3((name) => {
    const questionRef = questionRefsStore[name];
    if (questionRef?.current) {
      const focusableElement = questionRef.current.querySelector(
        FOCUSABLE_ELEMENT_SELECTOR
      );
      if (focusableElement instanceof HTMLElement) {
        focusableElement.focus({
          focusVisible: true,
          preventScroll: true
        });
        if (focusableElement.scrollIntoView) {
          focusableElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      } else {
        questionRef.current.focus({
          focusVisible: true,
          preventScroll: true
        });
        if (questionRef.current.scrollIntoView) {
          questionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      }
    }
  }, []);
  const focusFirstError = useCallback3(
    (errors) => {
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        const firstErrorKey = errorKeys[0];
        focusQuestion(firstErrorKey);
      }
    },
    [focusQuestion]
  );
  const registerButtonRef = useCallback3(
    (key, buttonElement) => {
      buttonRefsStore[key] = buttonElement;
    },
    []
  );
  const unregisterButtonRef = useCallback3((key) => {
    delete buttonRefsStore[key];
  }, []);
  const getButtonRef = useCallback3((key) => {
    return buttonRefsStore[key] ?? null;
  }, []);
  return {
    // Function to create and register a ref for a question
    registerQuestionRef,
    // Function to unregister a ref (for cleanup)
    unregisterQuestionRef,
    // Access to all registered question refs
    questionRefs: questionRefsStore,
    // Get a specific question's ref
    getQuestionRef,
    // Focus a question by finding and focusing its first focusable element
    focusQuestion,
    // Focus the first question that has an error
    focusFirstError,
    // Generic function to register any button ref
    registerButtonRef,
    // Generic function to unregister any button ref
    unregisterButtonRef,
    // Generic function to get any button ref
    getButtonRef
  };
};

// src/hooks/questionnaire/useQuestionnaireSubmit.ts
var useQuestionnaireSubmit = () => {
  const formRef = useRef2(null);
  const tokens = useTokensValues();
  const { locale } = useLocale();
  const state = useQuestionnaire();
  const {
    answers,
    sections,
    validateForm,
    setErrors,
    setCurrentSectionIndex,
    visibility
  } = state;
  const url = useQuestionnaireFormUrl();
  const { focusFirstError } = useQuestionRefs();
  const transformedAnswers = useMemo5(
    () => transformAnswers(answers, sections, visibility, locale),
    [answers, sections, visibility, locale]
  );
  const validate = useCallback4(
    () => validateForm(state),
    [validateForm, state]
  );
  const inputValues = useMemo5(
    () => Object.entries(transformedAnswers).reduce(
      (acc, [key, value]) => {
        if (!value) {
          return acc;
        }
        if (Array.isArray(value)) {
          return [...acc, ...value.map((v) => [`${key}[]`, v])];
        }
        return [...acc, [key, value.toString()]];
      },
      []
    ),
    [transformedAnswers]
  );
  const handleSubmit = useCallback4(
    (event) => {
      event.preventDefault();
      const { isValid, errors, firstInvalidSectionIndex } = validate();
      if (isValid) {
        formRef.current?.submit();
      } else {
        setErrors(errors);
        focusFirstError(errors);
        if (firstInvalidSectionIndex !== void 0) {
          setCurrentSectionIndex(firstInvalidSectionIndex);
        }
      }
    },
    [validate, setErrors, focusFirstError, setCurrentSectionIndex]
  );
  return {
    formRef,
    handleSubmit,
    inputValues,
    tokens,
    url
  };
};

// src/hooks/questionnaire/useQuestionnaireValidation.ts
import { useCallback as useCallback7 } from "react";

// src/hooks/usePrepopulateAnswers/usePrepopulateAnswers.tsx
import { useMutation } from "@tanstack/react-query";
import { useAtomCallback } from "jotai/utils";
import { useCallback as useCallback5 } from "react";
function prepopulateAnswersApi(config, tokens) {
  return async function prepopulateAnswers(answers) {
    const isCommercial = answers.landlord_type !== "residential";
    const vertical = isCommercial ? "commercial_landlord" : config?.vertical;
    const body = snakeCaseKeys({
      ...tokens ?? {},
      ...config ?? {},
      vertical,
      partial_proposal: answers
    });
    return fetch(PREPOP_ENDPOINT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json; charset=utf-8"
      },
      body: JSON.stringify(body)
    }).then((response) => {
      if (!response.ok) {
        return Promise.reject(new Error("Failed to prepopulate answers"));
      }
      return response.json();
    });
  };
}
function usePrepopulateAnswers() {
  const { config } = useQuestionnaire();
  const { locale } = useLocale();
  const tokens = useTokensValues();
  const isPrepopulatingAnswers = useMutationStatus("prepopulateAnswers");
  const { mutateAsync } = useMutation({
    mutationKey: ["prepopulateAnswers"],
    mutationFn: prepopulateAnswersApi(config, tokens)
  });
  const getQuestionnaireState = useAtomCallback(
    useCallback5((get) => get(questionnaireReducerAtom), [])
  );
  const prepopulateAnswers = async (answers) => {
    const state = getQuestionnaireState();
    const transformedAnswers = transformAnswers(
      answers,
      state.sections,
      state.visibility,
      locale
    );
    await mutateAsync(transformedAnswers).then((response) => {
      if (response.url) {
        window.location.replace(response.url);
      }
    });
  };
  return {
    prepopulateAnswers,
    isPending: isPrepopulatingAnswers
  };
}

// src/hooks/useRedirect/useRedirect.tsx
import { useAtomValue as useAtomValue7, useSetAtom as useSetAtom2 } from "jotai";
var useRedirect = () => {
  return useAtomValue7(redirectAtom);
};
var useSetRedirect = () => {
  const setRedirectUrl = useSetAtom2(redirectAtom);
  return setRedirectUrl;
};

// src/hooks/useSendAnswers/useSendAnswers.ts
import { getAirbrake } from "@simplybusiness/services";
import { useMutation as useMutation2 } from "@tanstack/react-query";
import { useAtomCallback as useAtomCallback2 } from "jotai/utils";
import { useCallback as useCallback6 } from "react";
var airbrake = getAirbrake({
  projectId: AIRBRAKE_PROJECT_ID,
  projectKey: AIRBRAKE_PROJECT_KEY
});
var useSendAnswers = () => {
  const [tokens, setTokens] = useTokens();
  const setRedirectUrl = useSetRedirect();
  const { merge } = useQuestionnaireActions();
  const { locale } = useLocale();
  const isSendingAnswers = useMutationStatus("sendAnswers");
  const getQuestionnaireState = useAtomCallback2(
    useCallback6((get) => get(questionnaireReducerAtom), [])
  );
  const mutation = useMutation2({
    mutationFn: (answers) => {
      const state = getQuestionnaireState();
      const transformedAnswers = transformAnswers(
        answers,
        state.sections,
        state.visibility,
        locale
      );
      const answersArray = mapToArray(transformedAnswers);
      return apiRequest("?step_action=questions", {
        ...tokens,
        questions: answersArray
      });
    },
    mutationKey: ["sendAnswers"]
  });
  const { isError, error, isSuccess, mutateAsync } = mutation;
  const sendAnswers = (answers) => {
    return mutateAsync(answers, {
      onSuccess: ({ data }) => {
        const { sections } = getPropsByName({
          name: "questionnaire",
          components: data.layout.components
        });
        merge(sections);
        setTokens(data.configuration);
        setRedirectUrl(data.layout.components[0].props.config?.redirectUrl);
      },
      onError: (error2) => {
        console.error(`Error updating questions: ${error2.message}`);
        airbrake.notify(error2);
      }
    });
  };
  return {
    sendAnswers,
    isSuccess,
    isPending: isSendingAnswers,
    isError,
    error
  };
};

// src/hooks/questionnaire/useQuestionnaireValidation.ts
var useQuestionnaireValidation = () => {
  const sections = useSections();
  const state = useQuestionnaire();
  const { validateForm, setErrors } = state;
  const { setCurrentSectionIndex } = useQuestionnaireActions();
  const { goNext } = useQuestionnaireNavigation();
  const { currentSectionIndex } = useCurrentSection(sections);
  const answers = useAnswers();
  const landlordType = useAnswer("Landlord type");
  const { sendAnswers } = useSendAnswers();
  const { prepopulateAnswers } = usePrepopulateAnswers();
  const redirectUrl = useRedirect();
  const { focusFirstError } = useQuestionRefs();
  const handleSectionUpdate = useCallback7(() => {
    try {
      const validate = () => validateForm(state);
      const { isValid, errors, firstInvalidSectionIndex } = validate();
      if (!!landlordType && landlordType !== "residential") {
        prepopulateAnswers({
          ...answers,
          "Landlord type": landlordType,
          "Property type": landlordType
        }).catch(() => {
          setErrors({ "Landlord type": "Unable to continue" });
        });
        return;
      }
      const earlierSectionInvalid = !isValid && firstInvalidSectionIndex !== void 0 && firstInvalidSectionIndex < currentSectionIndex;
      if (earlierSectionInvalid) {
        setCurrentSectionIndex(firstInvalidSectionIndex);
        return false;
      }
      const currentSectionInvalid = !isValid && firstInvalidSectionIndex !== void 0 && firstInvalidSectionIndex === currentSectionIndex;
      if (currentSectionInvalid) {
        setErrors(errors);
        focusFirstError(errors);
        return false;
      }
      sendAnswers(answers).then(() => {
        if (redirectUrl) {
          window.location.assign(redirectUrl);
        } else {
          goNext();
        }
      }).catch(console.error);
    } catch (e) {
      console.error(e);
    }
  }, [
    answers,
    currentSectionIndex,
    validateForm,
    state,
    setCurrentSectionIndex,
    setErrors,
    focusFirstError,
    sendAnswers,
    goNext,
    prepopulateAnswers,
    redirectUrl,
    landlordType
  ]);
  return {
    handleSectionUpdate
  };
};

// src/hooks/questionnaire/useVisibility.tsx
import { useAtomValue as useAtomValue8 } from "jotai";

// src/hooks/useQuestionnaire/atoms/questionnaireVisibilityAtom.tsx
import { atomFamily as atomFamily3, selectAtom as selectAtom7 } from "jotai/utils";
var questionnaireVisibilityAtom = atomFamily3(
  (questionName) => selectAtom7(
    questionnaireReducerAtom,
    (state) => state.visibility?.[questionName]
  )
);

// src/hooks/questionnaire/useVisibility.tsx
function useVisibility(questionName) {
  return useAtomValue8(questionnaireVisibilityAtom(questionName));
}

// src/hooks/questionnaire/useVisibilityMap.tsx
import { useAtomValue as useAtomValue9 } from "jotai";
import { selectAtom as selectAtom8 } from "jotai/utils";
var visibilityMapAtom = selectAtom8(
  questionnaireReducerAtom,
  (state) => state.visibility ?? {},
  shallowEqual
);
function useVisibilityMap() {
  return useAtomValue9(visibilityMapAtom);
}

// src/components/Questionnaire/Section/index.tsx
import { forwardRef as forwardRef12 } from "react";

// src/components/Questionnaire/Section/EditSection.tsx
import {
  Box,
  Button as Button13
} from "@simplybusiness/mobius";
import classNames6 from "classnames/dedupe";
import { forwardRef as forwardRef6, useEffect as useEffect13 } from "react";

// src/hooks/useDependentQuestions/useDependentQuestions.ts
import { getAirbrake as getAirbrake2 } from "@simplybusiness/services";
import { useMutation as useMutation3 } from "@tanstack/react-query";
import { useAtomCallback as useAtomCallback3 } from "jotai/utils";
import { useCallback as useCallback8 } from "react";
var airbrake2 = getAirbrake2({
  projectId: AIRBRAKE_PROJECT_ID,
  projectKey: AIRBRAKE_PROJECT_KEY
});
var useDependentQuestions = () => {
  const [tokens, setTokens] = useTokens();
  const { appendQuestions } = useQuestionnaireActions();
  const { locale } = useLocale();
  const isSendingAnswers = useMutationStatus("dependentQuestions");
  const getQuestionnaireState = useAtomCallback3(
    useCallback8((get) => get(questionnaireReducerAtom), [])
  );
  const mutation = useMutation3({
    mutationFn: ({ answers, targetQuestion }) => {
      const state = getQuestionnaireState();
      const transformedAnswers = transformAnswers(
        answers,
        state.sections,
        state.visibility,
        locale
      );
      const answersArray = mapToArray(transformedAnswers);
      return apiRequest("?step_action=dependent_questions", {
        ...tokens,
        answers: answersArray,
        targetQuestion
      });
    },
    mutationKey: ["dependentQuestions"]
  });
  const { isError, error, isSuccess, mutateAsync } = mutation;
  const sendAnswers = (answers, targetQuestion) => {
    return mutateAsync(
      { answers, targetQuestion },
      {
        onSuccess: ({ data }) => {
          const { questions } = data;
          appendQuestions(questions);
          setTokens(data.configuration);
        },
        onError: (error2) => {
          if (error2.name !== "AbortError" && error2.name !== "TimeoutError") {
            console.error(`Error updating questions: ${error2.message}`);
            airbrake2.notify(error2);
          }
        }
      }
    );
  };
  return {
    sendAnswers,
    isSuccess,
    isPending: isSendingAnswers,
    isError,
    error
  };
};

// src/components/Questionnaire/Section/Question/Question.tsx
import { useLabel, useValidationClasses } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext11 } from "@simplybusiness/services";
import classNames5 from "classnames/dedupe";
import { useId as useId4, useEffect as useEffect12, useMemo as useMemo10 } from "react";

// src/components/Questionnaire/Section/Question/HelpLink/HelpLink.tsx
import { circleInfo } from "@simplybusiness/icons";
import { Button as Button4, Drawer, Icon as Icon4, TextOrHTML } from "@simplybusiness/mobius";
import { useState as useState2 } from "react";
import { Fragment as Fragment2, jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var HelpLink = ({
  headline,
  label,
  text,
  displayAsButton,
  onOpenDrawer
}) => {
  const [open, setOpen] = useState2(false);
  const openDrawer = () => {
    if (onOpenDrawer) {
      onOpenDrawer();
    }
    setOpen(true);
  };
  const closeDrawer = () => {
    setOpen(false);
  };
  return /* @__PURE__ */ jsxs5(Fragment2, { children: [
    displayAsButton ? /* @__PURE__ */ jsxs5(
      Button4,
      {
        className: "mobius-question__help-button",
        onClick: openDrawer,
        size: "sm",
        variant: "ghost",
        children: [
          /* @__PURE__ */ jsx10(Icon4, { icon: circleInfo, title: "Get more information" }),
          " ",
          label
        ]
      }
    ) : /* @__PURE__ */ jsxs5(
      Button4,
      {
        className: "mobius-question__help-link",
        onClick: openDrawer,
        variant: "link",
        size: "sm",
        children: [
          /* @__PURE__ */ jsx10(Icon4, { icon: circleInfo, size: "sm" }),
          /* @__PURE__ */ jsx10("span", { children: label })
        ]
      }
    ),
    /* @__PURE__ */ jsxs5(
      Drawer,
      {
        className: "mobius-question__help-link-drawer",
        isOpen: open,
        direction: "right",
        closeLabel: "Close",
        onClose: closeDrawer,
        children: [
          /* @__PURE__ */ jsx10(Drawer.Header, { children: headline }),
          /* @__PURE__ */ jsx10(Drawer.Content, { children: /* @__PURE__ */ jsx10(TextOrHTML, { text, htmlElementType: "div" }) })
        ]
      }
    )
  ] });
};

// src/components/Questionnaire/Section/Question/HelpLink/CompareTheMarketHelpLink/HelpLink.tsx
import { chevronDown } from "@simplybusiness/icons";
import { Button as Button5, Drawer as Drawer2, Icon as Icon5, TextOrHTML as TextOrHTML2 } from "@simplybusiness/mobius";
import { useState as useState3 } from "react";
import { Fragment as Fragment3, jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
var HelpLink2 = ({
  headline,
  label,
  text,
  onOpenDrawer
}) => {
  const [open, setOpen] = useState3(false);
  const openDrawer = () => {
    if (onOpenDrawer) {
      onOpenDrawer();
    }
    setOpen(true);
  };
  const closeDrawer = () => {
    setOpen(false);
  };
  return /* @__PURE__ */ jsxs6(Fragment3, { children: [
    /* @__PURE__ */ jsxs6(
      Button5,
      {
        className: "mobius-question__help-link",
        onClick: openDrawer,
        variant: "link",
        size: "sm",
        children: [
          /* @__PURE__ */ jsx11(Icon5, { fixedWidth: true, icon: chevronDown, size: "sm" }),
          /* @__PURE__ */ jsx11("span", { children: label })
        ]
      }
    ),
    /* @__PURE__ */ jsxs6(
      Drawer2,
      {
        className: "mobius-question__help-link-drawer",
        isOpen: open,
        direction: "right",
        closeLabel: "Close",
        onClose: closeDrawer,
        children: [
          /* @__PURE__ */ jsx11(Drawer2.Header, { children: headline }),
          /* @__PURE__ */ jsx11(Drawer2.Content, { children: /* @__PURE__ */ jsx11(TextOrHTML2, { text, htmlElementType: "div" }) })
        ]
      }
    )
  ] });
};

// src/components/Questionnaire/Section/Question/HelpLink/index.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
var HelpLink3 = (props) => {
  const { site } = useEnvironmentValues();
  if (site === "comparethemarket") {
    return /* @__PURE__ */ jsx12(HelpLink2, { ...props });
  }
  return /* @__PURE__ */ jsx12(HelpLink, { ...props });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/QuestionFactory.tsx
import { useSnowplowContext as useSnowplowContext10 } from "@simplybusiness/services";
import { useCallback as useCallback11, useEffect as useEffect11, useMemo as useMemo9, useState as useState11 } from "react";

// src/hooks/useRestoreFocus/useRestoreFocus.ts
import { useEffect as useEffect4, useRef as useRef3 } from "react";
var useRestoreFocus = (shouldBlockRestore) => {
  const elementToRefocusRef = useRef3(null);
  const storeFocusedElement = () => {
    elementToRefocusRef.current = document.activeElement;
  };
  useEffect4(() => {
    const element = elementToRefocusRef.current;
    if (!shouldBlockRestore && element && "focus" in element) {
      const focusableElement = element;
      focusableElement.focus();
      elementToRefocusRef.current = null;
    }
  }, [shouldBlockRestore]);
  return { storeFocusedElement };
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AddressLookupQuestion/AddressLookupQuestion.tsx
import {
  AddressLookup,
  Button as Button6,
  LoqateAddressLookupService
} from "@simplybusiness/mobius";
import { useRef as useRef4 } from "react";
import { useSnowplowContext as useSnowplowContext3 } from "@simplybusiness/services";

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AddressLookupQuestion/transformAddressData.ts
var POSTCODE_FIELD = "PostalCode";
var ADDRESS_FIELD_MAP = {
  "1": "Line1",
  "2": "Line2",
  town: "City",
  postcode: POSTCODE_FIELD
};
var cleanPostalCode = (postalCode) => {
  return postalCode.split("-")[0];
};
var transformAddressData = ({
  questionName,
  addressData,
  mappedFields
}) => mappedFields.reduce(
  (acc, { associatedQuestionId }) => {
    const lowerCaseId = associatedQuestionId.toLowerCase();
    for (const [key, value] of Object.entries(ADDRESS_FIELD_MAP)) {
      if (lowerCaseId.includes(key)) {
        const data = addressData[value] || "";
        acc[associatedQuestionId] = value === POSTCODE_FIELD ? cleanPostalCode(data) : data;
        break;
      }
    }
    return acc;
  },
  {
    [questionName]: addressData.Label?.replace(/\n/g, ", ")
  }
);

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AddressLookupQuestion/AddressLookupQuestion.tsx
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
import { createElement } from "react";
var AddressLookupQuestion = ({
  questionFieldProps,
  config,
  sectionTitle,
  setAddressMode
}) => {
  const {
    value,
    onChange,
    onFocus,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const typedConfig = config;
  const { apiKey, mappedFields } = typedConfig;
  const { snowplow } = useSnowplowContext3();
  const { name } = questionFieldProps;
  const sendAddressLookupEvent = useRef4(false);
  const { focusQuestion } = useQuestionRefs();
  const { questions } = useQuestionnaire();
  const handleFocus = (event) => {
    snowplow?.trigger("addressLookupFocused", {
      category: name,
      label: "Address lookup focused"
    });
    onFocus?.(event);
  };
  const handleOptionsDiplayed = () => {
    snowplow?.trigger("addressOptionsDisplayed", {
      category: name,
      label: "Address options displayed"
    });
  };
  const handleChange = (data) => {
    const transformedData = transformAddressData({
      questionName: name,
      addressData: data,
      mappedFields
    });
    onChange?.({
      target: { name, value: transformedData }
    });
  };
  const showManualFields = () => {
    snowplow?.trigger("manualAddressClicked", {
      category: sectionTitle || questionFieldProps.label || questionFieldProps.name
    });
    setAddressMode?.(name, "manual");
    const firstMappedField = mappedFields?.[0];
    if (firstMappedField?.associatedQuestionId) {
      setTimeout(() => {
        const questionToFocus = questions.filter.getById(
          firstMappedField.associatedQuestionId
        );
        if (questionToFocus?.name) {
          focusQuestion(questionToFocus.name);
        }
      }, 0);
    }
  };
  const handleAddressInput = (e) => {
    const { value: value2 } = e.target;
    if (value2.length === 1 && !sendAddressLookupEvent?.current) {
      snowplow?.trigger("addressLookupTypedIn", {
        category: e.target.name,
        label: "First character input"
      });
      sendAddressLookupEvent.current = true;
    }
  };
  const loquateService = new LoqateAddressLookupService({ apiKey, countries: ["GB"] });
  return /* @__PURE__ */ jsxs7("div", { className: "mobius-address-lookup__container", children: [
    /* @__PURE__ */ createElement(
      AddressLookup,
      {
        addressLookupService: loquateService,
        ...otherProps,
        onChange: handleAddressInput,
        value,
        key: value,
        placeholder: "Enter your postcode",
        onAddressSelected: handleChange,
        onOptionsDisplayed: handleOptionsDiplayed,
        onFocus: handleFocus,
        minSearchLength: 5
      }
    ),
    /* @__PURE__ */ jsx13(Button6, { size: "sm", variant: "link", onClick: showManualFields, children: "Enter address manually" })
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AnnualTurnoverQuestion/AnnualTurnoverQuestion.tsx
import { Option, Select, TextField, Text } from "@simplybusiness/mobius";
import { useEffect as useEffect5, useRef as useRef5 } from "react";

// src/components/Questionnaire/Section/Question/QuestionLabel.tsx
import { Label, TextOrHTML as TextOrHTML3 } from "@simplybusiness/mobius";
import { Fragment as Fragment4, jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
var QuestionLabel = ({
  primaryText,
  secondaryText,
  labelClasses,
  labelElementType,
  labelProps,
  secondaryTextId
}) => {
  const secondaryTextComponent = !!secondaryText && (typeof secondaryText === "string" ? /* @__PURE__ */ jsx14(
    TextOrHTML3,
    {
      id: secondaryTextId,
      className: "mobius-question__secondary-text",
      text: secondaryText,
      textWrapper: true
    }
  ) : secondaryText);
  return /* @__PURE__ */ jsxs8(Fragment4, { children: [
    /* @__PURE__ */ jsx14(
      Label,
      {
        className: labelClasses,
        elementType: labelElementType,
        ...labelProps,
        children: /* @__PURE__ */ jsx14(TextOrHTML3, { text: primaryText })
      }
    ),
    secondaryTextComponent
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AnnualTurnoverQuestion/utils.ts
var shouldShowCustomInput = (currentValue, lastOptionValue) => {
  return currentValue === lastOptionValue || currentValue !== "" && Number(currentValue) > Number(lastOptionValue);
};
var getValidationErrorMessage = (numValue, minValue, maxValue, currencySymbol) => {
  if (numValue <= minValue) {
    return `Please enter a value greater than ${currencySymbol}${minValue.toLocaleString()}`;
  }
  if (maxValue && numValue > maxValue) {
    return `Please enter a value less than ${currencySymbol}${maxValue.toLocaleString()}`;
  }
  return "Please enter a valid amount";
};
var getCustomDefaultValue = (currentValue, lastOptionValue) => {
  return currentValue && Number(currentValue) > Number(lastOptionValue) ? currentValue : "";
};
var getDisplayOptions = (placeholder, options) => {
  return placeholder ? [{ label: placeholder, value: "" }, ...options] : options;
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AnnualTurnoverQuestion/AnnualTurnoverQuestion.tsx
import { Fragment as Fragment5, jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var AnnualTurnoverQuestion = ({
  questionFieldProps,
  options,
  config
}) => {
  const {
    value,
    onChange,
    onBlur,
    placeholder,
    questionLabelProps,
    helpInfo,
    errorMessage,
    isInvalid,
    ...otherProps
  } = questionFieldProps;
  const { isUK } = useLocale();
  const currencySymbol = isUK ? "\xA3" : "$";
  const { setManualError, clearManualError } = useQuestionnaireActions();
  const customInputRef = useRef5(null);
  const lastOption = options?.[options.length - 1];
  const lastOptionValue = lastOption?.value ?? "";
  const currentValue = value ?? "";
  const showCustomInput = shouldShowCustomInput(currentValue, lastOptionValue);
  const { customMaximumValue, optionalPrimaryText } = config ?? {};
  const customDefaultValue = getCustomDefaultValue(
    currentValue,
    lastOptionValue
  );
  useEffect5(() => {
    return () => {
      clearManualError(questionFieldProps.name);
    };
  }, [questionFieldProps.name, clearManualError]);
  if (!options?.length) {
    console.warn("AnnualTurnoverQuestion: No required options found");
    return null;
  }
  const handleSelectChange = (event) => {
    const selectedValue = event.target.value;
    const isNotLastOption = selectedValue !== lastOptionValue;
    if (isNotLastOption) {
      clearManualError(questionFieldProps.name);
    }
    onChange?.({
      target: {
        name: questionFieldProps.name,
        value: selectedValue
      }
    });
  };
  const handleCustomInputBlur = (event) => {
    const inputValue = event.target.value;
    const isValidInput = customInputRef.current?.checkValidity();
    if (!isValidInput) {
      const numValue = Number(inputValue);
      const minValue = Number(lastOptionValue);
      const clientSetErrorMessage = getValidationErrorMessage(
        numValue,
        minValue,
        customMaximumValue,
        currencySymbol
      );
      const manualErrorMessage = getErrorMessage({
        questionErrorMessage: errorMessage,
        fallbackMessage: clientSetErrorMessage
      });
      setManualError(questionFieldProps.name, manualErrorMessage);
      return;
    }
    clearManualError(questionFieldProps.name);
    if (inputValue) {
      onBlur?.({
        target: {
          name: questionFieldProps.name,
          value: inputValue
        }
      });
    }
  };
  const customInputPrimaryText = optionalPrimaryText || `Please enter your annual turnover if it's ${lastOption?.label.toLowerCase() ?? ""}`;
  const displayOptions = getDisplayOptions(placeholder, options);
  return /* @__PURE__ */ jsxs9(Fragment5, { children: [
    /* @__PURE__ */ jsx15(
      Select,
      {
        ...otherProps,
        value: showCustomInput ? lastOptionValue : currentValue,
        onChange: handleSelectChange,
        errorMessage: showCustomInput ? void 0 : errorMessage,
        isInvalid: showCustomInput ? false : isInvalid,
        className: showCustomInput ? "mobius-question__annual-turnover-select" : void 0,
        children: displayOptions.map((option) => /* @__PURE__ */ jsx15(Option, { value: option.value, children: option.label }, option.value))
      }
    ),
    showCustomInput && /* @__PURE__ */ jsxs9(Fragment5, { children: [
      /* @__PURE__ */ jsx15(
        QuestionLabel,
        {
          ...questionLabelProps,
          primaryText: customInputPrimaryText,
          secondaryText: void 0
        }
      ),
      /* @__PURE__ */ jsx15(
        TextField,
        {
          ...otherProps,
          errorMessage,
          isInvalid,
          ref: customInputRef,
          type: "number",
          defaultValue: customDefaultValue,
          onBlur: handleCustomInputBlur,
          isRequired: true,
          prefixInside: /* @__PURE__ */ jsx15(Text, { children: currencySymbol }),
          min: Number(lastOptionValue),
          max: customMaximumValue
        }
      )
    ] })
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/DateInputQuestion.tsx
import { DateField } from "@simplybusiness/mobius";
import { jsx as jsx16 } from "react/jsx-runtime";
var DateInputQuestion = ({
  questionFieldProps
}) => {
  const {
    name,
    validation,
    onBlur,
    value,
    onChange,
    readOnly,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  let minDate;
  let maxDate;
  if (validation?.type === "range") {
    const { min, max } = validation.value;
    minDate = min;
    maxDate = max;
  }
  const handleBlur = (event) => {
    const { value: value2 } = event.target;
    onBlur?.({
      target: { name: questionFieldProps.name, value: value2 }
    });
  };
  return /* @__PURE__ */ jsx16(
    DateField,
    {
      ...otherProps,
      defaultValue: value,
      onBlur: handleBlur,
      min: minDate,
      max: maxDate,
      isReadOnly: !!readOnly
    }
  );
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/InfoQuestion/InfoQuestion.tsx
import { Alert, TextOrHTML as TextOrHTML4 } from "@simplybusiness/mobius";
import { jsx as jsx17 } from "react/jsx-runtime";
var InfoQuestion = ({
  questionFieldProps,
  config
}) => {
  const { questionLabelProps } = questionFieldProps;
  const { primaryText, secondaryText } = questionLabelProps;
  const { level } = config ?? {};
  if (typeof level === "string") {
    return /* @__PURE__ */ jsx17(Alert, { header: primaryText, variant: level, show: true, children: !!secondaryText && typeof secondaryText === "string" && /* @__PURE__ */ jsx17(TextOrHTML4, { text: secondaryText }) });
  }
  return /* @__PURE__ */ jsx17(QuestionLabel, { ...questionLabelProps });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/MultiSelectQuestion.tsx
import { Checkbox, CheckboxGroup, TextOrHTML as TextOrHTML5 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext4 } from "@simplybusiness/services";
import { jsx as jsx18 } from "react/jsx-runtime";
var makeLabel = (label) => {
  if (containsHtmlMarkup(label)) {
    return /* @__PURE__ */ jsx18(TextOrHTML5, { text: label });
  }
  return label;
};
var triggerShadowQuestionAnswerEvent = ({
  previousValue,
  newValues,
  snowplow,
  sectionTitle
}) => {
  const previousValues = new Set(previousValue);
  const newValuesSet = new Set(newValues);
  const added = newValues.filter((v) => !previousValues.has(v));
  const removed = previousValue.filter((v) => !newValuesSet.has(v));
  const changedItem = added[0] || removed[0];
  snowplow?.trigger("questionAnswered", {
    section: sectionTitle ?? "",
    question: snakeCase(changedItem),
    answer: added[0] ? "yes" : "no"
  });
};
var MultiSelectQuestion = ({
  options,
  config,
  sectionTitle,
  questionFieldProps
}) => {
  const { value, onChange, questionLabelProps, helpInfo, ...otherProps } = questionFieldProps;
  const lastItemDisables = !!config?.lastItemDisables;
  const { snowplow } = useSnowplowContext4();
  if (!options?.length) {
    console.warn(`No required options found`);
    return null;
  }
  const handleGroupChange = (values, shouldUpdate) => {
    if (isShadowQuestion(questionFieldProps.name)) {
      triggerShadowQuestionAnswerEvent({
        previousValue: value,
        newValues: values,
        snowplow,
        sectionTitle
      });
    }
    onChange({
      target: { name: questionFieldProps.name, value: values, shouldUpdate }
    });
  };
  const handleOptionChange = (event, optionValue) => {
    const isChecked = event.target.checked;
    const isLastOption = optionValue === options[options.length - 1].value;
    if (lastItemDisables && isChecked && isLastOption) {
      handleGroupChange([optionValue], true);
      return;
    }
    const currentValues = new Set(value);
    if (isChecked) {
      currentValues.add(optionValue);
    } else {
      currentValues.delete(optionValue);
    }
    handleGroupChange(Array.from(currentValues), true);
  };
  return /* @__PURE__ */ jsx18(
    CheckboxGroup,
    {
      ...otherProps,
      lastItemDisables,
      defaultValue: value,
      onChange: handleGroupChange,
      children: options.map((option) => /* @__PURE__ */ jsx18(
        Checkbox,
        {
          label: makeLabel(option.label),
          value: option.value,
          onChange: option.shouldUpdate ? (e) => handleOptionChange(e, option.value) : void 0
        },
        option.value
      ))
    }
  );
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/NumberQuestion.tsx
import { NumberField, Text as Text2 } from "@simplybusiness/mobius";
import { jsx as jsx19 } from "react/jsx-runtime";
var getRangeProps = (value) => {
  return {
    minValue: value?.min || void 0,
    maxValue: value?.max || void 0
  };
};
var NumberQuestion = ({
  questionFieldProps,
  config
}) => {
  const {
    value,
    validation,
    readOnly,
    onChange,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { wholeNumber = false, prefix } = config ?? {};
  const rangeProps = getRangeProps(validation?.value);
  if (Array.isArray(value)) {
    throw new TypeError("NumberQuestion: defaultValue must be a number");
  }
  const parsedDefaultValue = value == null || value === "" ? void 0 : Number(value);
  const realPrefix = prefix != null ? /* @__PURE__ */ jsx19(Text2, { children: prefix }) : void 0;
  return /* @__PURE__ */ jsx19(
    NumberField,
    {
      ...otherProps,
      step: wholeNumber ? 1 : 0.01,
      defaultValue: parsedDefaultValue,
      prefixInside: realPrefix,
      ...rangeProps,
      isReadOnly: !!readOnly
    }
  );
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/PercentageQuestion/PercentageQuestion.tsx
import { ErrorMessage, Flex as Flex4, Grid, NumberField as NumberField2 } from "@simplybusiness/mobius";
import {
  useCallback as useCallback9,
  useRef as useRef6,
  useState as useState4,
  Fragment as Fragment6,
  useMemo as useMemo6,
  useId
} from "react";

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/PercentageQuestion/percentageQuestionUtils.ts
var computeTotal = (percentages) => Object.values(percentages).reduce((acc, curr) => acc + curr, 0);
var clampForLabel = (percentages, label, maxOverall = 100) => {
  const total = computeTotal(percentages);
  const current = percentages[label] || 0;
  return maxOverall - total + current;
};
var buildInitialPercentages = (normalizedLabels, answers, questionName) => {
  const result = {};
  normalizedLabels.forEach(({ key }) => {
    const answerKey = `${questionName}[${key}]`;
    const raw = answers?.[answerKey];
    const num = Number(raw);
    result[key] = Number.isFinite(num) && num >= 0 ? num : 0;
  });
  return result;
};
var buildInitialGroupPercentages = (normalizedLabels, answers) => {
  const result = {};
  normalizedLabels.forEach(({ key }) => {
    const rawValue = answers?.[key];
    const isPrimitive = typeof rawValue === "string" || typeof rawValue === "number";
    if (!isPrimitive) {
      result[key] = 0;
      return;
    }
    const stringValue = String(rawValue);
    const numericValue = Number(stringValue.replace(/%/g, ""));
    result[key] = Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
  });
  return result;
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/PercentageQuestion/PercentageQuestion.tsx
import { jsx as jsx20, jsxs as jsxs10 } from "react/jsx-runtime";
var PercentageQuestion = ({
  questionFieldProps,
  config
}) => {
  const answers = useAnswers();
  const {
    value,
    validation,
    onChange,
    // we deliberately extract onBlur so it isn't passed to each field
    onBlur,
    questionLabelProps,
    helpInfo,
    errorMessage,
    name,
    id,
    ...otherProps
  } = questionFieldProps;
  const { labels } = config || {};
  const normalizedLabels = useMemo6(
    () => labels?.map((label) => ({
      display: label,
      // Original case for UI display
      key: label.toLowerCase()
      // Lowercase for data storage
    })) ?? [],
    [labels]
  );
  const [percentages, setPercentages] = useState4(
    () => buildInitialPercentages(normalizedLabels, answers, name)
  );
  const total = computeTotal(percentages);
  const errorMessageId = useId();
  const containerRef = useRef6(null);
  const handleContainerBlur = useCallback9(
    (e) => {
      const nextFocus = e.relatedTarget;
      if (containerRef.current && nextFocus && containerRef.current.contains(nextFocus)) {
        return;
      }
      if (onBlur) {
        const parentValue = Object.values(percentages).join(",");
        const individualAnswers = Object.fromEntries(
          Object.entries(percentages).map(([label, value2]) => [
            `${questionFieldProps.name}[${label}]`,
            String(value2)
          ])
        );
        onBlur({
          target: {
            name: questionFieldProps.name,
            value: {
              [questionFieldProps.name]: parentValue,
              ...individualAnswers
            }
          }
        });
      }
    },
    [onBlur, percentages, questionFieldProps.name]
  );
  return /* @__PURE__ */ jsx20("div", { onBlur: handleContainerBlur, ref: containerRef, tabIndex: -1, children: /* @__PURE__ */ jsxs10(Flex4, { flexDirection: "column", gap: "sm", children: [
    /* @__PURE__ */ jsxs10(Grid, { columns: 2, alignItems: "center", rowGap: "sm", children: [
      normalizedLabels.map(({ display, key }) => {
        const handleChange = (e) => {
          const newPercentages = { ...percentages };
          newPercentages[key] = Number(e.target.value);
          setPercentages(newPercentages);
        };
        return /* @__PURE__ */ jsxs10(Fragment6, { children: [
          /* @__PURE__ */ jsx20("label", { id: `${id}-${display}-label`, children: display }),
          /* @__PURE__ */ jsx20(
            NumberField2,
            {
              ...otherProps,
              step: 1,
              id: `${id}-${display}`,
              "aria-labelledby": `${id}-${display}-label`,
              "aria-errormessage": errorMessage ? errorMessageId : void 0,
              "aria-invalid": !!errorMessage,
              onChange: handleChange,
              value: String(percentages[key] || 0),
              minValue: 0,
              maxValue: clampForLabel(percentages, key, 100),
              suffixInside: /* @__PURE__ */ jsx20("span", { children: "%" })
            }
          )
        ] }, `${id}-${display}`);
      }),
      /* @__PURE__ */ jsx20("label", { children: "Total" }),
      /* @__PURE__ */ jsxs10("span", { className: "mobius-percentage-question__total", children: [
        total,
        "%"
      ] })
    ] }),
    !!errorMessage && /* @__PURE__ */ jsx20(ErrorMessage, { id: errorMessageId, errorMessage })
  ] }) });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/PercentageQuestion/PercentageGroupQuestion.tsx
import { ErrorMessage as ErrorMessage2, Flex as Flex5, Grid as Grid2, NumberField as NumberField3 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext5 } from "@simplybusiness/services";
import {
  useCallback as useCallback10,
  useRef as useRef7,
  useState as useState5,
  Fragment as Fragment7,
  useMemo as useMemo7,
  useId as useId2
} from "react";
import { jsx as jsx21, jsxs as jsxs11 } from "react/jsx-runtime";
var PercentageGroupQuestion = ({
  questionFieldProps,
  config,
  sectionTitle
}) => {
  const answers = useAnswers();
  const { groups } = useQuestionnaire();
  const { snowplow } = useSnowplowContext5();
  const {
    value,
    validation,
    onChange,
    // we deliberately extract onBlur so it isn't passed to each field
    onBlur,
    questionLabelProps,
    helpInfo,
    errorMessage,
    name,
    id,
    ...otherProps
  } = questionFieldProps;
  const { labels, step: configStep } = config || {};
  const step = useMemo7(() => {
    const parsedStep = Number(configStep);
    return Number.isFinite(parsedStep) && parsedStep > 0 ? parsedStep : 1;
  }, [configStep]);
  const group = groups[name];
  const normalizedLabels = useMemo7(() => {
    if (!Array.isArray(labels) || !group?.names || group.kind !== "percent") {
      return [];
    }
    const normalized = labels.map((label, index) => ({
      display: label,
      key: group.names[index]
    }));
    if (normalized.length < 2) {
      console.warn(
        `PercentageGroupQuestion: Expected at least 2 labels, got ${normalized.length}`
      );
    }
    return normalized;
  }, [labels, group]);
  const regularQuestionNormalizedLabels = normalizedLabels.slice(0, -1);
  const totalQuestionNormalizedLabel = normalizedLabels[normalizedLabels.length - 1];
  const [percentages, setPercentages] = useState5(
    () => buildInitialGroupPercentages(regularQuestionNormalizedLabels, answers)
  );
  const total = computeTotal(percentages);
  const errorMessageId = useId2();
  const containerRef = useRef7(null);
  const handlers = useMemo7(() => {
    return regularQuestionNormalizedLabels.reduce(
      (acc, { key }) => {
        acc[key] = (e) => {
          const newValue = Number(e.target.value);
          setPercentages((prev) => ({
            ...prev,
            [key]: newValue
          }));
          snowplow?.trigger("questionAnswered", {
            section: sectionTitle ?? "",
            question: snakeCase(key),
            answer: `${newValue}%`
          });
        };
        return acc;
      },
      {}
    );
  }, [regularQuestionNormalizedLabels, snowplow, sectionTitle]);
  const handleContainerBlur = useCallback10(
    (e) => {
      const nextFocus = e.relatedTarget;
      if (nextFocus && containerRef.current?.contains(nextFocus)) {
        return;
      }
      if (onBlur) {
        const answersByOriginalName = {};
        regularQuestionNormalizedLabels.forEach(({ key }) => {
          answersByOriginalName[key] = `${percentages[key] || 0}%`;
        });
        answersByOriginalName[totalQuestionNormalizedLabel.key] = `${total}%`;
        onBlur({
          target: {
            name: questionFieldProps.name,
            value: answersByOriginalName
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onBlur, percentages, questionFieldProps.name, normalizedLabels]
  );
  return /* @__PURE__ */ jsx21("div", { onBlur: handleContainerBlur, ref: containerRef, tabIndex: -1, children: /* @__PURE__ */ jsxs11(Flex5, { flexDirection: "column", gap: "sm", children: [
    /* @__PURE__ */ jsxs11(Grid2, { columns: 2, alignItems: "center", rowGap: "sm", children: [
      regularQuestionNormalizedLabels.map(({ display, key }) => /* @__PURE__ */ jsxs11(Fragment7, { children: [
        /* @__PURE__ */ jsx21("label", { id: `${id}-${display}-label`, children: display }),
        /* @__PURE__ */ jsx21(
          NumberField3,
          {
            ...otherProps,
            step,
            id: `${id}-${display}`,
            "aria-labelledby": `${id}-${display}-label`,
            "aria-errormessage": errorMessage ? errorMessageId : void 0,
            "aria-invalid": !!errorMessage,
            onChange: handlers[key],
            value: String(percentages[key] || 0),
            minValue: 0,
            maxValue: clampForLabel(percentages, key, 100),
            suffixInside: /* @__PURE__ */ jsx21("span", { children: "%" })
          }
        )
      ] }, `${id}-${display}`)),
      /* @__PURE__ */ jsx21("label", { children: totalQuestionNormalizedLabel.display }),
      /* @__PURE__ */ jsxs11("span", { className: "mobius-percentage-question__total", children: [
        total,
        "%"
      ] })
    ] }),
    !!errorMessage && /* @__PURE__ */ jsx21(ErrorMessage2, { id: errorMessageId, errorMessage })
  ] }) });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/RadioButtonsQuestion/RadioButtonsQuestion.tsx
import { Icon as Icon6, Radio, RadioGroup } from "@simplybusiness/mobius";
import { useState as useState7 } from "react";

// src/hooks/useIsClient/useIsClient.tsx
import { useEffect as useEffect6, useState as useState6 } from "react";
function useIsClient() {
  const [isClient, setIsClient] = useState6(false);
  useEffect6(() => {
    setIsClient(true);
  }, []);
  return isClient;
}

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/RadioButtonsQuestion/orientation.ts
var isYesNoOptions = (options) => {
  if (options.length !== 2) return false;
  const values = options.map((option) => option.value.toLowerCase()).sort();
  return values[0] === "no" && values[1] === "yes";
};
var getOrientation = ({ site, options }) => {
  let orientation = "vertical";
  if (site !== "comparethemarket" && isYesNoOptions(options)) {
    orientation = "horizontal";
  }
  return orientation;
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/RadioButtonsQuestion/RadioButtonsQuestion.tsx
import { jsx as jsx22, jsxs as jsxs12 } from "react/jsx-runtime";
var RadioButtonsQuestion = ({
  options,
  questionFieldProps,
  setAddressMode
}) => {
  const {
    value,
    isDisabled = false,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const [errorMessage, _] = useState7(void 0);
  const isClient = useIsClient();
  const { site } = useEnvironmentValues();
  if (!options?.length) {
    console.warn(`No required options found`);
    return null;
  }
  const handleChange = (event) => {
    const { value: value2, name } = event.target;
    if (name === LINK_ADDRESS_QUESTION_NAME) {
      setAddressMode?.(
        LINKED_ADDRESS_QUESTION_NAME,
        value2 === "yes" ? "none" : "lookup"
      );
    }
    questionFieldProps.onChange?.(event);
  };
  const orientation = getOrientation({ site, options });
  return /* @__PURE__ */ jsx22(
    RadioGroup,
    {
      ...otherProps,
      isDisabled: !isClient || isDisabled,
      value,
      onChange: handleChange,
      errorMessage: errorMessage || questionFieldProps.errorMessage,
      className: "mobius-radio-buttons-question",
      orientation,
      children: options.map(({ image, label, value: value2 }) => {
        if (!image || !isValidIconKey(image)) {
          if (image && !isValidIconKey(image)) {
            console.warn(
              `Invalid icon name provided to the radio button labelled "${label}"`
            );
          }
          return /* @__PURE__ */ jsx22(Radio, { label, value: value2 }, value2);
        }
        return /* @__PURE__ */ jsxs12(Radio, { value: value2, children: [
          /* @__PURE__ */ jsx22(
            Icon6,
            {
              icon: iconMap[image],
              size: "sm",
              title: `Business location type: ${label}`
            }
          ),
          label
        ] }, value2);
      })
    }
  );
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/RadioButtonsQuestion/iconMap.ts
import {
  briefcase,
  buildingCircleCheck,
  carBuilding,
  houseChimney,
  user,
  users
} from "@simplybusiness/icons";
var isValidIconKey = (key) => {
  return key in iconMap;
};
var iconMap = {
  "home-only": houseChimney,
  offsite: carBuilding,
  "own-rent-space": buildingCircleCheck,
  "sole-trader": user,
  partnership: users,
  "limited-company": briefcase
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/SelectQuestion.tsx
import { Option as Option2, Select as Select2 } from "@simplybusiness/mobius";
import { jsx as jsx23 } from "react/jsx-runtime";
var SelectQuestion = ({
  options,
  questionFieldProps
}) => {
  const { placeholder, value, questionLabelProps, helpInfo, ...otherProps } = questionFieldProps;
  if (!options?.length) {
    console.warn(`No required options found`);
    return null;
  }
  const displayOptions = placeholder ? [{ label: placeholder, value: "" }, ...options] : options;
  return /* @__PURE__ */ jsx23(Select2, { ...otherProps, value, children: displayOptions.map((option) => /* @__PURE__ */ jsx23(Option2, { value: option.value, children: option.label }, option.value)) });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TelephoneQuestion/TelephoneQuestion.tsx
import { MaskedField } from "@simplybusiness/mobius";

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TextQuestion/TextQuestion.tsx
import { Button as Button7, TextField as TextField2 } from "@simplybusiness/mobius";
import { Fragment as Fragment8, jsx as jsx24, jsxs as jsxs13 } from "react/jsx-runtime";
var TEXT_FIELD_TYPES = [
  "text",
  "email",
  "tel",
  "number",
  "postcode"
];
var DEFAULT_TYPE = "text";
var isValidFieldType = (type) => TEXT_FIELD_TYPES.includes(type);
var TextQuestion = ({
  questionFieldProps,
  type = DEFAULT_TYPE,
  addressModes,
  setAddressMode,
  associatedQuestionName
}) => {
  const {
    value,
    onChange,
    readOnly,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { id, name } = questionFieldProps;
  const inputType = isValidFieldType(type) ? type : DEFAULT_TYPE;
  const { focusQuestion, registerButtonRef, getButtonRef } = useQuestionRefs();
  const handleButtonRef = (buttonElement) => {
    if (buttonElement && QUESTIONS_WITH_TOGGLE.includes(id)) {
      registerButtonRef(
        `address-lookup-${associatedQuestionName}`,
        buttonElement
      );
    }
  };
  const addressMode = associatedQuestionName ? addressModes?.[associatedQuestionName] : void 0;
  const isAddressQuestion = !!addressMode;
  if (isAddressQuestion && addressMode === "lookup") {
    return null;
  }
  const handleChange = (event) => {
    const { value: value2 } = event.target;
    onChange?.({
      target: { name, value: { [id]: value2, [name]: value2 } }
    });
  };
  const handleBlur = (event) => {
    const relatedTarget = event.relatedTarget;
    const addressLookupButton = getButtonRef(
      `address-lookup-${associatedQuestionName}`
    );
    const wasAddressLookupButtonClicked = relatedTarget && addressLookupButton && relatedTarget === addressLookupButton;
    if (wasAddressLookupButtonClicked) {
      return;
    }
    questionFieldProps.onBlur?.(event);
  };
  return /* @__PURE__ */ jsxs13(Fragment8, { children: [
    /* @__PURE__ */ jsx24(
      TextField2,
      {
        ...otherProps,
        type: inputType,
        value: value ?? "",
        onChange: handleChange,
        onBlur: handleBlur,
        isReadOnly: !!readOnly
      }
    ),
    QUESTIONS_WITH_TOGGLE.includes(id) && /* @__PURE__ */ jsx24(
      Button7,
      {
        className: "mobius-text-question__button-link",
        size: "sm",
        variant: "link",
        ref: handleButtonRef,
        onClick: () => {
          setAddressMode?.(associatedQuestionName, "lookup");
          setTimeout(() => {
            focusQuestion(associatedQuestionName);
          }, 0);
        },
        children: "Use address lookup"
      }
    )
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TelephoneQuestion/TelephoneQuestion.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var TelephoneQuestionUS = (props) => {
  const { questionFieldProps } = props;
  const {
    value,
    validation,
    readOnly,
    onChange,
    onBlur,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  return /* @__PURE__ */ jsx25(
    MaskedField,
    {
      ...otherProps,
      onChange,
      onBlur,
      mask: { mask: "(000) 000-0000" },
      isReadOnly: !!readOnly,
      defaultValue: value?.toString() || "",
      useMaskedValue: true
    }
  );
};
var TelephoneQuestion = (props) => {
  const { isUK } = useLocale();
  return isUK ? /* @__PURE__ */ jsx25(TextQuestion, { ...props, type: "tel" }) : /* @__PURE__ */ jsx25(TelephoneQuestionUS, { ...props });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/TradeQuestion.tsx
import { Link } from "@simplybusiness/mobius";
import { TradeSelector } from "@simplybusiness/mobius-trade-selector";
import { useSnowplowContext as useSnowplowContext6 } from "@simplybusiness/services";
import { useEffect as useEffect7, useState as useState8 } from "react";

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/redirect.ts
var shouldRedirect = (data) => {
  const { currentVertical, nextVertical, allowTradeBasedRedirect } = data;
  if (allowTradeBasedRedirect && !!currentVertical && currentVertical !== nextVertical) {
    return true;
  }
  return false;
};
var getRedirectUrl = (data) => {
  const { nextVertical, affclie, journeyId, trade } = data;
  const params = new URLSearchParams({
    vertical: nextVertical,
    trade,
    journey_id: journeyId,
    ...affclie ? { AFFCLIE: affclie } : {}
  });
  return `/route_trade?${params.toString()}`;
};
var redirect = (data) => {
  if (!shouldRedirect(data)) return;
  const url = getRedirectUrl(data);
  window.location.assign(url);
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/TradeQuestionSelected/TradeQuestionSelectedUK.tsx
import { Button as Button8, Icon as Icon7, Text as Text3 } from "@simplybusiness/mobius";
import { penToSquare } from "@simplybusiness/icons";
import { jsx as jsx26, jsxs as jsxs14 } from "react/jsx-runtime";
var TradeQuestionSelectedUK = ({
  trade,
  onEditTrade
}) => /* @__PURE__ */ jsx26(
  "div",
  {
    className: "mobius-trade-question--selected",
    "data-testid": "trade-selected-uk",
    children: /* @__PURE__ */ jsxs14("div", { className: "mobius-trade-question__grid-container", children: [
      /* @__PURE__ */ jsxs14("div", { className: "mobius-trade-question__content", children: [
        /* @__PURE__ */ jsx26(
          Text3,
          {
            elementType: "p",
            className: "mobius-trade-question__selected-text",
            "data-testid": "trade-selected-text",
            children: /* @__PURE__ */ jsx26("strong", { children: "Trade/profession" })
          }
        ),
        /* @__PURE__ */ jsx26(Text3, { elementType: "p", className: "mobius-trade-question__selected-text", children: trade.value })
      ] }),
      /* @__PURE__ */ jsxs14(
        Button8,
        {
          className: "mobius-question__help-link mobius-trade-question__edit-btn",
          onClick: onEditTrade,
          variant: "link",
          size: "sm",
          type: "button",
          "aria-label": "Edit selected trade or profession",
          children: [
            /* @__PURE__ */ jsx26("span", { children: "Edit" }),
            /* @__PURE__ */ jsx26(Icon7, { fixedWidth: true, icon: penToSquare, size: "sm" })
          ]
        }
      )
    ] })
  }
);

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/TradeQuestionSelected/TradeQuestionSelected.tsx
import { Button as Button9, Icon as Icon8, Text as Text4 } from "@simplybusiness/mobius";
import { penToSquare as penToSquare2 } from "@simplybusiness/icons";
import { jsx as jsx27, jsxs as jsxs15 } from "react/jsx-runtime";
var TradeQuestionSelected = ({
  trade,
  onEditTrade
}) => /* @__PURE__ */ jsx27("div", { className: "mobius-trade-question--selected", children: /* @__PURE__ */ jsxs15("div", { className: "mobius-trade-question__grid-container", children: [
  /* @__PURE__ */ jsxs15("div", { className: "mobius-trade-question__content", children: [
    /* @__PURE__ */ jsxs15(
      Text4,
      {
        elementType: "p",
        className: "mobius-trade-question__selected-text",
        "data-testid": "trade-selected-text",
        children: [
          "You have selected \u2018",
          /* @__PURE__ */ jsx27("strong", { children: trade.value }),
          "\u2019 as your trade/profession."
        ]
      }
    ),
    trade?.definition && /* @__PURE__ */ jsx27(
      HelpLink3,
      {
        headline: "How do we define this type of business?",
        label: "How do we define this type of business?",
        text: trade.definition
      }
    )
  ] }),
  /* @__PURE__ */ jsxs15(
    Button9,
    {
      className: "mobius-question__help-link mobius-trade-question__edit-btn",
      onClick: onEditTrade,
      variant: "link",
      size: "sm",
      type: "button",
      "aria-label": "Edit selected trade or profession",
      children: [
        /* @__PURE__ */ jsx27("span", { children: "Edit" }),
        /* @__PURE__ */ jsx27(Icon8, { fixedWidth: true, icon: penToSquare2, size: "sm" })
      ]
    }
  )
] }) });

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/TradeQuestionSelected/index.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
var TradeQuestionSelected2 = (props) => {
  const { isUK } = useLocale();
  if (isUK) {
    return /* @__PURE__ */ jsx28(TradeQuestionSelectedUK, { ...props });
  }
  return /* @__PURE__ */ jsx28(TradeQuestionSelected, { ...props });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/utils.ts
var EMPTY_TRADE_OPTION = Object.freeze({
  label: "",
  value: "",
  definition: "",
  vertical: ""
});
var getTradeOptionFromAnswer = (trade, definition) => ({
  ...EMPTY_TRADE_OPTION,
  label: trade,
  value: trade,
  definition: definition ?? ""
});
var convertTradesToOptions = (trades) => {
  return trades.map(({ value, vertical, definition }) => ({
    label: value,
    value,
    vertical,
    definition
  }));
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/TradeQuestion.tsx
import { Fragment as Fragment9, jsx as jsx29, jsxs as jsxs16 } from "react/jsx-runtime";
var TradeQuestion = ({
  questionFieldProps,
  config,
  type
}) => {
  const {
    onChange,
    name,
    label,
    errorMessage,
    questionLabelProps,
    helpInfo,
    ...otherQuestionProps
  } = questionFieldProps;
  const { occupationsPageUrl, ...tradeSelectorConfig } = config || {};
  const answerValue = useAnswer(name);
  const answeredTrade = answerValue?.toString() ?? "";
  const { site, vertical, affclie, journeyId } = useEnvironmentValues();
  const { snowplow } = useSnowplowContext6();
  const [selectedTrade, setSelectedTrade] = useState8(
    getTradeOptionFromAnswer(answeredTrade)
  );
  useEffect7(() => {
    if (answeredTrade && answeredTrade !== selectedTrade.value) {
      setSelectedTrade(getTradeOptionFromAnswer(answeredTrade));
    }
  }, [answeredTrade]);
  const variant = type === "trade_search_ai" ? "ai" : "default";
  const handleSelectTrade = (trade) => {
    onChange?.({ target: { name, value: trade.value } });
    setSelectedTrade(trade);
    redirect({
      currentVertical: vertical,
      nextVertical: trade.vertical,
      affclie,
      journeyId,
      trade: trade.value,
      allowTradeBasedRedirect: Boolean(
        tradeSelectorConfig.allowTradeBasedRedirect
      )
    });
  };
  const handleTradeChangeClick = () => {
    snowplow?.trigger("tradeChanged", {
      label: questionFieldProps.id,
      property: answeredTrade
    });
    setSelectedTrade(EMPTY_TRADE_OPTION);
  };
  return /* @__PURE__ */ jsx29("div", { className: "mobius-trade-question", children: selectedTrade.value ? /* @__PURE__ */ jsx29(
    TradeQuestionSelected2,
    {
      trade: selectedTrade,
      onEditTrade: handleTradeChangeClick
    }
  ) : /* @__PURE__ */ jsxs16(Fragment9, { children: [
    /* @__PURE__ */ jsx29(QuestionLabel, { ...questionLabelProps }),
    helpInfo && /* @__PURE__ */ jsx29(HelpLink3, { ...helpInfo }),
    /* @__PURE__ */ jsx29(
      TradeSelector,
      {
        defaultValue: answeredTrade,
        onSelectTrade: handleSelectTrade,
        variant,
        ...tradeSelectorConfig,
        site,
        vertical,
        ...otherQuestionProps
      }
    ),
    typeof occupationsPageUrl === "string" && /* @__PURE__ */ jsxs16("div", { className: "mobius-trade-question__note", children: [
      "Or choose from the",
      " ",
      /* @__PURE__ */ jsx29(Link, { href: occupationsPageUrl, children: "full list of occupations" })
    ] })
  ] }) });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/AiTradeQuestion/AiTradeQuestion.tsx
import { loading as loading2, rightArrow as rightArrow4, search } from "@simplybusiness/icons";
import { Button as Button11, Icon as Icon10, Link as Link2, TextField as TextField3 } from "@simplybusiness/mobius";
import {
  useFetchTrades
} from "@simplybusiness/mobius-trade-selector";
import { useSnowplowContext as useSnowplowContext7 } from "@simplybusiness/services";
import { useEffect as useEffect8, useId as useId3, useRef as useRef8, useState as useState9 } from "react";

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/AiTradeQuestion/AiTradeQuestionSelected.tsx
import { Button as Button10, Icon as Icon9, Text as Text5 } from "@simplybusiness/mobius";
import { penToSquare as penToSquare3 } from "@simplybusiness/icons";
import { jsx as jsx30, jsxs as jsxs17 } from "react/jsx-runtime";
var AiTradeQuestionSelected = ({
  trade,
  onEditTrade
}) => /* @__PURE__ */ jsx30("div", { className: "mobius-trade-question--selected", children: /* @__PURE__ */ jsxs17("div", { className: "mobius-trade-question__grid-container", children: [
  /* @__PURE__ */ jsxs17("div", { className: "mobius-trade-question__content", children: [
    /* @__PURE__ */ jsx30(
      Text5,
      {
        elementType: "p",
        className: "mobius-trade-question__selected-text",
        "data-testid": "trade-selected-text",
        children: /* @__PURE__ */ jsx30("strong", { children: "Business Type" })
      }
    ),
    /* @__PURE__ */ jsx30(Text5, { elementType: "p", className: "mobius-trade-question__selected-text", children: trade.value }),
    trade?.definition && /* @__PURE__ */ jsx30(
      HelpLink3,
      {
        headline: "How do we define this type of business?",
        label: "How do we define this type of business?",
        text: trade.definition
      }
    )
  ] }),
  /* @__PURE__ */ jsxs17(
    Button10,
    {
      className: "mobius-question__help-link mobius-trade-question__edit-btn",
      onClick: onEditTrade,
      variant: "link",
      size: "sm",
      type: "button",
      "aria-label": "Edit selected trade or profession",
      children: [
        /* @__PURE__ */ jsx30("span", { children: "Edit" }),
        /* @__PURE__ */ jsx30(Icon9, { fixedWidth: true, icon: penToSquare3, size: "sm" })
      ]
    }
  )
] }) });

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/AiTradeQuestion/AiTradeQuestion.tsx
import { Fragment as Fragment10, jsx as jsx31, jsxs as jsxs18 } from "react/jsx-runtime";
var AiTradeQuestion = ({
  questionFieldProps,
  config
}) => {
  const { onChange, name, questionLabelProps, helpInfo, errorMessage, value } = questionFieldProps;
  const { occupationsPageUrl, tradeDefinition, ...tradeSelectorConfig } = config || {};
  const typedTradeSelectorConfig = tradeSelectorConfig;
  const answeredTrade = value?.toString() ?? "";
  const { site, vertical, affclie, journeyId } = useEnvironmentValues();
  const { snowplow } = useSnowplowContext7();
  const labelId = useId3();
  const { tradesEndpoint, searchScope, primaryDetail } = typedTradeSelectorConfig;
  const [searchQuery, setSearchQuery] = useState9("");
  const [selectedTrade, setSelectedTrade] = useState9(
    getTradeOptionFromAnswer(
      answeredTrade,
      tradeDefinition
    )
  );
  useEffect8(() => {
    if (answeredTrade && answeredTrade !== selectedTrade.value) {
      setSelectedTrade(
        getTradeOptionFromAnswer(
          answeredTrade,
          tradeDefinition
        )
      );
    }
  }, [answeredTrade]);
  const constructedEndpointRef = useRef8(tradesEndpoint);
  useEffect8(() => {
    if (typeof window !== "undefined" && window.location?.origin) {
      constructedEndpointRef.current = `${window.location.origin}/trade_search`;
    }
  }, []);
  const tradesMutation = useFetchTrades({
    variant: "ai",
    site,
    vertical,
    tradesEndpoint: constructedEndpointRef.current,
    searchScope,
    primaryDetail,
    transformResults: convertTradesToOptions
  });
  const handleFindBusiness = () => {
    const trimmedSearchQuery = searchQuery.trim();
    if (trimmedSearchQuery) {
      tradesMutation.mutate(trimmedSearchQuery);
    }
  };
  const handleTradeSelect = (trade) => {
    setSelectedTrade(trade);
    onChange?.({ target: { name, value: trade.value } });
    redirect({
      currentVertical: vertical,
      nextVertical: trade.vertical,
      affclie,
      journeyId,
      trade: trade.value,
      allowTradeBasedRedirect: Boolean(
        tradeSelectorConfig.allowTradeBasedRedirect
      )
    });
  };
  const handleTradeChangeClick = () => {
    snowplow?.trigger("tradeChanged", {
      label: questionFieldProps.id,
      property: answeredTrade
    });
    setSelectedTrade(EMPTY_TRADE_OPTION);
  };
  return /* @__PURE__ */ jsxs18("div", { className: "mobius-ai-trade-question", children: [
    !selectedTrade.value ? /* @__PURE__ */ jsx31(QuestionLabel, { ...questionLabelProps, labelProps: { id: labelId } }) : null,
    !selectedTrade.value ? helpInfo && /* @__PURE__ */ jsx31(HelpLink3, { ...helpInfo }) : null,
    selectedTrade.value ? /* @__PURE__ */ jsx31(
      AiTradeQuestionSelected,
      {
        trade: selectedTrade,
        onEditTrade: handleTradeChangeClick
      }
    ) : /* @__PURE__ */ jsxs18(Fragment10, { children: [
      /* @__PURE__ */ jsx31(
        TextField3,
        {
          prefixInside: /* @__PURE__ */ jsx31(Icon10, { icon: search }),
          placeholder: "Describe your business in a few words",
          value: searchQuery,
          onChange: (e) => setSearchQuery(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              handleFindBusiness();
            }
          },
          errorMessage,
          "aria-labelledby": labelId
        }
      ),
      /* @__PURE__ */ jsx31("div", { className: "mobius-ai-trade-question__button", children: /* @__PURE__ */ jsxs18(
        Button11,
        {
          onClick: handleFindBusiness,
          isDisabled: !searchQuery.trim() || tradesMutation.isPending,
          children: [
            tradesMutation.isPending ? "Searching for trades..." : "Find My Business",
            tradesMutation.isPending ? /* @__PURE__ */ jsx31(Icon10, { icon: loading2, spin: true }) : /* @__PURE__ */ jsx31(Icon10, { icon: rightArrow4 })
          ]
        }
      ) }),
      /* @__PURE__ */ jsx31("div", { className: "mobius-ai-trade-question__results", children: /* @__PURE__ */ jsx31(
        TradesResults,
        {
          mutation: tradesMutation,
          onTradeSelect: handleTradeSelect
        }
      ) }),
      typeof occupationsPageUrl === "string" && /* @__PURE__ */ jsxs18("div", { className: "mobius-trade-question__note", children: [
        "Or choose from the",
        " ",
        /* @__PURE__ */ jsx31(Link2, { href: occupationsPageUrl, children: "full list of occupations" })
      ] })
    ] })
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/AiTradeQuestion/LoadingState.tsx
import { Text as Text6 } from "@simplybusiness/mobius";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { jsx as jsx32, jsxs as jsxs19 } from "react/jsx-runtime";
var LoadingState = () => /* @__PURE__ */ jsx32(
  SkeletonTheme,
  {
    baseColor: "var(--color-background-light)",
    highlightColor: "var(--color-background)",
    children: /* @__PURE__ */ jsxs19("div", { children: [
      /* @__PURE__ */ jsx32(Text6, { variant: "h4", children: "Select the business that most closely describes what you do" }),
      Array.from({ length: 3 }).map((_, index) => /* @__PURE__ */ jsx32(
        "div",
        {
          className: "mobius-ai-trade-question__trade-item mobius-ai-trade-question__trade-item--is-loading",
          children: /* @__PURE__ */ jsx32(
            Skeleton,
            {
              borderRadius: "var(--radius-1)",
              containerClassName: "mobius-skeleton"
            }
          )
        },
        index
      ))
    ] })
  }
);

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/AiTradeQuestion/TradesListState.tsx
import { Text as Text7, ExpandableText } from "@simplybusiness/mobius";
import { jsx as jsx33, jsxs as jsxs20 } from "react/jsx-runtime";
var TradesListState = ({
  trades,
  onTradeSelect
}) => {
  return /* @__PURE__ */ jsxs20("div", { children: [
    /* @__PURE__ */ jsx33(Text7, { variant: "h4", children: "Select the business that most closely describes what you do" }),
    trades.map((trade) => /* @__PURE__ */ jsxs20(
      "div",
      {
        onClick: () => onTradeSelect(trade),
        className: "mobius-ai-trade-question__trade-item",
        children: [
          /* @__PURE__ */ jsx33(Text7, { className: "mobius-ai-trade-question__trade-title", children: trade.value }),
          trade.definition && /* @__PURE__ */ jsx33(
            ExpandableText,
            {
              text: trade.definition,
              maxLines: 3,
              breakpoint: "sm",
              showText: "Read more",
              hideText: "Read less",
              className: "mobius-ai-trade-question__trade-definition",
              textProps: { elementType: "span" }
            }
          )
        ]
      },
      trade.value
    ))
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/AiTradeQuestion/TradesResults.tsx
import { Text as Text8 } from "@simplybusiness/mobius";
import { jsx as jsx34 } from "react/jsx-runtime";
var TradesResults = ({
  mutation,
  onTradeSelect
}) => {
  const shouldShowResults = mutation.data || mutation.isError || mutation.isPending;
  if (!shouldShowResults) {
    return null;
  }
  if (mutation.isPending) {
    return /* @__PURE__ */ jsx34(LoadingState, {});
  }
  if (mutation.isError) {
    return /* @__PURE__ */ jsx34(Text8, { variant: "h4", children: "Failed to fetch trades. Please try again." });
  }
  if (!mutation.data || mutation.data.length === 0) {
    return /* @__PURE__ */ jsx34(Text8, { variant: "h4", children: "No trades found. Try a different description." });
  }
  return /* @__PURE__ */ jsx34(TradesListState, { trades: mutation.data, onTradeSelect });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/TradeQuestion/index.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var TradeQuestion2 = (props) => {
  const { type } = props;
  const variant = type === "trade_search_ai" ? "ai" : "default";
  if (variant === "ai") {
    return /* @__PURE__ */ jsx35(AiTradeQuestion, { ...props });
  } else {
    return /* @__PURE__ */ jsx35(TradeQuestion, { ...props });
  }
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AddressLookupQuestion/AddressLookupGroupQuestion.tsx
import {
  AddressLookup as AddressLookup2,
  Button as Button12,
  LoqateAddressLookupService as LoqateAddressLookupService2
} from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext8 } from "@simplybusiness/services";
import { useRef as useRef9 } from "react";

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AddressLookupQuestion/getStateCode.ts
var STATE_NAME_TO_CODE_MAP = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  // Federal districts and territories
  "district of columbia": "DC",
  "washington dc": "DC",
  "washington d.c.": "DC",
  "puerto rico": "PR",
  "american samoa": "AS",
  guam: "GU",
  "northern mariana islands": "MP",
  "u.s. virgin islands": "VI",
  "virgin islands": "VI"
};
var getStateCode = (stateName) => {
  if (!stateName || typeof stateName !== "string") {
    return "";
  }
  const normalizedName = stateName.trim().toLowerCase();
  return STATE_NAME_TO_CODE_MAP[normalizedName] || "";
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/AddressLookupQuestion/AddressLookupGroupQuestion.tsx
import { jsx as jsx36, jsxs as jsxs21 } from "react/jsx-runtime";
import { createElement as createElement2 } from "react";
var AddressLookupGroupQuestion = ({
  questionFieldProps,
  config,
  sectionTitle
}) => {
  const { showAddressGroupManual, groups } = useQuestionnaire();
  const { currentSection } = useCurrentSection();
  const typedConfig = config;
  const { snowplow } = useSnowplowContext8();
  const sendAddressLookupEvent = useRef9(false);
  const USState = useAnswer(US_STATE_ANSWER_KEY);
  const USStateCode = getStateCode(USState);
  const {
    name,
    value,
    onChange,
    onFocus,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const loqateService = new LoqateAddressLookupService2({
    apiKey: typedConfig.apiKey,
    countries: [typedConfig.countryCode],
    filters: USStateCode ? { AdministrativeArea: USStateCode } : void 0
  });
  const handleFocus = (event) => {
    snowplow?.trigger("addressLookupFocused", {
      category: name,
      label: "Address lookup focused"
    });
    onFocus?.(event);
  };
  const handleOptionsDisplayed = () => {
    snowplow?.trigger("addressOptionsDisplayed", {
      category: name,
      label: "Address options displayed"
    });
  };
  const handleChange = (data) => {
    const addressData = {};
    const group = groups[name];
    if (group && group.kind === "address") {
      const childQuestions = currentSection?.questions.filter(isQuestion).filter((question) => group.names.includes(question.name));
      childQuestions?.forEach((question) => {
        const mappingKey = toPascalCase(
          question?.loqateMapping
        );
        const value2 = data[mappingKey] || "";
        if (mappingKey === "Street") {
          addressData[question.name] = `${data.BuildingNumber} ${value2}`;
        } else {
          addressData[question.name] = mappingKey === "PostalCode" ? value2.split("-")[0] : value2;
        }
      });
    }
    const target = {
      name,
      value: addressData
    };
    onChange?.({ target });
  };
  const showManualFields = () => {
    snowplow?.trigger("manualAddressClicked", {
      category: sectionTitle || questionFieldProps.label || name
    });
    showAddressGroupManual(name);
  };
  const handleAddressInput = (e) => {
    const { value: value2 } = e.target;
    if (value2.length === 1 && !sendAddressLookupEvent?.current) {
      snowplow?.trigger("addressLookupTypedIn", {
        category: e.target.name,
        label: "First character input"
      });
      sendAddressLookupEvent.current = true;
    }
  };
  return /* @__PURE__ */ jsxs21("div", { className: "mobius-address-lookup__container", children: [
    /* @__PURE__ */ createElement2(
      AddressLookup2,
      {
        addressLookupService: loqateService,
        ...otherProps,
        onChange: handleAddressInput,
        value,
        key: value,
        placeholder: "Enter your address",
        onAddressSelected: handleChange,
        onOptionsDisplayed: handleOptionsDisplayed,
        onFocus: handleFocus,
        minSearchLength: 5
      }
    ),
    !typedConfig.manualEntryDisabled && /* @__PURE__ */ jsx36(Button12, { size: "sm", variant: "link", onClick: showManualFields, children: "Enter address manually" })
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/CurrencyQuestion/CurrencyQuestion.tsx
import { MaskedField as MaskedField2, Text as Text9 } from "@simplybusiness/mobius";
import { jsx as jsx37 } from "react/jsx-runtime";
var sanitizeValue = (val) => {
  if (val === void 0) return void 0;
  else if (typeof val === "number") return val.toString();
  return val.replace(/,/g, "");
};
var stringToNumber = (value) => {
  return value === "" ? 0 : Number(value);
};
var CurrencyQuestion = ({
  questionFieldProps,
  config
}) => {
  const {
    value,
    validation,
    readOnly,
    onChange,
    onBlur,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { isUK } = useLocale();
  const sanitizedValue = sanitizeValue(value);
  const { wholeNumber = false } = config ?? {};
  const symbol = isUK ? "\xA3" : "$";
  const mask = {
    mask: Number,
    thousandsSeparator: ",",
    scale: wholeNumber ? 0 : 2,
    signed: false
  };
  const handleChange = (event) => onChange?.({
    target: {
      name: event.target.name,
      value: stringToNumber(event.target.value)
    }
  });
  const handleBlur = (event) => onBlur?.({
    target: {
      name: event.target.name,
      value: stringToNumber(event.target.value)
    }
  });
  return /* @__PURE__ */ jsx37(
    MaskedField2,
    {
      ...otherProps,
      onChange: handleChange,
      onBlur: handleBlur,
      mask,
      prefixInside: /* @__PURE__ */ jsx37(Text9, { children: symbol }),
      isReadOnly: !!readOnly,
      defaultValue: sanitizedValue,
      useMaskedValue: false
    }
  );
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/ZipCodeQuestion/ZipCodeQuestion.tsx
import { TextField as TextField4 } from "@simplybusiness/mobius";
import { useEffect as useEffect10, useRef as useRef11 } from "react";

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/ZipCodeQuestion/useZipCodeValidation.ts
import { useQuery } from "@tanstack/react-query";
var validateZipCode = async ({
  zipcode,
  state,
  endpoint = `${location.pathname}?step_action=verify_mailing_address`,
  authenticityToken
}) => {
  const requestData = {
    authenticity_token: authenticityToken,
    mailing_us_zipcode: zipcode,
    mailing_us_state: state ?? ""
  };
  const response = await fetch(endpoint, {
    method: "POST",
    body: JSON.stringify(requestData),
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    }
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.json();
};
var useZipCodeValidation = ({
  value,
  state,
  validationOptions
}) => {
  const tokens = useTokensValues();
  const query = useQuery({
    queryKey: ["zipcode-validation", value, state],
    queryFn: () => {
      const zipString = String(value ?? "");
      if (zipString.length !== 5) {
        return Promise.resolve({ status: true });
      }
      return validateZipCode({
        zipcode: zipString,
        state,
        endpoint: validationOptions?.apiEndpoint,
        authenticityToken: tokens.authenticityToken
      });
    },
    enabled: !!value,
    // Always run when we have a value (even if it's short)
    staleTime: Infinity,
    // Cache forever as requested
    gcTime: Infinity,
    // Keep in cache forever
    retry: false
    // Don't retry on error to avoid hammering the API
  });
  let isValid = true;
  let message;
  if (query.isSuccess && query.data) {
    if (query.data.status === false && query.data.state_name && query.data.zip_code) {
      isValid = false;
      message = `Looks like that zip code is not in ${query.data.state_name}. Try updating your zip code.`;
    }
  } else if (query.isError) {
    console.error("Zip code validation failed:", query.error);
    isValid = true;
  }
  return {
    ...query,
    isValid,
    validationMessage: message,
    city: query.data?.city_for_zipcode
  };
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/ZipCodeQuestion/useUpdateCity.ts
import { useEffect as useEffect9, useRef as useRef10 } from "react";
function createAnswerChangeEvent(name, value) {
  return {
    target: {
      name,
      value
    }
  };
}
var useUpdateCity = ({ city, isValid }) => {
  const { updateAnswer } = useQuestionnaire();
  const previousCityRef = useRef10(void 0);
  const isFirstRender = useRef10(true);
  useEffect9(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousCityRef.current = city;
      return;
    }
    const hasCityChanged = previousCityRef.current !== city;
    if (hasCityChanged && city && isValid) {
      updateAnswer(createAnswerChangeEvent(BUSINESS_CITY_KEY, city));
    }
    previousCityRef.current = city;
  }, [city, isValid, updateAnswer]);
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/ZipCodeQuestion/ZipCodeQuestion.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
var ZipCodeQuestion = ({
  questionFieldProps
}) => {
  const {
    value,
    onChange,
    readOnly,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { id, name } = questionFieldProps;
  const { setManualError, clearManualError } = useQuestionnaire();
  const stateValue = useAnswer(US_STATE_ANSWER_KEY);
  const zipCodeValue = value;
  const { isValid, validationMessage, city } = useZipCodeValidation({
    value: zipCodeValue,
    state: stateValue
  });
  useUpdateCity({ city, isValid });
  const previousIsValidRef = useRef11(isValid);
  const previousValidationMessageRef = useRef11(
    validationMessage
  );
  useEffect10(() => {
    const hasValidationChanged = previousIsValidRef.current !== isValid || previousValidationMessageRef.current !== validationMessage;
    if (hasValidationChanged && name) {
      if (!isValid && validationMessage) {
        setManualError(name, validationMessage);
      } else {
        clearManualError(name);
      }
      previousIsValidRef.current = isValid;
      previousValidationMessageRef.current = validationMessage;
    }
  }, [isValid, validationMessage, name, setManualError, clearManualError]);
  const handleChange = (event) => {
    const { value: value2 } = event.target;
    const numericValue = value2.replace(/\D/g, "").slice(0, 5);
    onChange?.({
      target: { name, value: { [id]: numericValue, [name]: numericValue } }
    });
  };
  const handleBlur = (event) => {
    questionFieldProps.onBlur?.(event);
  };
  return /* @__PURE__ */ jsx38(
    TextField4,
    {
      ...otherProps,
      type: "text",
      inputMode: "numeric",
      pattern: "[0-9]{5}",
      placeholder: "12345",
      maxLength: 5,
      value: zipCodeValue,
      onChange: handleChange,
      onBlur: handleBlur,
      isReadOnly: !!readOnly
    }
  );
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/USStateQuestion/USStateQuestion.tsx
import { Combobox } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext9 } from "@simplybusiness/services";
import { useMemo as useMemo8 } from "react";
import { jsx as jsx39 } from "react/jsx-runtime";
var USStateQuestion = ({
  questionFieldProps,
  options = []
}) => {
  const {
    value,
    onChange,
    onBlur,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { snowplow } = useSnowplowContext9();
  const state = useQuestionnaire();
  const stateOptions = options;
  const { labels, labelToValueMap, valueToLabelMap } = useMemo8(() => {
    const labelsList = stateOptions.map((option) => option.label);
    const labelToValue = stateOptions.reduce(
      (acc, option) => {
        acc[option.label] = option.value;
        return acc;
      },
      {}
    );
    const valueToLabel = stateOptions.reduce(
      (acc, option) => {
        acc[option.value] = option.label;
        return acc;
      },
      {}
    );
    return {
      labels: labelsList,
      labelToValueMap: labelToValue,
      valueToLabelMap: valueToLabel
    };
  }, [stateOptions]);
  const handleSelected = (selectedLabel) => {
    const mappedValue = labelToValueMap[selectedLabel] || selectedLabel;
    snowplow?.trigger("stateQuestionAnswered", {
      answer: mappedValue,
      selectedCoverages: state.config?.selectedCoverages,
      selectedType: "coverage_selection"
    });
    onChange?.({
      target: {
        name: questionFieldProps.name,
        value: mappedValue
      }
    });
  };
  const displayValue = value ? valueToLabelMap[value] || value : "";
  return /* @__PURE__ */ jsx39(
    Combobox,
    {
      ...otherProps,
      value: displayValue,
      options: labels,
      onSelected: handleSelected
    }
  );
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/PersonalisedCoverQuestion/PersonalisedCoverQuestion.tsx
import { useState as useState10 } from "react";
import {
  Label as Label2,
  Flex as Flex6,
  TextOrHTML as TextOrHTML6,
  Switch,
  Accordion
} from "@simplybusiness/mobius";
import { Fragment as Fragment11, jsx as jsx40, jsxs as jsxs22 } from "react/jsx-runtime";
var PersonalisedCoverQuestion = ({
  questionFieldProps
}) => {
  const {
    name,
    value,
    onChange,
    questionLabelProps: {
      primaryText,
      secondaryText,
      labelClasses,
      labelElementType,
      labelProps
    },
    isRequired: isRequired2,
    errorMessage,
    helpInfo,
    isInvalid,
    ...otherProps
  } = questionFieldProps;
  const isChecked = value === "yes";
  const [accordionOpen, setAccordionOpen] = useState10(false);
  const { groups, questions } = useQuestionnaire();
  const toggleGroupQuestions = (show) => {
    const groupQuestions = groups?.[name]?.names || [];
    if (show) {
      questions.show(groupQuestions);
    } else {
      questions.hide(groupQuestions);
    }
  };
  const handleSwitchChange = (event) => {
    const checked = event.target.checked;
    const newValue = checked ? "yes" : "no";
    onChange?.({
      target: { name, value: newValue }
    });
    setAccordionOpen(checked);
    toggleGroupQuestions(checked);
  };
  const handleAccordionChange = (open) => {
    setAccordionOpen(open);
    toggleGroupQuestions(open && isChecked);
  };
  return /* @__PURE__ */ jsxs22(Fragment11, { children: [
    /* @__PURE__ */ jsxs22(Flex6, { justifyContent: "space-between", children: [
      /* @__PURE__ */ jsx40(
        Label2,
        {
          className: labelClasses,
          elementType: labelElementType,
          ...labelProps,
          children: /* @__PURE__ */ jsx40(TextOrHTML6, { text: primaryText })
        }
      ),
      /* @__PURE__ */ jsx40(
        Switch,
        {
          ...otherProps,
          checked: isChecked,
          onChange: handleSwitchChange
        }
      )
    ] }),
    /* @__PURE__ */ jsx40(
      Accordion,
      {
        showText: "More info",
        hideText: "Less info",
        startOpen: accordionOpen,
        onChange: handleAccordionChange,
        children: secondaryText && /* @__PURE__ */ jsx40(TextOrHTML6, { text: secondaryText, textWrapper: true })
      }
    )
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionFactory/questions/questionMap.tsx
var TRIGGERS = {
  CHANGE: "change",
  BLUR: "blur"
};
var ELEMENT_TYPES = {
  LABEL: "label",
  SPAN: "span"
};
var questionMap = {
  default: {
    component: TextQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  info: {
    component: InfoQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  address_lookup: {
    component: AddressLookupQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  address_lookup_group: {
    component: AddressLookupGroupQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  annual_turnover: {
    component: AnnualTurnoverQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  multiselect: {
    component: MultiSelectQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  radio_buttons: {
    component: RadioButtonsQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  select: {
    component: SelectQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  text: {
    component: TextQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  number: {
    component: NumberQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  currency: {
    component: CurrencyQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  tel: {
    component: TelephoneQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  rebuild_cost_estimate: {
    component: NumberQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  datepicker: {
    component: DateQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  date: {
    component: DateInputQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  trade_search: {
    component: TradeQuestion2,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  trade_search_ai: {
    component: TradeQuestion2,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.SPAN
  },
  personalised_cover: {
    component: PersonalisedCoverQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  // Items below need to be reconciled
  given_name: {
    component: TextQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  family_name: {
    component: TextQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  email: {
    component: TextQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  percentage_split: {
    component: PercentageQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  percentage_split_group: {
    component: PercentageGroupQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  postcode: {
    component: TextQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  zipcode: {
    component: ZipCodeQuestion,
    trigger: TRIGGERS.BLUR,
    labelElementType: ELEMENT_TYPES.LABEL
  },
  us_state_select: {
    component: USStateQuestion,
    trigger: TRIGGERS.CHANGE,
    labelElementType: ELEMENT_TYPES.LABEL
  }
};

// src/components/Questionnaire/Section/Question/QuestionFactory/QuestionFactory.tsx
import { jsx as jsx41 } from "react/jsx-runtime";
var CUSTOM_VALIDATION_TYPES = ["email", "tel", "zipcode"];
var QuestionFactory = (props) => {
  const {
    display,
    options,
    shouldUpdate,
    trackAs,
    associatedQuestionName,
    questionFieldProps
  } = props;
  const { type, config, component: displayType } = display;
  const {
    updateAnswer,
    validateQuestion: validateQuestion2,
    removeError,
    validations,
    addressModes,
    setAddressMode,
    isEditing,
    groups
  } = useQuestionnaire();
  const visibility = useVisibilityMap();
  const { currentSection, currentSectionIndex } = useCurrentSection();
  const { locale } = useLocale();
  const { snowplow } = useSnowplowContext10();
  const { sendAnswers, isPending } = useSendAnswers();
  const answers = useAnswers();
  const currentQuestionAnswer = useAnswer(questionFieldProps.name);
  const { sendAnswers: sendAssumptions, isPending: isPendingAssumptions } = useDependentQuestions();
  const stateError = useError(questionFieldProps.name) ?? "";
  const [errorMessage, setErrorMessage] = useState11(stateError);
  const [isValid, setIsValid] = useState11(!!stateError);
  const [isDirty, setIsDirty] = useState11(false);
  const { storeFocusedElement } = useRestoreFocus(
    isPending || isPendingAssumptions
    // Block restoration while requests are pending
  );
  const validationCompareRule = useMemo9(
    () => validations?.find(
      (validation) => validation.id === questionFieldProps.name
    ),
    [questionFieldProps.name, validations]
  );
  const validate = useCallback11(
    (event) => {
      const payload = event.target;
      const mergedAnswers = computeMergedAnswersWithGroups({
        answers,
        groups,
        payload
      });
      const validation = {
        validation: questionFieldProps.validation,
        ...isOneOf(type, CUSTOM_VALIDATION_TYPES) && {
          validation: { type }
        }
      };
      const result = validateQuestion2({
        question: {
          ...questionFieldProps,
          ...validation,
          visible: true,
          display
        },
        answers: mergedAnswers,
        visibilityMap: visibility,
        locale,
        groups
      });
      setIsDirty(true);
      if (result?.valid) {
        setIsValid(true);
        if (stateError) {
          removeError(questionFieldProps.name);
        }
        setErrorMessage("");
      } else {
        setIsValid(false);
        setErrorMessage(String(result?.message ?? ""));
      }
      return result.valid;
    },
    [
      answers,
      groups,
      questionFieldProps,
      type,
      display,
      validateQuestion2,
      visibility,
      stateError,
      removeError,
      locale
    ]
  );
  useEffect11(() => {
    if (validationCompareRule && isDirty) {
      validate({
        target: {
          name: questionFieldProps.name,
          value: currentQuestionAnswer
        }
      });
    }
  }, [
    validationCompareRule,
    questionFieldProps.name,
    currentQuestionAnswer,
    validate,
    isDirty
  ]);
  const componentType = displayType ?? type;
  const questionConfig = questionMap[componentType] ?? questionMap.default;
  const Component = questionConfig.component;
  const trigger = questionConfig.trigger;
  if (!questionMap[componentType]) {
    console.warn(
      `No component found to render as: ${componentType}, falling back to default (TextQuestion)`
    );
  }
  const handleActions = (event) => {
    const payload = event.target;
    const isValid2 = validate(event);
    updateAnswer(event);
    trackQuestionAnswer(event);
    if (!isValid2 && !payload.shouldUpdate) {
      return;
    }
    if (shouldUpdate || payload.shouldUpdate) {
      storeFocusedElement();
      const mergedAnswers = computeMergedAnswersWithGroups({
        answers,
        groups,
        payload
      });
      if (isEditing) {
        void sendAssumptions(mergedAnswers, questionFieldProps.name);
      } else {
        void sendAnswers(mergedAnswers);
      }
    }
  };
  const handleBlurChanges = (event) => {
    updateAnswer(event);
  };
  const trackQuestionAnswer = (event) => {
    const payload = event.target;
    const { name } = payload;
    let { value } = payload;
    if (isObject(value)) {
      value = String(value[name]);
    }
    if (trackAs === "form_question_answered" && !!value) {
      snowplow?.trigger("questionAnswered", {
        section: currentSection?.title ?? "",
        question: snakeCase(questionFieldProps.name),
        answer: value
      });
    }
    if (trackAs === "primary_detail_selected" && !!value) {
      snowplow?.trigger("primaryDetailSelected", {
        answer: value
      });
    }
  };
  const enhancedQuestionProps = {
    ...questionFieldProps,
    ...trigger === TRIGGERS.CHANGE && { onChange: handleActions },
    ...trigger === TRIGGERS.BLUR && {
      onBlur: handleActions,
      onChange: handleBlurChanges
    },
    isInvalid: !isValid && isDirty || !!stateError,
    errorMessage: errorMessage || stateError,
    isDisabled: isPending || isPendingAssumptions
  };
  return /* @__PURE__ */ jsx41(
    Component,
    {
      options,
      type,
      config,
      questionFieldProps: enhancedQuestionProps,
      sectionTitle: currentSection?.title,
      sectionIndex: currentSectionIndex,
      addressModes,
      setAddressMode,
      associatedQuestionName
    }
  );
};

// src/components/Questionnaire/Section/Question/Question.tsx
import { jsx as jsx42, jsxs as jsxs23 } from "react/jsx-runtime";
var SIMPLE_WRAPPER_TYPES = /* @__PURE__ */ new Set([
  "info",
  "trade_search",
  "trade_search_ai",
  "personalised_cover"
]);
var Question = (props) => {
  const {
    id,
    name,
    primaryText,
    secondaryText,
    errorMessage,
    helpInfo,
    placeholder,
    shouldUpdate,
    readOnly,
    required,
    display,
    options,
    isInvalid,
    className,
    validation,
    trackAs,
    associatedQuestionName,
    chatConfig,
    suggested,
    shouldAnimate = false
  } = props;
  const answerValue = useAnswer(name);
  const visible = useVisibility(name) ?? props.visible;
  const secondaryTextId = useId4();
  const classes = classNames5(
    "mobius",
    "mobius-question",
    { "mobius-question--animate": shouldAnimate },
    className
  );
  const validationClasses = useValidationClasses({ isInvalid });
  const labelClasses = classNames5("mobius-question__label", validationClasses);
  const { registerQuestionRef, unregisterQuestionRef } = useQuestionRefs();
  const questionRef = useMemo10(
    () => registerQuestionRef(name),
    [name, registerQuestionRef]
  );
  useEffect12(() => {
    return () => {
      unregisterQuestionRef(name);
    };
  }, [name, unregisterQuestionRef]);
  const { labelElementType } = questionMap[display.type] ?? {};
  const { labelProps, fieldProps } = useLabel({
    label: primaryText,
    id,
    labelElementType
  });
  const { snowplow } = useSnowplowContext11();
  if (!visible && !suggested) return null;
  const isSecurityLockQuestion = id === "sof_door_locks";
  const showSecondaryText = secondaryText && !isSecurityLockQuestion;
  const questionLabelProps = {
    primaryText,
    secondaryText: showSecondaryText ? secondaryText : void 0,
    labelClasses,
    labelElementType,
    labelProps,
    secondaryTextId
  };
  const onOpenDrawer = () => {
    snowplow?.trigger("helpTextOpened", {
      label: helpInfo?.label,
      helpText: helpInfo?.headline,
      //schema getting broken here
      primaryText
    });
  };
  const questionFactoryProps = {
    display,
    options,
    shouldUpdate,
    trackAs,
    associatedQuestionName,
    questionFieldProps: {
      id,
      name,
      placeholder,
      readOnly,
      isRequired: required,
      "aria-describedby": secondaryText ? secondaryTextId : void 0,
      value: answerValue,
      validation,
      errorMessage,
      questionLabelProps,
      helpInfo: helpInfo ? { ...helpInfo, onOpenDrawer } : void 0,
      ...fieldProps
    }
  };
  if (SIMPLE_WRAPPER_TYPES.has(display.type)) {
    return /* @__PURE__ */ jsx42("div", { className: classes, ref: questionRef, children: /* @__PURE__ */ jsx42(QuestionFactory, { ...questionFactoryProps }) });
  }
  return /* @__PURE__ */ jsxs23("div", { className: classes, ref: questionRef, children: [
    /* @__PURE__ */ jsx42(QuestionLabel, { ...questionLabelProps }),
    helpInfo && /* @__PURE__ */ jsx42(HelpLink3, { ...helpInfo, onOpenDrawer }),
    isSecurityLockQuestion && /* @__PURE__ */ jsx42(SecurityLocks, {}),
    /* @__PURE__ */ jsx42(QuestionFactory, { ...questionFactoryProps }),
    chatConfig && /* @__PURE__ */ jsx42(QuestionChatBox, { chatConfig, questionName: name })
  ] });
};

// src/components/Questionnaire/Section/Question/QuestionChatBox.tsx
import { Flex as Flex7, Text as Text10 } from "@simplybusiness/mobius";
import { jsx as jsx43, jsxs as jsxs24 } from "react/jsx-runtime";
var QuestionChatBox = ({
  questionName,
  chatConfig: { text, hintText, chatLabel }
}) => {
  return /* @__PURE__ */ jsxs24(ChatBox, { className: "mobius-question__chatbox", position: "left", children: [
    /* @__PURE__ */ jsx43(Text10, { variant: "body", children: text }),
    !!hintText && /* @__PURE__ */ jsx43(Text10, { variant: "body", children: /* @__PURE__ */ jsxs24("strong", { children: [
      "Hint: ",
      hintText
    ] }) }),
    /* @__PURE__ */ jsxs24(Flex7, { className: "mobius-question__chatbox-link-container", children: [
      /* @__PURE__ */ jsx43(Text10, { variant: "body", children: "Still unsure?" }),
      /* @__PURE__ */ jsx43(ChatBox.ChatLink, { label: chatLabel, questionName })
    ] })
  ] });
};

// src/components/Questionnaire/Section/EditSection.tsx
import { jsx as jsx44, jsxs as jsxs25 } from "react/jsx-runtime";
var EditSection = forwardRef6((props, ref) => {
  const { questions, className } = props;
  const classes = classNames6("mobius", "mobius-edit-section", className);
  const {
    sections,
    validations,
    groups,
    navigatePrev,
    validateForm,
    setErrors,
    setEditMode
  } = useQuestionnaire();
  const visibility = useVisibilityMap();
  const { currentSectionIndex, lastViewedSectionIndex } = useCurrentSection();
  const answers = useAnswers();
  const { isPending } = useDependentQuestions();
  const { sendAnswers, isPending: isSending } = useSendAnswers();
  const { focusFirstError } = useQuestionRefs();
  useEffect13(() => {
    setEditMode(true);
  }, [setEditMode]);
  const handleSaveAndContinue = () => {
    const result = validateForm({
      answers,
      sections,
      validations,
      visibility,
      groups,
      manualErrors: {},
      currentSectionIndex,
      lastViewedSectionIndex
    });
    if (!result.isValid) {
      setErrors(result.errors);
      focusFirstError(result.errors);
    } else {
      setErrors({});
      sendAnswers(answers).then(navigatePrev).catch((err) => console.error("Error sending answers:", err));
    }
  };
  return /* @__PURE__ */ jsxs25("section", { "data-testid": "mobius-section", ref, className: classes, children: [
    /* @__PURE__ */ jsx44("div", { className: "mobius-section__question-container", children: questions.map((question) => /* @__PURE__ */ jsx44(Question, { ...question, answers }, question.name)) }),
    /* @__PURE__ */ jsx44(Box, { paddingBlockStart: "sm", paddingBlockEnd: "sm", children: /* @__PURE__ */ jsx44(
      Button13,
      {
        onClick: handleSaveAndContinue,
        isDisabled: isPending || isSending,
        children: "Save and Continue"
      }
    ) })
  ] });
});
EditSection.displayName = "EditSection";

// src/components/Questionnaire/Section/ReviewSection.tsx
import { TextOrHTML as TextOrHTML8 } from "@simplybusiness/mobius";
import classNames11 from "classnames/dedupe";
import { forwardRef as forwardRef8, useEffect as useEffect15, useMemo as useMemo11, useState as useState13 } from "react";

// src/components/Questionnaire/Collapsible/Collapsible.tsx
import classNames10 from "classnames/dedupe";
import { forwardRef as forwardRef7, useCallback as useCallback12, useEffect as useEffect14, useRef as useRef12, useState as useState12 } from "react";

// src/components/Questionnaire/Collapsible/Content.tsx
import { List as List2, ListItem as ListItem2, Text as Text12 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext12 } from "@simplybusiness/services";
import classNames9 from "classnames/dedupe";

// src/components/Questionnaire/WindowLocks/WindowLocks.tsx
import classNames7 from "classnames/dedupe";
import { Image, Text as Text11 } from "@simplybusiness/mobius";

// src/components/Questionnaire/WindowLocks/windowLocksData.ts
var windowLocksData = {
  imageSrc: "data:image/png;base64,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"
};

// src/components/Questionnaire/WindowLocks/WindowLocks.tsx
import { jsx as jsx45, jsxs as jsxs26 } from "react/jsx-runtime";
var WindowLocks = () => {
  const classes = classNames7("mobius", "mobius-window-locks");
  return /* @__PURE__ */ jsxs26("div", { className: classes, children: [
    /* @__PURE__ */ jsx45(Text11, { children: "Examples of these are shown below." }),
    /* @__PURE__ */ jsx45(Image, { src: windowLocksData.imageSrc, alt: "" })
  ] });
};

// src/components/Questionnaire/Collapsible/ContentTextBlock.tsx
import { penToSquare as penToSquare4 } from "@simplybusiness/icons";
import { Button as Button14, Icon as Icon11, TextOrHTML as TextOrHTML7 } from "@simplybusiness/mobius";
import classNames8 from "classnames/dedupe";
import { jsx as jsx46, jsxs as jsxs27 } from "react/jsx-runtime";
function ContentTextBlock({
  block,
  answer,
  shouldAddAnswer,
  blockIndex,
  hasSensitiveData,
  readOnly,
  capitalizeAnswer,
  onEditQuestion
}) {
  const answerClasses = classNames8({
    "has-pii": hasSensitiveData
  });
  const casedAnswer = blockIndex > 0 && capitalizeAnswer !== true ? answer?.charAt(0).toLowerCase() + answer?.slice(1) : answer;
  const casedAnswerElement = !readOnly ? /* @__PURE__ */ jsxs27(
    Button14,
    {
      "aria-label": `Edit answer: ${casedAnswer}`,
      variant: "link",
      size: "sm",
      className: classNames8(
        "mobius-collapsible__content-edit-button",
        answerClasses
      ),
      onClick: () => onEditQuestion?.(),
      children: [
        casedAnswer,
        " ",
        /* @__PURE__ */ jsx46(Icon11, { icon: penToSquare4, title: "Edit this answer" })
      ]
    }
  ) : casedAnswer;
  if (block === "")
    return /* @__PURE__ */ jsx46("strong", { className: answerClasses, children: casedAnswerElement });
  if (shouldAddAnswer)
    return /* @__PURE__ */ jsxs27("span", { children: [
      /* @__PURE__ */ jsx46("strong", { className: answerClasses, children: casedAnswerElement }),
      block
    ] });
  return /* @__PURE__ */ jsx46(TextOrHTML7, { text: block });
}

// src/components/Questionnaire/Collapsible/parseHtml.ts
var parseHtml = (html) => {
  const text = html.replace(/<ul>.*?<\/ul>/g, "").replace(/<b>/g, "").replace(/<\/b>/g, "").trim();
  const listItems = html.match(/<li>(.*?)<\/li>/g)?.map((item) => item.replace(/<\/?li>/g, "").trim()) || [];
  return {
    text,
    listItems
  };
};

// src/components/Questionnaire/Collapsible/Content.tsx
import { jsx as jsx47, jsxs as jsxs28 } from "react/jsx-runtime";
function Content({
  id,
  name,
  text,
  answer = "",
  assumptionGroup,
  hasSensitiveData,
  readOnly,
  capitalizeAnswer,
  onEditQuestion
}) {
  const { changedQuestions } = useQuestionnaire();
  const { snowplow } = useSnowplowContext12();
  const { text: t, listItems } = parseHtml(text);
  const textBlocks = t.split("${answer}");
  const addAnswerInMiddle = textBlocks.filter((b) => b !== "").length > 1;
  const shouldShowWindowLocks = id === "sof_window_locks";
  const shouldShowDoorLocks = id === "sof_door_locks";
  const isChanged = changedQuestions?.includes(name);
  const classes = classNames9({
    "mobius-collapsible__changed_question": isChanged
  });
  const handleClick = () => {
    snowplow?.trigger("assumptionLinkClicked", {
      category: assumptionGroup,
      label: name,
      property: answer
    });
    onEditQuestion?.();
  };
  return /* @__PURE__ */ jsxs28("div", { id: name, className: "mobius-collapsible__bottom-divider", children: [
    /* @__PURE__ */ jsxs28("div", { className: classes, children: [
      /* @__PURE__ */ jsx47("div", { className: "mobius-collapsible__content-blocks", children: /* @__PURE__ */ jsx47(Text12, { children: textBlocks.map((block, index) => {
        const shouldAddAnswer = addAnswerInMiddle && index === 1;
        return /* @__PURE__ */ jsx47(
          ContentTextBlock,
          {
            block,
            answer,
            shouldAddAnswer,
            blockIndex: index,
            hasSensitiveData,
            readOnly,
            onEditQuestion: handleClick,
            capitalizeAnswer
          },
          index
        );
      }) }, "text") }),
      listItems.length > 0 && /* @__PURE__ */ jsx47(List2, { children: listItems.map((item) => /* @__PURE__ */ jsx47(ListItem2, { children: item }, item)) })
    ] }),
    shouldShowWindowLocks && /* @__PURE__ */ jsx47(WindowLocks, {}),
    shouldShowDoorLocks && /* @__PURE__ */ jsx47("div", { className: "mobius-collapsible__locks-spacer", children: /* @__PURE__ */ jsx47(SecurityLocks, {}) })
  ] });
}

// src/components/Questionnaire/Collapsible/Details.tsx
import { Button as Button15, Flex as Flex8, Icon as Icon13 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext13 } from "@simplybusiness/services";

// src/components/Questionnaire/Collapsible/Summary.tsx
import { filePen, home, lock, tick, users as users2 } from "@simplybusiness/icons";
import { Icon as Icon12, Text as Text13 } from "@simplybusiness/mobius";
import { jsx as jsx48, jsxs as jsxs29 } from "react/jsx-runtime";
var headerIconsMap = {
  "about your property": home,
  security: lock,
  "property use": users2,
  "financial and claims": filePen
};
var headerTitlesMap = {
  "about your property": "Property information section",
  security: "Security information section",
  "property use": "Property usage section",
  "financial and claims": "Financial and claims section"
};
var getHeaderIcon = (header) => {
  const key = header.toLowerCase();
  return headerIconsMap[key] || home;
};
var getHeaderTitle = (header) => {
  const key = header.toLowerCase();
  return headerTitlesMap[key] ?? "Information section";
};
var Summary = ({ header, isCollapsed }) => {
  return /* @__PURE__ */ jsx48(
    "summary",
    {
      className: "mobius-collapsible__summary",
      onClick: (e) => e.preventDefault(),
      tabIndex: -1,
      children: /* @__PURE__ */ jsxs29("div", { className: "mobius-collapsible__summary-content", children: [
        isCollapsed ? /* @__PURE__ */ jsx48(
          Icon12,
          {
            "data-testid": "tick-icon",
            icon: tick,
            size: "md",
            title: "Section confirmed"
          }
        ) : /* @__PURE__ */ jsx48(
          Icon12,
          {
            "data-testid": "header-icon",
            className: "mobius-collapsible__summary-header-icon",
            icon: getHeaderIcon(header),
            size: "md",
            title: getHeaderTitle(header)
          }
        ),
        /* @__PURE__ */ jsx48(Text13, { elementType: "h2", children: header })
      ] })
    }
  );
};

// src/components/Questionnaire/Collapsible/Details.tsx
import { rightArrow as rightArrow5 } from "@simplybusiness/icons";
import { jsx as jsx49, jsxs as jsxs30 } from "react/jsx-runtime";
var Details = ({
  id,
  header,
  isOpen,
  isConfirmed,
  onReOpen,
  onConfirm,
  assumptionGroupIndex,
  children,
  helpText
}) => {
  const { snowplow } = useSnowplowContext13();
  const isCollapsed = isConfirmed && !isOpen;
  const showHelpButton = helpText !== "" && isOpen;
  const trackButtonClick = (source) => {
    snowplow?.trigger("assumptionsButtonClicked", {
      source,
      presentationGroup: header
    });
  };
  const handleReview = () => {
    trackButtonClick("Review");
    onReOpen(assumptionGroupIndex);
  };
  const handleConfirm = () => {
    trackButtonClick("Confirm");
    onConfirm(assumptionGroupIndex);
  };
  const handleHelpOpen = () => {
    trackButtonClick("More Info");
  };
  return /* @__PURE__ */ jsxs30("div", { className: "mobius-collapsible__wrapper", children: [
    /* @__PURE__ */ jsxs30("div", { className: "mobius-collapsible__external-buttons", children: [
      isCollapsed && /* @__PURE__ */ jsx49(
        Button15,
        {
          variant: "secondary",
          size: "sm",
          onClick: handleReview,
          className: "mobius-collapsible__review-button",
          children: "Review"
        }
      ),
      showHelpButton && /* @__PURE__ */ jsx49(
        HelpLink3,
        {
          headline: header,
          label: "More info",
          text: helpText ?? "",
          displayAsButton: true,
          onOpenDrawer: handleHelpOpen
        }
      )
    ] }),
    /* @__PURE__ */ jsxs30("details", { id, open: isOpen, className: "mobius-collapsible__details", children: [
      /* @__PURE__ */ jsx49(Summary, { header, isCollapsed }),
      /* @__PURE__ */ jsx49("div", { className: "mobius-collapsible__content", children }),
      /* @__PURE__ */ jsx49(Flex8, { gap: "sm", className: "mobius-collapsible__actions", children: /* @__PURE__ */ jsxs30(Button15, { variant: "primary", size: "sm", onClick: handleConfirm, children: [
        /* @__PURE__ */ jsx49("span", { children: "Confirm" }),
        /* @__PURE__ */ jsx49(Icon13, { icon: rightArrow5, title: "Proceed to next step" })
      ] }) })
    ] })
  ] });
};

// src/components/Questionnaire/Collapsible/scrollToAssumptionGroup.tsx
var IOS_SCROLL_TIMEOUT = 300;
var scrollToAssumptionGroup = (assumptionGroup) => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return;
  }
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !Object.prototype.hasOwnProperty.call(window, "MSStream");
  const nextAssumptionGroup = document.getElementById(
    `assumption-group-${assumptionGroup}`
  );
  const firstFocusable = nextAssumptionGroup?.querySelector(
    FOCUSABLE_ELEMENT_SELECTOR
  );
  if (firstFocusable) {
    firstFocusable?.focus();
  }
  if (nextAssumptionGroup && isIOS) {
    const elementPosition = nextAssumptionGroup.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - 20;
    setTimeout(() => {
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }, IOS_SCROLL_TIMEOUT);
  } else if (nextAssumptionGroup) {
    nextAssumptionGroup.scrollIntoView({ behavior: "smooth" });
  } else {
    const submission = document.getElementById("mobius-submission");
    submission?.scrollIntoView({ behavior: "smooth" });
  }
};

// src/components/Questionnaire/Collapsible/Collapsible.tsx
import { jsx as jsx50 } from "react/jsx-runtime";
var Collapsible = forwardRef7((props, ref) => {
  const {
    assumptionGroups,
    className,
    onConfirmAssumptionGroup,
    onAllConfirmed,
    expandedAssumptionGroup,
    confirmedAssumptionGroups,
    onEditQuestion,
    onAssumptionGroupExpanded,
    ...rest
  } = props;
  const sentAllConfirmed = useRef12(false);
  const [openAssumptionGroup, setOpenAssumptionGroup] = useState12(
    expandedAssumptionGroup ?? 0
  );
  const [_confirmedAssumptionGroups, setConfirmedAssumptionGroups] = useState12(confirmedAssumptionGroups ?? /* @__PURE__ */ new Set());
  const allAssumptionGroupsConfirmed = useCallback12(() => {
    return assumptionGroups.length === _confirmedAssumptionGroups.size;
  }, [assumptionGroups, _confirmedAssumptionGroups]);
  useEffect14(() => {
    if (allAssumptionGroupsConfirmed() && onAllConfirmed && !sentAllConfirmed.current) {
      sentAllConfirmed.current = true;
      onAllConfirmed();
    }
  }, [
    allAssumptionGroupsConfirmed,
    confirmedAssumptionGroups,
    onAllConfirmed
  ]);
  const handleExpandAssumptionGroup = (assumptionGroup) => {
    setOpenAssumptionGroup(assumptionGroup);
    onAssumptionGroupExpanded?.(assumptionGroup);
  };
  const handleConfirm = (assumptionGroup) => {
    onConfirmAssumptionGroup?.(assumptionGroup);
    if (document.startViewTransition) {
      document.startViewTransition?.(() => {
        setConfirmedAssumptionGroups(
          (prev) => new Set(prev).add(assumptionGroup)
        );
        handleExpandAssumptionGroup(assumptionGroup + 1);
      });
    } else {
      setConfirmedAssumptionGroups(
        (prev) => new Set(prev).add(assumptionGroup)
      );
      handleExpandAssumptionGroup(assumptionGroup + 1);
    }
  };
  useEffect14(() => {
    if (openAssumptionGroup) {
      scrollToAssumptionGroup(openAssumptionGroup);
    }
  }, [openAssumptionGroup]);
  const handleReOpen = (assumptionGroup) => {
    handleExpandAssumptionGroup(assumptionGroup);
    scrollToAssumptionGroup(assumptionGroup);
  };
  const classes = classNames10("mobius", "mobius-collapsible", className);
  return /* @__PURE__ */ jsx50("div", { ref, className: classes, ...rest, children: assumptionGroups.map(({ header, content, help }, index) => /* @__PURE__ */ jsx50(
    Details,
    {
      id: `assumption-group-${index}`,
      header,
      onReOpen: handleReOpen,
      assumptionGroupIndex: index,
      isOpen: openAssumptionGroup === index,
      isConfirmed: _confirmedAssumptionGroups.has(index),
      onConfirm: handleConfirm,
      helpText: help,
      children: content.map((question) => /* @__PURE__ */ jsx50(
        Content,
        {
          "data-assumption-group-index": index,
          assumptionGroup: header,
          onEditQuestion: () => onEditQuestion?.(question.name),
          ...question
        },
        question.name
      ))
    },
    header
  )) });
});
Collapsible.displayName = "Collapsible";

// src/hooks/useQuestionnaire/makeFullAddress.ts
var makeFullAddress = (questionName, state) => {
  const { sections: sectionsFromState, answers = {} } = state;
  const sections = sectionsFromState;
  const question = sections.flatMap((section) => section.questions).find((q) => q.name === questionName);
  if (!question) {
    return "";
  }
  const mappedFields = question.display.config?.mappedFields || [];
  const questionIds = Array.isArray(mappedFields) ? mappedFields.map(
    (field) => field.associatedQuestionId
  ) : [];
  const questionNames = sections.flatMap((section) => section.questions).filter((q) => questionIds.includes(q.id)).map((q) => q.name);
  const manualAnswers = questionNames.map((name) => answers[name]);
  const fullAddress = manualAnswers.filter(Boolean).join(", ");
  return fullAddress;
};

// src/components/Questionnaire/Section/groupByAssumption.ts
var getAnswerText = (answer, options) => {
  if (Array.isArray(answer)) {
    return answer.join(", ");
  }
  return options?.find((o) => o.value === answer)?.label || (typeof answer === "object" ? "" : answer?.toString());
};
var makeContent = ({
  question,
  answers,
  sections,
  addressModes,
  locale
}) => {
  const {
    id,
    name,
    presentationText,
    options,
    hasSensitiveData,
    display,
    capitalizeAnswer
  } = question;
  const { type } = display;
  const isAddress = type === "address_lookup";
  const isDate = type === "date";
  let answer;
  if (isAddress && addressModes?.[name] === "manual") {
    answer = makeFullAddress(name, { sections, answers });
  } else if (isDate && answers[name]) {
    answer = formatDateByLocale(answers[name], locale);
  } else {
    answer = getAnswerText(answers[name], options);
  }
  return {
    id,
    name,
    text: presentationText || "",
    answer,
    hasSensitiveData,
    readOnly: isAddress,
    capitalizeAnswer
  };
};
var makePercentGroupContent = ({
  question,
  answers,
  questions,
  groups
}) => {
  const { id, name, presentationText, hasSensitiveData } = question;
  const memberNames = groups[name].names;
  const memberAnswers = memberNames.map((memberName) => {
    const memberQuestion = questions.find((q) => q.name === memberName);
    const memberAnswer = answers[memberName];
    if (memberQuestion?.readOnly || memberAnswer === "0%") {
      return null;
    }
    return memberQuestion?.primaryText && memberAnswer ? `${memberQuestion.primaryText} ${String(memberAnswer)}` : null;
  }).filter(Boolean);
  const EM_SPACE = "\u2003";
  const answer = memberAnswers.join(EM_SPACE);
  const presentationTextFallback = "Breakdown <br/>";
  return {
    id,
    name,
    text: presentationText || presentationTextFallback,
    answer,
    hasSensitiveData,
    readOnly: false,
    capitalizeAnswer: true
  };
};
var groupByAssumption = ({
  questions,
  answers,
  sections,
  addressModes,
  locale,
  groups
}) => {
  const groupedQuestions = questions.reduce(
    (acc, question) => {
      const assumptionGroup = question.presentationGroup || "";
      if ((question.visible || question.display.type === "address_lookup") && assumptionGroup !== "") {
        acc[assumptionGroup] = acc[assumptionGroup] || [];
        acc[assumptionGroup].push(question);
      }
      return acc;
    },
    {}
  );
  return Object.entries(groupedQuestions).map(
    ([assumptionGroup, groupQuestions]) => ({
      header: assumptionGroup,
      content: groupQuestions.map((question) => {
        if (groups && isPercentKindGroup(groups[question.name])) {
          return makePercentGroupContent({
            question,
            answers,
            questions,
            groups
          });
        }
        return makeContent({
          question,
          answers,
          addressModes,
          sections,
          locale
        });
      }),
      help: groupQuestions.filter(({ helpInfo }) => helpInfo).map(
        ({ helpInfo }) => `<h3>${helpInfo?.headline}</h3>
${helpInfo?.text}`
      ).join("\n")
    })
  );
};

// src/components/Questionnaire/Section/ReviewSection.tsx
import { jsx as jsx51, jsxs as jsxs31 } from "react/jsx-runtime";
var ReviewSection = forwardRef8((props, ref) => {
  const {
    title,
    preamble,
    postamble = "By continuing, you agree that the above statements are up to date and correct to the best of your knowledge.",
    questions,
    className,
    submitActionLabel = "Continue to quotes"
  } = props;
  const classes = classNames11("mobius", "mobius-section", className);
  const {
    sections: sectionsFromState,
    showQuestion,
    editQuestion,
    selectedQuestion,
    changedQuestions,
    changedAssumptionGroupIndex,
    confirmedAssumptionGroups,
    confirmAssumptionGroup,
    setEditMode,
    addressModes,
    errors,
    groups
  } = useQuestionnaire();
  const { currentSectionIndex } = useCurrentSection();
  const answers = useAnswers();
  const sections = sectionsFromState;
  const isMutating = useMutationStatus("prepopulateAnswers");
  const [allowSubmission, setAllowSubmission] = useState13(false);
  const [expandedAssumptionGroup, setExpandedAssumptionGroup] = useState13(
    changedAssumptionGroupIndex
  );
  const { locale } = useLocale();
  useEffect15(() => {
    const questionName = changedQuestions?.[0] || selectedQuestion;
    if (questionName) {
      const element = document.getElementById(questionName);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    setEditMode(false);
  }, []);
  const assumptionGroupsData = useMemo11(
    () => groupByAssumption({
      questions: questions.filter(isQuestion),
      // filter out groups to prevent runtime errors on the first render before flattening.
      answers,
      sections,
      addressModes,
      locale,
      groups
    }),
    [questions, answers, sections, addressModes, locale, groups]
  );
  const handleEditQuestion = (questionName) => {
    const foundIndex = sections.findIndex(
      ({ questions: questions2 }) => questions2.some(({ name }) => name === questionName)
    );
    if (foundIndex < currentSectionIndex) {
      showQuestion(questionName);
    } else {
      editQuestion({
        questionName,
        assumptionGroupIndex: expandedAssumptionGroup
      });
    }
  };
  return /* @__PURE__ */ jsxs31("section", { "data-testid": "mobius-review-section", ref, className: classes, children: [
    title && /* @__PURE__ */ jsx51("h2", { className: "mobius-section__title", children: title }),
    preamble && /* @__PURE__ */ jsx51(
      TextOrHTML8,
      {
        className: "mobius-section__preamble",
        text: preamble,
        textWrapper: true
      }
    ),
    /* @__PURE__ */ jsx51(
      Collapsible,
      {
        assumptionGroups: assumptionGroupsData,
        expandedAssumptionGroup,
        confirmedAssumptionGroups,
        onAllConfirmed: () => setAllowSubmission(true),
        onEditQuestion: handleEditQuestion,
        onAssumptionGroupExpanded: setExpandedAssumptionGroup,
        onConfirmAssumptionGroup: confirmAssumptionGroup
      }
    ),
    /* @__PURE__ */ jsx51(
      SubmitAction,
      {
        isDisabled: isMutating || !allowSubmission,
        buttonLabel: submitActionLabel,
        errors,
        children: /* @__PURE__ */ jsx51(
          TextOrHTML8,
          {
            className: "mobius-section__postamble",
            text: postamble,
            textWrapper: true
          }
        )
      }
    )
  ] });
});
ReviewSection.displayName = "ReviewSection";

// src/components/Questionnaire/Section/Section.tsx
import { TextOrHTML as TextOrHTML9 } from "@simplybusiness/mobius";
import classNames13 from "classnames/dedupe";
import { forwardRef as forwardRef9 } from "react";

// src/hooks/useShouldAnimateQuestions/useShouldAnimateQuestions.tsx
import { useEffect as useEffect16, useState as useState14 } from "react";
function useShouldAnimateQuestions() {
  const [shouldAnimate, setShouldAnimate] = useState14(false);
  useEffect16(() => {
    const timer = setTimeout(() => {
      setShouldAnimate(true);
    }, 0);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  return shouldAnimate;
}

// src/components/Questionnaire/Section/Insurers/Insurers.tsx
import { Flex as Flex9 } from "@simplybusiness/mobius";
import classNames12 from "classnames/dedupe";
import { jsx as jsx52 } from "react/jsx-runtime";
var VERTICALS = ["landlord", "shop", "business"];
var BUSINESS_SHOP_INSURERS = [
  "hiscox",
  "axa",
  "zurich",
  "aig",
  "lexicon",
  "maltings",
  "churchill",
  "chiswell",
  "covea",
  "finsbury"
];
var LANDLORD_INSURERS = [
  "axa",
  "churchill",
  "zurich",
  "covea",
  "aro",
  "modus",
  "melius",
  "uno",
  "chiswell",
  "finsbury",
  "maltings"
];
var getVerticalFromPreambleName = (preambleName) => {
  if (!preambleName?.includes("insurer_logos")) {
    return null;
  }
  const vertical = preambleName.split("_")[0];
  if (!vertical || !VERTICALS.includes(vertical)) {
    return null;
  }
  return vertical;
};
var Insurers = (props) => {
  const { className } = props;
  const classes = classNames12("mobius", "mobius-insurers", className);
  const insurersSet = props.insurersLogosSet === "landlord" ? LANDLORD_INSURERS : BUSINESS_SHOP_INSURERS;
  return /* @__PURE__ */ jsx52(
    Flex9,
    {
      "data-testid": `mobius-insurers-${props.insurersLogosSet}`,
      justifyContent: "space-between",
      flexWrap: "wrap",
      alignItems: "center",
      className: classes,
      children: insurersSet.map((insurer) => /* @__PURE__ */ jsx52(
        "div",
        {
          "data-testid": `mobius-insurers__insurer--${insurer}`,
          className: `mobius-insurers-${props.insurersLogosSet} mobius-insurers__insurer--${insurer}`
        },
        insurer
      ))
    }
  );
};

// src/components/Questionnaire/Section/Section.tsx
import { jsx as jsx53, jsxs as jsxs32 } from "react/jsx-runtime";
var Section = forwardRef9((props, ref) => {
  const { id, title, preamble, postamble, preambleName, questions, className } = props;
  const showInsurers = getVerticalFromPreambleName(preambleName);
  const businessOwner = useAnswer("Business owner");
  const shouldAnimate = useShouldAnimateQuestions();
  const shouldHideTitle = id === "Policyholder information" && businessOwner === "yes";
  const classes = classNames13("mobius", "mobius-section", className);
  return /* @__PURE__ */ jsxs32("section", { "data-testid": "mobius-section", ref, className: classes, children: [
    title && !shouldHideTitle && /* @__PURE__ */ jsx53("h2", { className: "mobius-section__title", children: title }),
    showInsurers && /* @__PURE__ */ jsx53(Insurers, { insurersLogosSet: showInsurers }),
    preamble && /* @__PURE__ */ jsx53(
      TextOrHTML9,
      {
        className: "mobius-section__preamble",
        text: preamble,
        textWrapper: true
      }
    ),
    /* @__PURE__ */ jsx53("div", { className: "mobius-section__question-container", children: questions.filter(isQuestion).map((question) => /* @__PURE__ */ jsx53(
      Question,
      {
        ...question,
        shouldAnimate
      },
      question.name
    )) }),
    postamble && /* @__PURE__ */ jsx53(
      TextOrHTML9,
      {
        htmlClassName: "mobius-section__postamble",
        text: postamble,
        textWrapper: true
      }
    )
  ] });
});
Section.displayName = "Section";

// src/components/Questionnaire/Section/CoverGeniusSection.tsx
import { TextOrHTML as TextOrHTML10 } from "@simplybusiness/mobius";
import classNames14 from "classnames/dedupe";
import { forwardRef as forwardRef10 } from "react";

// src/hooks/useCoverGeniusSection/useCoverGeniusSection.tsx
import { useEffect as useEffect17 } from "react";
var getVisibleQuestions = (allQuestions) => allQuestions.filter(isQuestion).filter(({ visible }) => visible);
var getSuggestedQuestions = (allQuestions) => allQuestions.filter(isQuestion).filter(({ suggested }) => suggested);
var useCoverGeniusSection = ({
  allQuestions
}) => {
  const { setVisible, setHidden } = useQuestionnaireActions();
  const [_, seeMoreCovers] = useCoverGeniusSeeMoreCoversState();
  const visiblySuggestedQuestionNames = getVisibleQuestions(allQuestions).map(
    ({ name }) => name
  );
  const visibleQuestions = getSuggestedQuestions(allQuestions).map(
    ({ name }) => name
  );
  useEffect17(() => {
    if (visiblySuggestedQuestionNames.length > 0) {
      setHidden(visibleQuestions);
      setVisible(visiblySuggestedQuestionNames);
      seeMoreCovers(
        visiblySuggestedQuestionNames.length < visibleQuestions.length
      );
    } else {
      seeMoreCovers(false);
    }
  }, []);
};

// src/components/Questionnaire/Section/CoverGeniusSection.tsx
import { jsx as jsx54, jsxs as jsxs33 } from "react/jsx-runtime";
var CoverGeniusSection = forwardRef10((props, ref) => {
  const { title, preamble, postamble, preambleName, questions, className } = props;
  const classes = classNames14("mobius", "mobius-section", className);
  const showInsurers = getVerticalFromPreambleName(preambleName);
  const visiblySuggestedQuestions = getVisibleQuestions(questions);
  const visibleQuestions = getSuggestedQuestions(questions);
  const allQuestions = visibleQuestions.length > 0 ? visibleQuestions : questions;
  const [showSeeMoreCoversTrigger] = useCoverGeniusSeeMoreCoversState();
  const coverGeniusQuestions = showSeeMoreCoversTrigger ? visiblySuggestedQuestions : allQuestions;
  useCoverGeniusSection({
    allQuestions: questions
  });
  return /* @__PURE__ */ jsxs33("section", { "data-testid": "mobius-section", ref, className: classes, children: [
    title && /* @__PURE__ */ jsx54("h2", { className: "mobius-section__title", children: title }),
    showInsurers && /* @__PURE__ */ jsx54(Insurers, { insurersLogosSet: showInsurers }),
    preamble && /* @__PURE__ */ jsx54(
      TextOrHTML10,
      {
        className: "mobius-section__preamble",
        text: preamble,
        textWrapper: true
      }
    ),
    /* @__PURE__ */ jsx54("div", { className: "mobius-section__question-container", children: coverGeniusQuestions.filter(isQuestion).map((question) => /* @__PURE__ */ jsx54(Question, { ...question }, question.name)) }),
    postamble && /* @__PURE__ */ jsx54(
      TextOrHTML10,
      {
        className: "mobius-section__postamble",
        text: postamble,
        textWrapper: true
      }
    )
  ] });
});
CoverGeniusSection.displayName = "Section";

// src/components/Questionnaire/Section/PersonalisedCoverSection/PersonalisedCoverSection.tsx
import { TextOrHTML as TextOrHTML11 } from "@simplybusiness/mobius";
import classNames15 from "classnames/dedupe";
import { forwardRef as forwardRef11 } from "react";

// src/components/Questionnaire/Section/PersonalisedCoverSection/utils.ts
var getGroupForQuestion = (name, groups) => {
  const group = groups[name];
  return group?.kind === "personalised_cover" ? group : void 0;
};
var isChildQuestion = (name, groups) => {
  return Object.values(groups).some((group) => group.names.includes(name));
};
var prepareQuestionsForRender = (questions, groups) => {
  const renderedChildren = /* @__PURE__ */ new Set();
  const items = [];
  questions.forEach((question) => {
    if (renderedChildren.has(question.name)) {
      return;
    }
    const group = getGroupForQuestion(question.name, groups);
    if (group) {
      const children = questions.filter((q) => group.names.includes(q.name));
      children.forEach((child) => renderedChildren.add(child.name));
      items.push({
        primary: question,
        children
      });
    } else if (!isChildQuestion(question.name, groups)) {
      items.push({
        primary: question,
        children: []
      });
    }
  });
  return items;
};

// src/components/Questionnaire/Section/PersonalisedCoverSection/PersonalisedCoverSection.tsx
import { jsx as jsx55, jsxs as jsxs34 } from "react/jsx-runtime";
var renderQuestionItem = ({ primary, children }) => /* @__PURE__ */ jsxs34(
  "div",
  {
    "data-testid": "mobius-section__personalised-cover-group",
    className: classNames15("mobius-section__personalised-cover-group", {
      "mobius-section__personalised-cover-group--has-children": children.length > 0
    }),
    children: [
      /* @__PURE__ */ jsx55(Question, { ...primary }),
      children.map((child) => /* @__PURE__ */ jsx55(Question, { ...child }, child.name))
    ]
  },
  primary.name
);
var PersonalisedCoverSection = forwardRef11((props, ref) => {
  const { title, preamble, postamble, questions, className } = props;
  const classes = classNames15("mobius", "mobius-section", className);
  const { groups = {} } = useQuestionnaire();
  return /* @__PURE__ */ jsxs34("section", { "data-testid": "mobius-section", ref, className: classes, children: [
    title && /* @__PURE__ */ jsx55("h2", { className: "mobius-section__title", children: title }),
    preamble && /* @__PURE__ */ jsx55(
      TextOrHTML11,
      {
        "data-testid": "mobius-section__preamble",
        className: "mobius-section__preamble",
        text: preamble,
        textWrapper: true
      }
    ),
    /* @__PURE__ */ jsx55("div", { className: "mobius-section__question-container", children: prepareQuestionsForRender(questions.filter(isQuestion), groups).map(
      renderQuestionItem
    ) }),
    postamble && /* @__PURE__ */ jsx55(
      TextOrHTML11,
      {
        "data-testid": "mobius-section__postamble",
        className: "mobius-section__postamble",
        text: postamble,
        textWrapper: true
      }
    )
  ] });
});
PersonalisedCoverSection.displayName = "PersonalisedCoverSection";

// src/components/Questionnaire/Section/index.tsx
import { jsx as jsx56 } from "react/jsx-runtime";
var Section2 = forwardRef12((props, ref) => {
  const { displayAs, ...otherProps } = props;
  if (displayAs === "review") {
    return /* @__PURE__ */ jsx56(ReviewSection, { ...otherProps, ref });
  }
  if (displayAs === "edit") {
    return /* @__PURE__ */ jsx56(EditSection, { ...otherProps, ref });
  }
  if (displayAs === "cover_genius") {
    return /* @__PURE__ */ jsx56(CoverGeniusSection, { ...otherProps, ref });
  }
  if (displayAs === "personalised_cover") {
    return /* @__PURE__ */ jsx56(PersonalisedCoverSection, { ...otherProps, ref });
  }
  return /* @__PURE__ */ jsx56(Section, { ...otherProps, ref });
});
Section2.displayName = "Section";

// src/components/Questionnaire/QuestionnaireForm/QuestionnaireForm.tsx
import { useBreakpoint } from "@simplybusiness/mobius";
import classNames16 from "classnames/dedupe";
import { useMemo as useMemo12 } from "react";

// src/hooks/useShouldBlockSubmission/useShouldBlockSubmission.tsx
import { useIsFetching } from "@tanstack/react-query";
function useShouldBlockSubmission() {
  const isSendingAnswers = useMutationStatus("sendAnswers");
  const isValidatingZipcode = useIsFetching({ queryKey: ["zipcode-validation"] }) > 0;
  return isSendingAnswers || isValidatingZipcode;
}

// src/components/Questionnaire/QuestionnaireForm/QuestionnaireForm.tsx
import { jsx as jsx57, jsxs as jsxs35 } from "react/jsx-runtime";
var QuestionnaireForm = (props) => {
  const { submitActionLabel, sections, children } = props;
  const { formRef, handleSubmit, inputValues, tokens, url } = useQuestionnaireSubmit();
  const { up } = useBreakpoint();
  const isDesktop = up("md");
  const { errors } = useQuestionnaire();
  const { currentSection, currentSectionIndex, isFirstSection, isLastSection } = useCurrentSection(sections);
  const shouldBlockSubmission = useShouldBlockSubmission();
  const { goBack, handleSeeMoreCovers, showSeeMoreCoversTrigger } = useQuestionnaireNavigation();
  const { handleSectionUpdate } = useQuestionnaireValidation();
  const isEditSection = currentSection?.displayAs === "edit";
  const isReviewSection = currentSection?.displayAs === "review";
  const isCoverGeniusSection = currentSection?.displayAs === "cover_genius";
  const showSubmitAction = isLastSection && !isReviewSection && !isEditSection;
  const navEventCategory = useMemo12(
    () => currentSection?.id ?? `step-${currentSectionIndex + 1}`,
    [currentSection?.id, currentSectionIndex]
  );
  return /* @__PURE__ */ jsxs35(
    "form",
    {
      ref: formRef,
      className: "mobius-submission__questionnaire-form",
      action: url,
      method: "POST",
      acceptCharset: "UTF-8",
      noValidate: true,
      onSubmit: handleSubmit,
      "aria-label": "Questionnaire form",
      children: [
        inputValues.map(([key, value], i) => /* @__PURE__ */ jsx57("input", { type: "hidden", name: key, value }, `${key}-${i}`)),
        /* @__PURE__ */ jsx57(
          "input",
          {
            type: "hidden",
            name: "authenticity_token",
            value: tokens.authenticityToken
          }
        ),
        /* @__PURE__ */ jsx57("input", { type: "hidden", name: "journey_token", value: tokens.journeyToken }),
        isDesktop && !isEditSection && /* @__PURE__ */ jsx57(
          "div",
          {
            "data-testid": "desktop-actions",
            className: classNames16("mobius-questionnaire__actions-container", {
              "--is-first-section": isFirstSection
            }),
            children: /* @__PURE__ */ jsx57(
              Actions3,
              {
                backLabel: !isFirstSection ? "Back" : void 0,
                backButton: { onClick: goBack },
                category: navEventCategory
              }
            )
          }
        ),
        children,
        !isEditSection && /* @__PURE__ */ jsx57(
          Actions3,
          {
            backLabel: !isDesktop && !isFirstSection ? "Back" : void 0,
            nextLabel: !isLastSection ? "Next" : void 0,
            backButton: { onClick: goBack },
            nextButton: {
              onClick: handleSectionUpdate,
              isLoading: shouldBlockSubmission,
              isDisabled: shouldBlockSubmission
            },
            variant: isCoverGeniusSection ? "cover_genius" : void 0,
            coverGenius: {
              label: "See more covers",
              onClick: handleSeeMoreCovers,
              desktop: isDesktop,
              showButton: showSeeMoreCoversTrigger
            },
            category: navEventCategory,
            submitAction: showSubmitAction ? /* @__PURE__ */ jsx57(
              SubmitAction,
              {
                buttonLabel: submitActionLabel,
                isDisabled: shouldBlockSubmission,
                errors,
                onSubmit: handleSectionUpdate
              }
            ) : void 0
          }
        )
      ]
    }
  );
};

// src/hooks/useFocusFirstElement/useFocusFirstElement.ts
import { useCallback as useCallback13 } from "react";
var useFocusFirstElement = (containerRef) => {
  const focusFirstElement = useCallback13(() => {
    const firstFocusable = containerRef?.current?.querySelector(
      FOCUSABLE_ELEMENT_SELECTOR
    );
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }, [containerRef]);
  return { focusFirstElement };
};

// src/hooks/useSectionNavigation/useSectionNavigation.ts
import { useEffect as useEffect18, useRef as useRef13, useCallback as useCallback14 } from "react";
var useSectionNavigation = (currentSectionIndex, setCurrentSectionIndex, fallbackSection = 0, sections = []) => {
  const SECTION_KEY = "section";
  const initialized = useRef13(false);
  const getSectionFromUrl = useCallback14(() => {
    const section = parseInt(
      new URLSearchParams(window.location.search).get(SECTION_KEY) ?? "",
      10
    );
    if (isNaN(section) || section < 0 || section >= sections.length) {
      return fallbackSection;
    }
    return section;
  }, [fallbackSection, sections.length]);
  const setUrlSection = (section, method) => {
    const params = new URLSearchParams(window.location.search);
    params.set(SECTION_KEY, section.toString());
    window.history[method](null, "", `${window.location.pathname}?${params}`);
  };
  useEffect18(() => {
    if (!sections.length) {
      return;
    }
    const sectionInUrl = getSectionFromUrl();
    if (!initialized.current) {
      if (sectionInUrl !== currentSectionIndex) {
        setCurrentSectionIndex(sectionInUrl);
      }
      setUrlSection(sectionInUrl, "replaceState");
      initialized.current = true;
    } else if (sectionInUrl !== currentSectionIndex) {
      setUrlSection(currentSectionIndex, "pushState");
    }
  }, [
    fallbackSection,
    sections.length,
    setCurrentSectionIndex,
    getSectionFromUrl,
    currentSectionIndex
  ]);
  useEffect18(() => {
    const onPopState = () => {
      const section = getSectionFromUrl();
      if (section !== currentSectionIndex) {
        setCurrentSectionIndex(section);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [currentSectionIndex, setCurrentSectionIndex, getSectionFromUrl]);
};

// src/components/Questionnaire/Questionnaire.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var Questionnaire = forwardRef13(
  (props, ref) => {
    const {
      elementType: Element = "div",
      config,
      sections,
      validations,
      componentName,
      actions,
      currentSection: initialCurrentSection,
      pageId,
      ...otherProps
    } = props;
    const { locale } = useLocale();
    const { setCurrentSectionIndex } = useQuestionnaireActions();
    const { focusFirstError } = useQuestionRefs();
    const configWithLocale = useMemo13(
      () => ({
        ...config,
        locale
      }),
      [config, locale]
    );
    useSetSeeMoreCoversState(false);
    useQuestionnaireInitialization(sections, validations, configWithLocale);
    const {
      currentSection: currentSectionFromState,
      currentSectionIndex,
      errors: currentSectionErrors
    } = useCurrentSection(sections);
    const currentSection = currentSectionFromState;
    const prevSectionIndexRef = useRef14(currentSectionIndex);
    const sectionRef = useRef14(null);
    const { focusFirstElement } = useFocusFirstElement(sectionRef);
    const isSlideBack = currentSectionIndex < prevSectionIndexRef.current;
    const submitActionLabel = useMemo13(() => {
      const submitAction = actions?.find(
        (action) => action.actionType === "submit"
      );
      return submitAction?.label || "Continue to quotes";
    }, [actions]);
    useEffect19(() => {
      prevSectionIndexRef.current = currentSectionIndex;
    }, [currentSectionIndex]);
    useCoverGeniusSection({
      allQuestions: currentSection.questions
    });
    useSectionNavigation(
      currentSectionIndex,
      setCurrentSectionIndex,
      initialCurrentSection,
      sections
    );
    const classes = classNames17(
      "mobius",
      "mobius-questionnaire",
      otherProps.className,
      {
        "mobius-questionnaire--slide-next": !isSlideBack,
        "mobius-questionnaire--slide-back": isSlideBack
      }
    );
    const { variant } = otherProps;
    return /* @__PURE__ */ jsx58(
      Element,
      {
        ref,
        ...otherProps,
        ...componentName ? { "data-component": componentName } : {},
        ...variant ? { "data-variant": variant } : {},
        className: classes,
        children: /* @__PURE__ */ jsx58(
          QuestionnaireForm,
          {
            submitActionLabel,
            sections,
            children: /* @__PURE__ */ jsx58(SwitchTransition, { children: /* @__PURE__ */ jsx58(
              CSSTransition,
              {
                nodeRef: sectionRef,
                timeout: 200,
                onEntered: () => {
                  const hasErrors = currentSectionErrors && Object.keys(currentSectionErrors).length > 0;
                  if (hasErrors) {
                    focusFirstError(currentSectionErrors);
                  } else {
                    window?.scrollTo({
                      top: 0,
                      behavior: "smooth"
                    });
                    focusFirstElement();
                  }
                },
                classNames: "mobius-section__transition",
                children: /* @__PURE__ */ jsx58(
                  Section2,
                  {
                    ref: sectionRef,
                    ...currentSection,
                    submitActionLabel
                  }
                )
              },
              currentSectionIndex
            ) })
          }
        )
      }
    );
  }
);
Questionnaire.displayName = "Questionnaire";

export {
  useSetQuestionnaireFormUrl,
  SecurityLocks,
  DateQuestion,
  SubmitAction,
  Actions3 as Actions,
  Questionnaire
};
